import {
  ApartmentOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  BgColorsOutlined,
  CheckOutlined,
  CloseOutlined,
  CodeOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  FileTextOutlined,
  FormOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
  SaveOutlined,
  SearchOutlined,
  SnippetsOutlined,
  StarOutlined,
  TagsOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Collapse,
  Divider,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Popover,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { TableProps, TabsProps } from "antd";
import {
  createContext,
  Fragment,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  WheelEvent as ReactWheelEvent,
} from "react";
import styled from "styled-components";
import { IdRender } from "../components/common";
import { ContextType, DataProvider } from "../contexts/DataProvider";
import { useFeedback } from "../contexts/FeedbackProvider";
import { AnimationConfig } from "../shared/types/Animation";
import type { DictItem } from "../shared/types/DictItem";
import { EffectConfig } from "../shared/types/Effect";
import {
  getPropGraphDescription,
  getPropGraphNegativeDescriptionFragments,
  getPropGraphStrength,
  getPropGraphStrengthDebug,
  PropGraphStrengthDebugResult,
  PropGraphStrengthResult,
} from "../shared/types/PropGraphUtils";
import {
  KeyValueDataProvider,
  useKeyValueData,
} from "../contexts/KeyValueDataProvider";
import {
  PropStrengthDataProvider,
  usePropStrengthData,
} from "../contexts/PropStrengthDataProvider";
import {
  createDefaultPropStrengthRows,
  PropStrength,
} from "../shared/types/PropStrength";
import { PropSimulationHistory } from "../shared/types/PropSimulation";
import {
  AttributeValueNodeData,
  ActionEffectNodeData,
  ActionPermanentBoostNodeData,
  BoardMaterialPosition,
  BoardMaterialScope,
  CompareNodeData,
  createDefaultNodeEditorState,
  createDefaultPropGraph,
  createDefaultPropNode,
  createPropNodeMap,
  createPropGraphId,
  EventTurnStartNodeData,
  getPropNodePorts,
  IntConstantNodeData,
  RarityConstantNodeData,
  TagConstantNodeData,
  PrototypeSelectNodeData,
  TargetBoardNodeData,
  PropEdge,
  PropGraph,
  PropGraphBindingKind,
  PropGraphEditorState,
  PropGraphNodeEditorState,
  PropGraphPort,
  PropGraphPosition,
  PropGraphPortKind,
  PropGraphValueType,
  ProbabilityNodeData,
  PropNode,
  PropNodeType,
  FloatConstantNodeData,
  FloatOperationNodeData,
  IntegerOperationNodeData,
  PrototypeLibraryNodeData,
  RoundNumberNodeData,
} from "../shared/types/PropGraph";
import PageKeyValue from "./PageKeyValue";
import { AnimationManagementPanel, EffectManagementPanel } from "./PageEffects";
import PagePropRarityWight from "./PagePropRarityWight";
import PagePropStrength from "./PagePropStrength";
import { dataCall } from "../tsrpc";
import { PropGraphDocsTab } from "./propGraphDocs/renderDocs";
import { getPropGraphPortSpecificDescription } from "./propGraphDocs/portDocs";

const { Title, Text } = Typography;

const PropGraphDataContext = createContext<ContextType<PropGraph> | null>(null);
const EffectDataContext = createContext<ContextType<EffectConfig> | null>(null);
const AnimationDataContext = createContext<ContextType<AnimationConfig> | null>(null);

const PropGraphPageFrame = styled(Flex)`
  height: 100%;
  width: 100%;
  min-width: 0;
  padding: 6px 8px;

  @media (max-width: 768px) {
    padding: 4px;
  }
`;

const FullHeightTabs = styled.div`
  height: 100%;
  width: 100%;
  min-width: 0;

  .propgraph-tabs {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .propgraph-tabs > .ant-tabs-nav {
    flex: 0 0 auto;
    margin: 0 0 6px 0 !important;
  }

  .propgraph-tabs > .ant-tabs-content-holder {
    flex: 1 1 auto;
    min-height: 0;
  }

  .propgraph-tabs > .ant-tabs-content-holder > .ant-tabs-content {
    height: 100%;
  }

  .propgraph-tabs > .ant-tabs-content-holder > .ant-tabs-content > .ant-tabs-tabpane {
    height: 100%;
    min-height: 0;
  }

  @media (max-width: 900px) {
    .propgraph-tabs > .ant-tabs-nav {
      margin-bottom: 4px !important;
    }

    .propgraph-tabs .ant-tabs-tab {
      padding: 6px 10px !important;
    }

    .propgraph-list-root {
      gap: 8px !important;
    }

    .propgraph-list-header {
      flex-direction: column;
      align-items: stretch !important;
      gap: 8px;
    }

    .propgraph-list-meta {
      flex-wrap: wrap;
      gap: 4px 10px !important;
    }

    .propgraph-list-actions {
      width: 100%;
      justify-content: flex-start !important;
      flex-wrap: nowrap !important;
      overflow-x: auto;
      padding-bottom: 2px;
      overscroll-behavior-inline: contain;
    }

    .propgraph-list-actions > .ant-input-affix-wrapper {
      flex: 1 0 230px;
      width: auto !important;
    }

    .propgraph-editor-header {
      flex-wrap: nowrap;
      overflow-x: auto;
      overflow-y: hidden;
      padding-bottom: 2px;
    }

    .propgraph-editor-fields {
      flex: 0 0 auto !important;
      overflow: visible !important;
    }

    .propgraph-description-bar {
      overflow-x: auto;
      overscroll-behavior-inline: contain;
    }

    .propgraph-editor-workspace {
      flex-direction: column;
      gap: 6px !important;
    }

    .propgraph-canvas-shell {
      flex: 1 1 58% !important;
      min-height: 240px !important;
    }

    .propgraph-inspector {
      width: 100% !important;
      flex: 0 0 36%;
      max-height: 36%;
      padding: 8px !important;
    }

    .propgraph-canvas-toolbar {
      height: 38px;
      min-height: 38px !important;
      flex-wrap: nowrap !important;
      overflow-x: auto;
      overflow-y: hidden;
      padding: 3px 6px !important;
      overscroll-behavior-inline: contain;
    }

    .propgraph-canvas-toolbar .ant-divider-vertical {
      flex: 0 0 auto;
    }

    .propgraph-canvas-toolbar .ant-btn {
      flex: 0 0 auto;
    }
  }
`;

const InspectorFieldControl = styled.div`
  width: 100%;
  min-width: 0;

  .ant-select,
  .ant-input-number {
    width: 100%;
  }
`;

const PropGraphListTableWrap = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;

  .ant-table-wrapper {
    height: 100%;
  }

  .ant-spin-nested-loading,
  .ant-spin-container,
  .ant-table,
  .ant-table-container {
    height: 100%;
    min-height: 0;
  }

  .ant-table {
    display: flex;
    flex-direction: column;
  }

  .ant-table-container {
    display: flex;
    flex-direction: column;
  }

  .ant-table-header {
    flex: 0 0 auto;
    overflow: hidden !important;
  }

  .ant-table-body {
    flex: 1 1 auto;
    min-height: 0;
    scrollbar-gutter: stable;
    overflow-x: hidden !important;
    overflow-y: auto !important;
  }

  .ant-table-cell {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ant-table-cell.propgraph-action-column {
    overflow: visible;
    text-overflow: clip;
  }

  .ant-table-cell.propgraph-description-column {
    overflow: visible;
    text-overflow: clip;
    white-space: normal;
  }

  .propgraph-action-buttons {
    display: flex;
    flex-wrap: nowrap;
    gap: 2px;
  }

  .propgraph-action-buttons .ant-btn {
    padding-inline: 4px;
  }

  @media (max-width: 900px) {
    .ant-table-header {
      overflow-x: hidden !important;
    }

    .ant-table-body {
      overflow-x: auto !important;
      -webkit-overflow-scrolling: touch;
    }
  }
`;

const GraphDescriptionBar = styled.div`
  flex: 0 0 auto;
  min-height: 26px;
  padding: 3px 8px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #f8fafc;
  color: #334155;
  font-size: 12px;
  line-height: 1.5;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const GraphDescriptionText = styled.span`
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DescriptionPropLink = styled.span<{ $negative?: boolean }>`
  color: ${({ $negative }) => ($negative ? "#dc2626" : "#1677ff")};
  cursor: help;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 2px;
`;

const NegativeDescriptionText = styled.span`
  color: #dc2626;
  font-weight: 500;
`;

const PropPreviewPanel = styled.div`
  width: 280px;
  max-width: calc(100vw - 48px);
`;

const PropPreviewTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
`;

const PropPreviewMeta = styled.div`
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr);
  gap: 4px 8px;
  font-size: 12px;
  line-height: 1.45;
`;

const PropPreviewDescription = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #f1f5f9;
  color: #334155;
  font-size: 12px;
  line-height: 1.5;
  white-space: normal;
`;

const GraphStrengthInline = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #334155;
  font-size: 12px;
  white-space: nowrap;
`;

const StrengthCellWrap = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 12px;
  line-height: 1.35;
`;

const StrengthCellRow = styled.div`
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: 6px;
  white-space: nowrap;
`;

const StrengthMainText = styled.span`
  color: #0f172a;
  font-weight: 600;
  white-space: nowrap;
`;

const StrengthSimulationText = styled.span`
  color: #64748b;
  white-space: nowrap;
`;

const StrengthOffsetText = styled.span<{ $tone: "positive" | "negative" | "neutral" }>`
  color: ${(props) =>
    props.$tone === "positive"
      ? "#cf1322"
      : props.$tone === "negative"
        ? "#389e0d"
        : "#64748b"};
  white-space: nowrap;
`;

const StrengthDetailPanel = styled.div`
  width: min(560px, calc(100vw - 48px));
  max-height: min(680px, calc(100vh - 120px));
  overflow-y: auto;
  color: #0f172a;
  font-size: 12px;
`;

const StrengthDetailSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const StrengthDetailGrid = styled.div`
  display: grid;
  grid-template-columns: 116px minmax(0, 1fr);
  gap: 4px 10px;
  line-height: 1.45;
`;

const StrengthDetailLabel = styled.span`
  color: #64748b;
`;

const StrengthDetailValue = styled.span`
  min-width: 0;
  overflow-wrap: anywhere;
  color: #0f172a;
`;

const StrengthDetailCard = styled.div`
  padding: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #ffffff;
`;

const StrengthDetailCardTitle = styled.div`
  margin-bottom: 6px;
  color: #0f172a;
  font-weight: 600;
`;

const NodeCreateMenuSurface = styled.div`
  width: min(420px, calc(100vw - 16px));
  max-height: min(360px, calc(100vh - 16px));
  overflow-y: auto;
  padding: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.18);
`;

const NodeCreateMenuRow = styled.button<{ $accent: string }>`
  width: 100%;
  min-height: 58px;
  display: grid;
  grid-template-columns: 14px minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
  padding: 7px 8px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #0f172a;
  cursor: pointer;
  text-align: left;
  transition: background 0.14s ease, color 0.14s ease;

  &:hover {
    background: color-mix(in srgb, ${(props) => props.$accent} 10%, #ffffff);
  }
`;

const NodeCreateMenuSwatch = styled.span<{ $accent: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(props) => props.$accent};
  box-shadow: 0 0 0 3px color-mix(in srgb, ${(props) => props.$accent} 16%, #ffffff);
`;

const NodeCreateMenuText = styled.span`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const NodeCreateMenuLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NodeCreateMenuDescription = styled.span`
  color: #64748b;
  font-size: 12px;
  line-height: 18px;
  overflow: hidden;
  white-space: normal;
  display: block;
  max-height: 36px;
  word-break: break-word;
`;

const PropGraphDescriptionText = styled.span<{ $compact?: boolean }>`
  display: block;
  min-width: 0;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: ${(props) => (props.$compact ? "18px" : "1.65")};

  .propgraph-description-line {
    display: block;
  }

  .propgraph-description-line + .propgraph-description-line {
    margin-top: ${(props) => (props.$compact ? "0" : "2px")};
  }
`;

const NodeCreateMenuArrow = styled.span`
  color: #94a3b8;
  font-size: 14px;
`;

const NodeCreateMenuTrailing = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
`;

const KeyboardPaletteSurface = styled.div`
  width: min(460px, calc(100vw - 16px));
  max-height: min(430px, calc(100vh - 16px));
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  border: 1px solid #dbeafe;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.22);
`;

const KeyboardPaletteHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const KeyboardPaletteTitle = styled.span`
  color: #0f172a;
  font-size: 13px;
  font-weight: 700;
`;

const KeyboardPaletteHint = styled.span`
  color: #64748b;
  font-size: 12px;
`;

const KeyboardPaletteList = styled.div`
  min-height: 0;
  max-height: 330px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const KeyboardPaletteRow = styled.button<{ $active: boolean; $accent: string }>`
  width: 100%;
  min-height: 58px;
  display: grid;
  grid-template-columns: 28px 10px minmax(0, 1fr);
  gap: 8px;
  align-items: center;
  padding: 7px 8px;
  border: 0;
  border-radius: 6px;
  background: ${(props) =>
    props.$active
      ? "color-mix(in srgb, #1677ff 12%, #ffffff)"
      : "transparent"};
  color: #0f172a;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: color-mix(in srgb, ${(props) => props.$accent} 10%, #ffffff);
  }
`;

const KeyboardShortcutBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 22px;
  padding: 0 6px;
  border: 1px solid #cbd5e1;
  border-radius: 5px;
  background: #f8fafc;
  color: #334155;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
`;

const NODE_CREATE_MENU_WIDTH = 420;
const NODE_CREATE_MENU_MAX_HEIGHT = 360;
const NODE_CREATE_MENU_VIEWPORT_GAP = 8;
const KEYBOARD_PALETTE_WIDTH = 460;
const KEYBOARD_PALETTE_MAX_HEIGHT = 430;
const NODE_WIDTH = 260;
const NODE_HEIGHT = 132;
const NODE_HEADER_HEIGHT = 30;
const NODE_BODY_PADDING = 8;
const PORT_ROW_HEIGHT = 28;
const PORT_HANDLE_SIZE = 14;
const PORT_HIT_SIZE = 28;
const GRID_SIZE = 24;
const MIN_CANVAS_SCALE = 0.35;
const MAX_CANVAS_SCALE = 2.5;
const AUTO_LAYOUT_COLUMN_GAP = 80;
const AUTO_LAYOUT_ROW_GAP = 40;
const AUTO_LAYOUT_GROUP_ROW_GAP = 60;
const PROP_GRAPH_LIST_LIMIT = 200;
const PROP_GRAPH_FULL_LOAD_CONCURRENCY = 6;
const PROP_GRAPH_LIST_AUTO_LOAD_DELAY_MS = 80;
const PROP_GRAPH_ID_LIST_PROJECTION: Record<string, 0 | 1> = {
  _id: 1,
};
const PROP_GRAPH_NAME_INDEX_PROJECTION: Record<string, 0 | 1> = {
  _id: 1,
  name: 1,
  assetId: 1,
  type: 1,
  rarity: 1,
  tags: 1,
  derived: 1,
  yield: 1,
};
const PROP_GRAPH_LIST_SORT: Record<string, 1 | -1> = {
  type: 1,
  tags: 1,
  rarity: 1,
  derived: 1,
  _id: 1,
} as const;

type OptionValue = string | number;

type DescribedOption<T extends OptionValue = string> = {
  value: T;
  label: string;
  description?: string;
};

const describedSelectProps = {
  optionRender: renderDescribedOption,
  listItemHeight: 72,
  listHeight: 360,
  popupMatchSelectWidth: false,
  popupClassName: "propgraph-described-select-popup",
  dropdownStyle: {
    width: "min(520px, calc(100vw - 32px))",
    maxWidth: "calc(100vw - 32px)",
  },
};

const nodeTypeOptions: DescribedOption<PropNodeType>[] = [
  { value: "eventTurnStart", label: "回合开始", description: "回合开始时输出流程，可设置触发间隔和循环次数。" },
  { value: "eventAcquire", label: "持有触发", description: "持有本道具期间每轮输出流程；道具被移除后停止生效。" },
  { value: "eventUse", label: "主动使用", description: "玩家主动使用本道具时输出流程。" },
  { value: "eventEliminated", label: "消除触发", description: "输入目标被消除时输出流程。" },
  { value: "targetSelf", label: "自身", description: "输出当前道具，统一按目标数组传递。" },
  { value: "targetBoard", label: "盘面原料", description: "按全部、相对位置或绝对位置，从盘面输出原料目标。" },
  { value: "prototypeLibrary", label: "原型库", description: "按原料、辅料或配方类型输出可用道具原型。" },
  { value: "prototypeSelect", label: "原型", description: "从道具列表中选择一个固定道具原型。" },
  { value: "targetFilter", label: "条件筛选", description: "按布尔条件过滤输入目标数组，并输出符合条件的目标。" },
  { value: "filterRandom", label: "随机筛选", description: "从输入目标数组中随机保留指定数量的目标。" },
  { value: "actionGrow", label: "成长", description: "让输入目标数组永久增加魔力产出，数量来自整数输入。" },
  { value: "actionExtraYield", label: "额外产出", description: "让输入目标数组本次额外产出魔力；数量和倍率均可选，倍率来自浮点数输入。" },
  { value: "actionPermanentBoost", label: "永久加成", description: "让输入目标数组此后的产出增加固定数值或倍率；同类效果不叠加，新效果会覆盖旧效果。" },
  { value: "actionPurify", label: "净化", description: "消除输入目标数组上的负面效果，输入端口与成长一致。" },
  { value: "actionProtect", label: "保护", description: "保护输入目标数组，使其不会被消除。" },
  { value: "actionAccelerate", label: "加速", description: "减少输入目标数组的轮次间隔，数量来自整数输入。" },
  { value: "actionTransform", label: "转化", description: "把输入目标转化为输入原型指定的道具。" },
  { value: "actionSpawn", label: "生成", description: "在输入位置生成指定目标，数量来自整数输入。" },
  { value: "actionEliminate", label: "消除", description: "从输入目标数组中消除目标；未连接数量时消除全部输入目标。" },
  { value: "actionCollect", label: "收集", description: "从输入目标处收集魔力并暂存在当前辅料中，不减少目标自身产出。" },
  { value: "actionHarvest", label: "收获", description: "主动使用时释放当前辅料暂存的魔力，加入当回合产出。" },
  { value: "actionRarityWeight", label: "品质权重", description: "调整后续抽取指定品质时的权重倍率。" },
  { value: "actionExtendCommission", label: "延长调和", description: "调和次数+1，每份委托限用1次。" },
  { value: "actionSwapPosition", label: "调换位置", description: "把目标移动到输入位置，并与该位置上的道具交换。" },
  { value: "actionTriggerVisual", label: "触发表现", description: "仅触发动作节点配置的动画和特效，不改变运行时数据，也不生成描述文本。" },
  { value: "intConstant", label: "整数常量", description: "输出一个固定整数。" },
  { value: "intAttribute", label: "整数属性", description: "从单个目标读取品质、标签或当前产出。" },
  { value: "intQuantity", label: "数量", description: "统计输入目标数组中的目标数量，输出整数。" },
  { value: "intKindCount", label: "种类", description: "统计输入目标数组中不同道具种类的数量，输出整数。" },
  { value: "intRandom", label: "整数随机", description: "在最小值和最大值之间随机输出一个整数。" },
  { value: "intOperation", label: "整数运算", description: "对两个整数执行加、减、乘、除、取余、最小或最大运算。" },
  { value: "floatConstant", label: "浮点常量", description: "输出一个固定浮点数。" },
  { value: "floatAttribute", label: "浮点属性", description: "从单个目标读取品质、标签或当前产出并作为浮点数输出。" },
  { value: "floatRandom", label: "浮点随机", description: "在最小值和最大值之间随机输出一个浮点数。" },
  { value: "floatOperation", label: "浮点运算", description: "对两个浮点数执行加、减、乘、除、取余、最小或最大运算。" },
  { value: "roundNumber", label: "取整", description: "将浮点数按四舍五入、向上取整或向下取整转换为整数。" },
  { value: "rarityConstant", label: "品质常量", description: "从品质配置中选择一个固定品质值。" },
  { value: "tagConstant", label: "标签常量", description: "从标签配置中选择一个固定标签值。" },
  { value: "filterRarity", label: "品质筛选", description: "按一个或多个品质输入筛选目标数组。" },
  { value: "filterTag", label: "标签筛选", description: "按一个或多个标签输入筛选目标数组。" },
  { value: "filterPrototype", label: "原型筛选", description: "按一个或多个指定道具原型筛选目标数组。" },
  { value: "logicCompareInt", label: "整数比较", description: "比较两个整数，输出布尔条件。" },
  { value: "logicCompareFloat", label: "浮点比较", description: "比较两个浮点数，输出布尔条件。" },
  { value: "logicContains", label: "包含", description: "判断目标数组中是否包含指定目标，输出布尔条件。" },
  { value: "logicProbability", label: "概率", description: "按配置的概率随机输出条件是否成立。" },
];

function findNodeTypeOption(type: PropNodeType): DescribedOption<PropNodeType> {
  return nodeTypeOptions.find((option) => option.value === type) ?? { value: type, label: type };
}

const actionNodeTypes: PropNodeType[] = [
  "actionGrow",
  "actionExtraYield",
  "actionPermanentBoost",
  "actionPurify",
  "actionProtect",
  "actionAccelerate",
  "actionTransform",
  "actionSpawn",
  "actionEliminate",
  "actionCollect",
  "actionHarvest",
  "actionRarityWeight",
  "actionExtendCommission",
  "actionSwapPosition",
  "actionTriggerVisual",
];

function isNodeCreateGroup(item: DescribedOption<PropNodeType> | NodeCreateGroup): item is NodeCreateGroup {
  return "children" in item;
}

type NodeCreateGroup = {
  key: string;
  label: string;
  children: Array<DescribedOption<PropNodeType> | NodeCreateGroup>;
};

type KeyboardPaletteMode = "search" | "category";

type KeyboardPaletteState = {
  mode: KeyboardPaletteMode;
  categoryKey?: KeyboardCategoryKey;
  query: string;
  activeIndex: number;
  screen: { x: number; y: number };
  world: PropGraphPosition;
};

type KeyboardCategoryKey = "event" | "target" | "filter" | "action" | "value" | "logic" | "calculate";

type KeyboardNodeOption = DescribedOption<PropNodeType> & {
  groupKey: KeyboardCategoryKey;
  groupLabel: string;
  shortcut?: string;
  aliases: string[];
};

const nodeCreateGroups: NodeCreateGroup[] = [
  {
    key: "event",
    label: "事件",
    children: [
      findNodeTypeOption("eventTurnStart"),
      findNodeTypeOption("eventAcquire"),
      findNodeTypeOption("eventUse"),
      findNodeTypeOption("eventEliminated"),
    ],
  },
  {
    key: "target",
    label: "目标",
    children: [
      findNodeTypeOption("targetSelf"),
      findNodeTypeOption("targetBoard"),
      findNodeTypeOption("prototypeLibrary"),
      findNodeTypeOption("prototypeSelect"),
    ],
  },
  {
    key: "filter",
    label: "筛选",
    children: [
      findNodeTypeOption("targetFilter"),
      findNodeTypeOption("filterRarity"),
      findNodeTypeOption("filterTag"),
      findNodeTypeOption("filterPrototype"),
      findNodeTypeOption("filterRandom"),
    ],
  },
  {
    key: "value",
    label: "数值",
    children: [
      {
        key: "integer",
        label: "整数",
        children: [
          findNodeTypeOption("intConstant"),
          findNodeTypeOption("intAttribute"),
          findNodeTypeOption("intQuantity"),
          findNodeTypeOption("intKindCount"),
          findNodeTypeOption("intRandom"),
        ],
      },
      {
        key: "float",
        label: "浮点数",
        children: [
          findNodeTypeOption("floatConstant"),
          findNodeTypeOption("floatAttribute"),
          findNodeTypeOption("floatRandom"),
        ],
      },
      {
        key: "rarity",
        label: "品质",
        children: [
          findNodeTypeOption("rarityConstant"),
        ],
      },
      {
        key: "tag",
        label: "标签",
        children: [
          findNodeTypeOption("tagConstant"),
        ],
      },
    ],
  },
  {
    key: "calculate",
    label: "计算",
    children: [
      findNodeTypeOption("intOperation"),
      findNodeTypeOption("floatOperation"),
      findNodeTypeOption("roundNumber"),
    ],
  },
  {
    key: "logic",
    label: "条件",
    children: [
      findNodeTypeOption("logicCompareInt"),
      findNodeTypeOption("logicCompareFloat"),
      findNodeTypeOption("logicContains"),
      findNodeTypeOption("logicProbability"),
    ],
  },
  {
    key: "action",
    label: "动作",
    children: actionNodeTypes.map(findNodeTypeOption),
  },
];

const keyboardCategoryShortcuts: Record<string, KeyboardCategoryKey> = {
  e: "event",
  t: "target",
  f: "filter",
  a: "action",
  n: "value",
  c: "logic",
  m: "calculate",
};

function getKeyboardCategoryShortcut(key: string) {
  const entry = Object.entries(keyboardCategoryShortcuts).find(([, value]) => value === key);
  return entry ? entry[0].toUpperCase() : "";
}

const keyboardCategoryLabels: Record<KeyboardCategoryKey, string> = {
  event: "事件",
  target: "目标",
  filter: "筛选",
  action: "动作",
  value: "数值",
  logic: "条件",
  calculate: "计算",
};

const nodeSecondKeyShortcuts: Partial<Record<PropNodeType, string>> = {
  eventTurnStart: "T",
  eventAcquire: "H",
  eventUse: "U",
  eventEliminated: "D",
  targetSelf: "S",
  targetBoard: "B",
  prototypeLibrary: "L",
  prototypeSelect: "P",
  targetFilter: "C",
  filterTag: "T",
  filterRarity: "P",
  filterPrototype: "O",
  filterRandom: "R",
  actionGrow: "G",
  actionExtraYield: "E",
  actionPermanentBoost: "B",
  actionAccelerate: "A",
  actionTransform: "T",
  actionSpawn: "S",
  actionEliminate: "D",
  actionProtect: "P",
  actionPurify: "U",
  actionCollect: "C",
  actionHarvest: "H",
  actionRarityWeight: "W",
  actionExtendCommission: "L",
  actionSwapPosition: "M",
  actionTriggerVisual: "V",
  intConstant: "I",
  floatConstant: "F",
  intAttribute: "A",
  floatAttribute: "O",
  intQuantity: "Q",
  intKindCount: "K",
  intRandom: "R",
  floatRandom: "D",
  intOperation: "I",
  floatOperation: "F",
  roundNumber: "R",
  rarityConstant: "P",
  tagConstant: "T",
  logicCompareInt: "C",
  logicCompareFloat: "F",
  logicContains: "I",
  logicProbability: "P",
};

const nodeSearchAliases: Partial<Record<PropNodeType, string[]>> = {
  eventTurnStart: ["turn", "start", "round", "hks", "hhks", "回合", "回合开始"],
  eventAcquire: ["hold", "held", "cy", "持有", "入手", "获得"],
  eventUse: ["use", "active", "zy", "sy", "主动", "使用"],
  eventEliminated: ["eliminated", "remove", "xq", "消除", "被消除"],
  targetSelf: ["self", "dq", "zs", "自身", "当前"],
  targetBoard: ["board", "pm", "yl", "盘面", "原料", "目标"],
  prototypeLibrary: ["library", "yxk", "原型库"],
  prototypeSelect: ["prototype", "yx", "原型", "指定"],
  targetFilter: ["condition", "filter", "tj", "sx", "条件", "筛选"],
  filterTag: ["tag", "bq", "标签", "筛选"],
  filterRarity: ["rarity", "quality", "pz", "普通", "少见", "稀有", "罕见", "品质"],
  filterPrototype: ["prototype", "yx", "原型筛选"],
  filterRandom: ["random", "sj", "随机", "随机筛选"],
  actionGrow: ["grow", "cz", "成长", "永久增加", "产出"],
  actionExtraYield: ["extra", "yield", "ec", "额外", "额外产出"],
  actionPermanentBoost: ["permanent", "boost", "yj", "jc", "永久", "永久加成", "加成", "互斥"],
  actionAccelerate: ["accelerate", "js", "加速", "倒计时"],
  actionTransform: ["transform", "zh", "转化"],
  actionSpawn: ["spawn", "add", "sc", "tj", "生成", "添加"],
  actionEliminate: ["eliminate", "delete", "xc", "消除"],
  actionProtect: ["protect", "bh", "保护"],
  actionPurify: ["purify", "jh", "净化"],
  actionCollect: ["collect", "sj", "收集"],
  actionHarvest: ["harvest", "sh", "收获"],
  actionRarityWeight: ["weight", "qz", "品质权重"],
  actionExtendCommission: ["extend", "yh", "延长", "调和"],
  actionSwapPosition: ["swap", "move", "wz", "调换", "位置"],
  actionTriggerVisual: ["visual", "effect", "animation", "bx", "tx", "触发表现", "动画", "特效"],
  intConstant: ["int", "integer", "zs", "整数", "常量"],
  floatConstant: ["float", "fd", "浮点", "小数", "常量"],
  intAttribute: ["attribute", "sx", "整数属性", "产出"],
  floatAttribute: ["attribute", "sx", "浮点属性", "产出"],
  intQuantity: ["quantity", "count", "sl", "数量"],
  intKindCount: ["kind", "zl", "种类"],
  intRandom: ["random", "sj", "整数随机"],
  floatRandom: ["random", "sj", "浮点随机"],
  intOperation: ["operation", "calc", "js", "ys", "整数运算", "加减乘除"],
  floatOperation: ["operation", "calc", "fd", "浮点运算", "加减乘除"],
  roundNumber: ["round", "floor", "ceil", "qz", "取整", "四舍五入"],
  rarityConstant: ["rarity", "quality", "pz", "普通", "少见", "稀有", "罕见", "品质"],
  tagConstant: ["tag", "bq", "标签", "植物", "液体", "蘑菇"],
  logicCompareInt: ["compare", "bj", "比较", "整数比较"],
  logicCompareFloat: ["compare", "bj", "比较", "浮点比较"],
  logicContains: ["contains", "include", "bh", "包含"],
  logicProbability: ["probability", "chance", "gl", "概率"],
};

const targetAttributeOptions: DescribedOption<AttributeValueNodeData["attribute"]>[] = [
  { value: "rarity", label: "品质", description: "读取目标的品质配置值，输出整数。" },
  { value: "tag", label: "标签", description: "读取目标的标签配置值，输出整数。" },
  { value: "yield", label: "产出", description: "读取目标当前魔力产出，包含永久增加或减少的产出加成。" },
];

const compareOpOptions: DescribedOption<CompareNodeData["op"]>[] = [
  { value: "eq", label: "等于", description: "左值与右值完全相等时条件成立。" },
  { value: "ne", label: "不等于", description: "左值与右值不相等时条件成立。" },
  { value: "gt", label: "大于", description: "左值严格大于右值时条件成立。" },
  { value: "gte", label: "大于等于", description: "左值大于或等于右值时条件成立。" },
  { value: "lt", label: "小于", description: "左值严格小于右值时条件成立。" },
  { value: "lte", label: "小于等于", description: "左值小于或等于右值时条件成立。" },
];

const integerOperationOptions: DescribedOption<IntegerOperationNodeData["op"]>[] = [
  { value: "add", label: "加", description: "输出左值加右值。" },
  { value: "subtract", label: "减", description: "输出左值减右值。" },
  { value: "multiply", label: "乘", description: "输出左值乘右值。" },
  { value: "divide", label: "除", description: "输出左值除以右值后的向下取整结果；右值为 0 时运行结果为 0。" },
  { value: "mod", label: "取余", description: "输出左值除以右值后的余数；右值为 0 时运行结果为 0。" },
  { value: "min", label: "最小", description: "输出左值和右值中的较小值。" },
  { value: "max", label: "最大", description: "输出左值和右值中的较大值。" },
];

const floatOperationOptions: DescribedOption<FloatOperationNodeData["op"]>[] = [
  { value: "add", label: "加", description: "输出左值加右值。" },
  { value: "subtract", label: "减", description: "输出左值减右值。" },
  { value: "multiply", label: "乘", description: "输出左值乘右值。" },
  { value: "divide", label: "除", description: "输出左值除以右值；右值为 0 时运行结果为 0。" },
  { value: "mod", label: "取余", description: "输出左值除以右值后的余数；右值为 0 时运行结果为 0。" },
  { value: "min", label: "最小", description: "输出左值和右值中的较小值。" },
  { value: "max", label: "最大", description: "输出左值和右值中的较大值。" },
];

const roundModeOptions: DescribedOption<RoundNumberNodeData["mode"]>[] = [
  { value: "round", label: "四舍五入", description: "按最接近的整数取整。" },
  { value: "ceil", label: "向上取整", description: "取大于或等于输入值的最小整数。" },
  { value: "floor", label: "向下取整", description: "取小于或等于输入值的最大整数。" },
];

const boardMaterialScopeOptions: DescribedOption<BoardMaterialScope>[] = [
  { value: "all", label: "全部", description: "输出当前盘面上的全部原料。" },
  { value: "relative", label: "相对位置", description: "按相邻、同行、同列或对角线等相对关系输出原料。" },
  { value: "absolute", label: "绝对位置", description: "按角落、边缘、行列等盘面固定区域输出原料。" },
];

const prototypeLibraryTypeOptions: DescribedOption<PrototypeLibraryNodeData["propType"]>[] = [
  { value: 1, label: "原料", description: "只输出可以进入材料库并上场反应的原料原型。" },
  { value: 2, label: "辅料", description: "只输出不上场、由玩家主动使用或收获的一次性辅料原型。" },
  { value: 3, label: "配方", description: "只输出获得后持续生效的配方光环原型。" },
];

const relativePositionOptions: DescribedOption<BoardMaterialPosition>[] = [
  { value: "adjacent", label: "相邻", description: "筛出与输入目标上下左右相邻的道具。" },
  { value: "sameRow", label: "同行", description: "筛出与输入目标位于同一行的道具。" },
  { value: "sameColumn", label: "同列", description: "筛出与输入目标位于同一列的道具。" },
  { value: "diagonal", label: "对角线", description: "筛出与输入目标处于斜向对角线关系的道具。" },
];

const absolutePositionOptions: DescribedOption<BoardMaterialPosition>[] = [
  { value: "corner", label: "角落", description: "筛出位于盘面四个角落的道具。" },
  { value: "edge", label: "边缘", description: "筛出位于盘面外圈边缘的道具，包含首行、尾行、首列、尾列。" },
  { value: "firstRow", label: "首行", description: "筛出位于盘面第一行的道具。" },
  { value: "lastRow", label: "尾行", description: "筛出位于盘面最后一行的道具。" },
  { value: "firstColumn", label: "首列", description: "筛出位于盘面第一列的道具。" },
  { value: "lastColumn", label: "尾列", description: "筛出位于盘面最后一列的道具。" },
  { value: "middleColumn", label: "中间列", description: "筛出位于盘面中间列的道具。" },
];

type EditTab = {
  key: string;
  graph: PropGraph;
  savedGraph: PropGraph;
};

type ManagementTabKey = "docs" | "type" | "rarity" | "tag" | "weight" | "strength" | "effect" | "animation";

type ManagementTab = {
  key: ManagementTabKey;
  label: string;
};

type Selection =
  | { type: "node"; id: string }
  | { type: "edge"; id: string }
  | { type: "multi"; nodeIds: string[]; edgeIds: string[] }
  | null;

type CanvasPoint = {
  clientX: number;
  clientY: number;
};

type WorldPoint = {
  x: number;
  y: number;
};

type SelectionIds = {
  nodeIds: Set<string>;
  edgeIds: Set<string>;
};

type SelectionMarquee = {
  pointerId: number;
  start: WorldPoint;
  current: WorldPoint;
};

type CanvasClipboard = {
  nodes: PropNode[];
  editorNodes: PropGraphNodeEditorState[];
  edges: PropEdge[];
  entryNodeIds: string[];
};

type PortDirection = "input" | "output";

type PortReference = {
  nodeId: string;
  portId: string;
  direction: PortDirection;
};

type ConnectionDraft = {
  from: PortReference;
  pointer: WorldPoint;
};

type PortConnectionState =
  | "idle"
  | "source"
  | "connectable"
  | "hoverConnectable"
  | "unavailable"
  | "hoverInvalid";

type PortConnectionVisualStyle = {
  borderColor: string;
  background: string;
  boxShadow: string;
  labelColor?: string;
  opacity: number;
  scale?: number;
};

type PropGraphValidationIssue = {
  kind: "graphField" | "field" | "port";
  message: string;
  nodeId?: string;
  field?: string;
  portId?: string;
};

type NodeDisplayRow = {
  label: string;
  value: ReactNode;
  muted?: boolean;
};

type PropGraphConfigOptions = {
  types: DictItem[];
  rarities: DictItem[];
  tags: DictItem[];
  props: PropGraph[];
  descriptionProps: PropGraph[];
  strengths: PropStrength[];
  effects: EffectConfig[];
  animations: AnimationConfig[];
};

type SimulationStrengthValues = {
  final?: number;
  balance?: number;
  actualCalculatedStrength?: number;
  balancedSimulationStrength?: number;
  strengthCoefficient?: number | null;
  propUpdatedAt?: number;
  simulationCreatedAt?: number;
  trigger?: number;
  pool?: number;
  confidenceLow?: number;
  confidenceHigh?: number;
  sampleCount?: number;
  intrinsic?: number;
  body?: number;
  source?: number;
};

type PropGraphListState = {
  keyword: string;
  filters: Record<string, any[] | null>;
  sorter: {
    columnKey?: string;
    order?: "ascend" | "descend" | null;
  } | null;
};

type PropGraphPageState = {
  activeKey: string;
  tabs: EditTab[];
  managementTabs: ManagementTab[];
  listState: PropGraphListState;
};

const defaultPropGraphListState: PropGraphListState = {
  keyword: "",
  filters: {},
  sorter: null,
};

const PROP_GRAPH_PAGE_CACHE_PREFIX = "page:propgraphs:new";
let cachedSimulationStrengthMap = new Map<string, SimulationStrengthValues>();
let cachedPropGraphDetailMap = new Map<string, PropGraph>();
let cachedPropGraphNameIndex: PropGraph[] = [];
let cachedPropGraphPageState: PropGraphPageState = {
  activeKey: "list",
  tabs: [],
  managementTabs: [],
  listState: defaultPropGraphListState,
};

export default function PagePropGraph() {
  return (
    <KeyValueDataProvider cachePrefix={PROP_GRAPH_PAGE_CACHE_PREFIX}>
      <PropStrengthDataProvider cacheKey={`${PROP_GRAPH_PAGE_CACHE_PREFIX}:propstrengths`}>
        <DataProvider<EffectConfig>
          context={EffectDataContext}
          collection="effects"
          limit={100000}
          cacheKey={`${PROP_GRAPH_PAGE_CACHE_PREFIX}:effects`}
        >
          <DataProvider<AnimationConfig>
            context={AnimationDataContext}
            collection="animations"
            limit={100000}
            cacheKey={`${PROP_GRAPH_PAGE_CACHE_PREFIX}:animations`}
          >
            <DataProvider<PropGraph>
              context={PropGraphDataContext}
              collection="propgraphs"
              sort={PROP_GRAPH_LIST_SORT}
              projection={PROP_GRAPH_ID_LIST_PROJECTION}
              limit={PROP_GRAPH_LIST_LIMIT}
              cacheKey={`${PROP_GRAPH_PAGE_CACHE_PREFIX}:propgraphs:ids:v1`}
            >
              <PagePropGraphInner />
            </DataProvider>
          </DataProvider>
        </DataProvider>
      </PropStrengthDataProvider>
    </KeyValueDataProvider>
  );
}

function usePropGraphData() {
  const context = useContext(PropGraphDataContext);
  if (!context) throw new Error("usePropGraphData must be used within provider");
  return context;
}

function useEffectConfigData() {
  const context = useContext(EffectDataContext);
  if (!context) throw new Error("useEffectConfigData must be used within provider");
  return context;
}

function useAnimationConfigData() {
  const context = useContext(AnimationDataContext);
  if (!context) throw new Error("useAnimationConfigData must be used within provider");
  return context;
}

function PagePropGraphInner() {
  const context = usePropGraphData();
  const effectContext = useEffectConfigData();
  const animationContext = useAnimationConfigData();
  const strengthContext = usePropStrengthData();
  const { message } = useFeedback()!;
  const { data: types, load: loadTypes } = useKeyValueData("proptypes");
  const { data: rarities, load: loadRarities } = useKeyValueData("proprarities");
  const { data: tags, load: loadTags } = useKeyValueData("proptags");
  const { data: effects, load: loadEffects } = effectContext;
  const { data: animations, load: loadAnimations } = animationContext;
  const [activeKey, setActiveKeyState] = useState(cachedPropGraphPageState.activeKey);
  const [tabs, setTabsState] = useState<EditTab[]>(() => cloneEditTabs(cachedPropGraphPageState.tabs));
  const [managementTabs, setManagementTabsState] = useState<ManagementTab[]>(() => cloneManagementTabs(cachedPropGraphPageState.managementTabs));
  const [listState, setListStateState] = useState<PropGraphListState>(() => clonePropGraphListState(cachedPropGraphPageState.listState));
  const [createOpen, setCreateOpen] = useState(false);
  const [closingTabKey, setClosingTabKey] = useState<string | null>(null);
  const [closingSaving, setClosingSaving] = useState(false);
  const canvasClipboardRef = useRef<CanvasClipboard | null>(null);
  const canvasPasteCountRef = useRef(0);
  const strengthSeededRef = useRef(false);
  const propGraphListFilterKeyRef = useRef("");
  const propGraphListAutoLoadTokenRef = useRef(0);
  const propGraphListAutoLoadingRef = useRef(false);
  const propGraphFullLoadGenerationRef = useRef(0);
  const propGraphFullLoadingIdsRef = useRef(new Set<string>());
  const propGraphFullPendingIdsRef = useRef<string[]>([]);
  const propGraphFullPendingIdSetRef = useRef(new Set<string>());
  const propGraphFullFailedIdsRef = useRef(new Set<string>());
  const propGraphFullRetryTimerRef = useRef<number | null>(null);
  const propGraphFullFailureNotifiedRef = useRef(false);
  const queueFullPropGraphLoadsRef = useRef<(ids: string[]) => void>(() => {});
  const [canvasClipboardVersion, setCanvasClipboardVersion] = useState(0);
  const [simulationStrengthMap, setSimulationStrengthMap] = useState<Map<string, SimulationStrengthValues>>(
    () => new Map(cachedSimulationStrengthMap),
  );
  const [propGraphDetailMap, setPropGraphDetailMap] = useState<Map<string, PropGraph>>(
    () => new Map(cachedPropGraphDetailMap),
  );
  const [propGraphNameIndex, setPropGraphNameIndex] = useState<PropGraph[]>(
    () => cachedPropGraphNameIndex.map(cloneGraph),
  );
  const [propGraphListAutoLoading, setPropGraphListAutoLoading] = useState(false);
  const [propGraphFullLoadStats, setPropGraphFullLoadStats] = useState({ loading: 0, failed: 0 });
  const [version, setVersion] = useState(0);

  const setActiveKey = (updater: SetStateAction<string>) => {
    setActiveKeyState((current) => {
      const next = typeof updater === "function" ? (updater as (value: string) => string)(current) : updater;
      cachedPropGraphPageState.activeKey = next;
      return next;
    });
  };

  const setTabs = (updater: SetStateAction<EditTab[]>) => {
    setTabsState((current) => {
      const next = typeof updater === "function" ? (updater as (value: EditTab[]) => EditTab[])(current) : updater;
      cachedPropGraphPageState.tabs = cloneEditTabs(next);
      return next;
    });
  };

  const setManagementTabs = (updater: SetStateAction<ManagementTab[]>) => {
    setManagementTabsState((current) => {
      const next = typeof updater === "function" ? (updater as (value: ManagementTab[]) => ManagementTab[])(current) : updater;
      cachedPropGraphPageState.managementTabs = cloneManagementTabs(next);
      return next;
    });
  };

  const setListState = (updater: SetStateAction<PropGraphListState>) => {
    setListStateState((current) => {
      const next = typeof updater === "function" ? (updater as (value: PropGraphListState) => PropGraphListState)(current) : updater;
      cachedPropGraphPageState.listState = clonePropGraphListState(next);
      return next;
    });
  };

  const patchListState = (patch: Partial<PropGraphListState>) => {
    setListState((current) => ({
      ...current,
      ...patch,
      filters: patch.filters === undefined ? current.filters : { ...patch.filters },
      sorter: patch.sorter === undefined ? current.sorter : patch.sorter ? { ...patch.sorter } : null,
    }));
  };

  const loadLatestSimulationStrength = useCallback(async () => {
    const res = await dataCall("editor/DataFind", {
      collection: "propsimulations",
      filter: { storageVersion: 15 },
      sort: { createdAt: -1 },
      limit: 1,
      projection: { createdAt: 1 },
    });
    if (!res.isSucc) throw res.err;
    const latest = res.res.data[0] as PropSimulationHistory | undefined;
    if (!latest?._id) {
      cachedSimulationStrengthMap = new Map();
      setSimulationStrengthMap(new Map(cachedSimulationStrengthMap));
      return;
    }
    const statsRes = await dataCall("editor/DataFind", {
      collection: "propsimulation_propstats",
      filter: {
        runId: latest._id,
        scopeKey: "all",
      },
      sort: { finalValue: -1, intrinsicPresentStrength: -1, sourcePresentStrength: -1, presentStrength: -1 },
      limit: 100000,
      projection: {
        propId: 1,
        finalValue: 1,
        balanceValue: 1,
        actualCalculatedStrength: 1,
        balancedSimulationStrength: 1,
        strengthCoefficient: 1,
        propUpdatedAt: 1,
        triggerValue: 1,
        poolValue: 1,
        confidenceLow: 1,
        confidenceHigh: 1,
        sampleCount: 1,
        intrinsicPresentStrength: 1,
        bodyPresentStrength: 1,
        sourcePresentStrength: 1,
        presentStrength: 1,
        presentRounds: 1,
      },
    });
    if (!statsRes.isSucc) throw statsRes.err;
    cachedSimulationStrengthMap = new Map(
      statsRes.res.data
        .map((row: any) => {
          const body = Number(row.bodyPresentStrength ?? row.presentStrength ?? 0);
          const source = Number(row.sourcePresentStrength ?? row.presentStrength ?? 0);
          const intrinsic = Number(row.intrinsicPresentStrength ?? source);
          const final = Number(row.finalValue ?? intrinsic);
          const balance = Number(row.balanceValue ?? final);
          const actualCalculatedStrength = Number(row.actualCalculatedStrength);
          const balancedSimulationStrength = Number(row.balancedSimulationStrength ?? final);
          const rawCoefficient = row.strengthCoefficient;
          const strengthCoefficient =
            rawCoefficient === null || rawCoefficient === undefined
              ? null
              : Number(rawCoefficient);
          const trigger = Number(row.triggerValue ?? intrinsic);
          const pool = Number(row.poolValue ?? 0);
          const confidenceLow = Number(row.confidenceLow ?? final);
          const confidenceHigh = Number(row.confidenceHigh ?? final);
          return [
            String(row.propId),
            {
              final: Number.isFinite(final) ? final : body,
              balance: Number.isFinite(balance) ? balance : final,
              actualCalculatedStrength: Number.isFinite(actualCalculatedStrength) ? actualCalculatedStrength : undefined,
              balancedSimulationStrength: Number.isFinite(balancedSimulationStrength) ? balancedSimulationStrength : final,
              strengthCoefficient: strengthCoefficient === null || Number.isFinite(strengthCoefficient) ? strengthCoefficient : null,
              propUpdatedAt: Number(row.propUpdatedAt ?? 0),
              simulationCreatedAt: Number(latest.createdAt ?? 0),
              trigger: Number.isFinite(trigger) ? trigger : body,
              pool: Number.isFinite(pool) ? pool : 0,
              confidenceLow: Number.isFinite(confidenceLow) ? confidenceLow : final,
              confidenceHigh: Number.isFinite(confidenceHigh) ? confidenceHigh : final,
              sampleCount: Number(row.sampleCount ?? row.presentRounds ?? 0),
              intrinsic: Number.isFinite(intrinsic) ? intrinsic : source,
              body: Number.isFinite(body) ? body : 0,
              source: Number.isFinite(source) ? source : 0,
            },
          ];
        }),
    );
    setSimulationStrengthMap(new Map(cachedSimulationStrengthMap));
  }, []);

  const loadVersion = useCallback(async () => {
    const res = await dataCall("editor/DataFind", {
      collection: "versions",
      filter: { collection: "propgraphs" },
      limit: 1,
    });
    if (!res.isSucc) throw res.err;
    setVersion(res.res?.data?.[0]?.version ?? 0);
  }, []);

  const loadPropGraphNameIndex = useCallback(async () => {
    const res = await dataCall("editor/DataFind", {
      collection: "propgraphs",
      filter: {},
      sort: PROP_GRAPH_LIST_SORT,
      limit: 100000,
      projection: PROP_GRAPH_NAME_INDEX_PROJECTION,
    });
    if (!res.isSucc) throw res.err;
    cachedPropGraphNameIndex = res.res.data.map(normalizePropGraphNameIndexItem);
    setPropGraphNameIndex(cachedPropGraphNameIndex.map(cloneGraph));
  }, []);

  const propGraphListFilter = useMemo(
    () => createPropGraphListServerFilter(listState),
    [listState],
  );
  const propGraphListFilterKey = useMemo(
    () => JSON.stringify(propGraphListFilter),
    [propGraphListFilter],
  );

  const writePropGraphDetails = useCallback((graphs: PropGraph[]) => {
    if (!graphs.length) return;
    setPropGraphDetailMap((current) => {
      const next = new Map(current);
      graphs.forEach((graph) => {
        next.set(String(graph._id), normalizePropGraph(graph));
      });
      cachedPropGraphDetailMap = new Map(next);
      return next;
    });
  }, []);

  const updatePropGraphFullLoadStats = useCallback(() => {
    setPropGraphFullLoadStats({
      loading: propGraphFullLoadingIdsRef.current.size,
      failed: propGraphFullFailedIdsRef.current.size,
    });
  }, []);

  const getFullPropGraph = useCallback(async (graphOrId: PropGraph | string) => {
    const id = typeof graphOrId === "string" ? graphOrId : String(graphOrId._id);
    const existing = cachedPropGraphDetailMap.get(id);
    if (existing) return cloneGraph(existing);
    if (typeof graphOrId !== "string" && isFullPropGraphDocument(graphOrId)) {
      const normalized = normalizePropGraph(graphOrId);
      writePropGraphDetails([normalized]);
      return cloneGraph(normalized);
    }
    const res = await dataCall("editor/DataFind", {
      collection: "propgraphs",
      filter: { _id: id },
      limit: 1,
    });
    if (!res.isSucc) throw res.err;
    const graph = res.res.data[0] as PropGraph | undefined;
    if (!graph) throw new Error("道具不存在或已被删除");
    const normalized = normalizePropGraph(graph);
    writePropGraphDetails([normalized]);
    return cloneGraph(normalized);
  }, [writePropGraphDetails]);

  const setPropGraphFullLoading = useCallback((id: string, loading: boolean) => {
    if (!id) return;
    if (loading) {
      propGraphFullLoadingIdsRef.current.add(id);
    } else {
      propGraphFullLoadingIdsRef.current.delete(id);
    }
    updatePropGraphFullLoadStats();
  }, [updatePropGraphFullLoadStats]);

  const loadSinglePropGraphDetail = useCallback(async (id: string) => {
    const res = await dataCall("editor/DataFind", {
      collection: "propgraphs",
      filter: { _id: id },
      limit: 1,
    });
    if (!res.isSucc) throw res.err;
    const graph = res.res.data[0] as PropGraph | undefined;
    return graph ? normalizePropGraph(graph) : undefined;
  }, []);

  const schedulePropGraphFullRetry = useCallback(() => {
    if (propGraphFullRetryTimerRef.current !== null) {
      window.clearTimeout(propGraphFullRetryTimerRef.current);
    }
    const generation = propGraphFullLoadGenerationRef.current;
    propGraphFullRetryTimerRef.current = window.setTimeout(() => {
      propGraphFullRetryTimerRef.current = null;
      if (generation !== propGraphFullLoadGenerationRef.current) return;
      const retryIds = Array.from(propGraphFullFailedIdsRef.current);
      if (!retryIds.length) return;
      queueFullPropGraphLoadsRef.current(retryIds);
    }, 1000);
  }, []);

  const queueFullPropGraphLoads = useCallback((ids: string[]) => {
    const missingIds = Array.from(new Set(ids.map(String).filter(Boolean)))
      .filter((id) =>
        !cachedPropGraphDetailMap.has(id) &&
        !propGraphFullLoadingIdsRef.current.has(id) &&
        !propGraphFullPendingIdSetRef.current.has(id)
      );
    missingIds.forEach((id) => {
      propGraphFullPendingIdsRef.current.push(id);
      propGraphFullPendingIdSetRef.current.add(id);
    });
    if (!propGraphFullPendingIdsRef.current.length) return;

    const generation = propGraphFullLoadGenerationRef.current;
    const runNext = async (): Promise<void> => {
      if (generation !== propGraphFullLoadGenerationRef.current) return;
      const id = propGraphFullPendingIdsRef.current.shift();
      if (!id) return;
      propGraphFullPendingIdSetRef.current.delete(id);
      if (cachedPropGraphDetailMap.has(id) || propGraphFullLoadingIdsRef.current.has(id)) {
        await runNext();
        return;
      }
      setPropGraphFullLoading(id, true);
      try {
        const graph = await loadSinglePropGraphDetail(id);
        if (graph && generation === propGraphFullLoadGenerationRef.current) {
          propGraphFullFailedIdsRef.current.delete(id);
          writePropGraphDetails([graph]);
          updatePropGraphFullLoadStats();
        } else if (generation === propGraphFullLoadGenerationRef.current) {
          propGraphFullFailedIdsRef.current.add(id);
          updatePropGraphFullLoadStats();
          schedulePropGraphFullRetry();
        }
      } catch (e: any) {
        if (generation === propGraphFullLoadGenerationRef.current) {
          propGraphFullFailedIdsRef.current.add(id);
          updatePropGraphFullLoadStats();
          if (!propGraphFullFailureNotifiedRef.current) {
            propGraphFullFailureNotifiedRef.current = true;
            message.warning(e?.message ?? "部分道具详情加载失败，稍后重试");
          }
          schedulePropGraphFullRetry();
        }
      } finally {
        setPropGraphFullLoading(id, false);
        if (generation === propGraphFullLoadGenerationRef.current) {
          await runNext();
        }
      }
    };

    const availableWorkerCount = Math.max(0, PROP_GRAPH_FULL_LOAD_CONCURRENCY - propGraphFullLoadingIdsRef.current.size);
    const workerCount = Math.min(availableWorkerCount, propGraphFullPendingIdsRef.current.length);
    for (let index = 0; index < workerCount; index += 1) {
      void runNext();
    }
  }, [loadSinglePropGraphDetail, message, schedulePropGraphFullRetry, setPropGraphFullLoading, updatePropGraphFullLoadStats, writePropGraphDetails]);
  queueFullPropGraphLoadsRef.current = queueFullPropGraphLoads;

  const queuePropGraphFullLoads = useCallback(async (rows: PropGraph[]) => {
    queueFullPropGraphLoads(rows.map((item) => String(item._id)).filter(Boolean));
  }, [queueFullPropGraphLoads]);

  const loadPropGraphListBatch = async (reset: boolean) => {
    if (!reset && propGraphListAutoLoadingRef.current) return [];
    const token = reset
      ? propGraphListAutoLoadTokenRef.current + 1
      : propGraphListAutoLoadTokenRef.current;
    if (reset) propGraphListAutoLoadTokenRef.current = token;
    if (reset) {
      propGraphFullLoadGenerationRef.current += 1;
      propGraphFullLoadingIdsRef.current.clear();
      propGraphFullPendingIdsRef.current = [];
      propGraphFullPendingIdSetRef.current.clear();
      propGraphFullFailedIdsRef.current.clear();
      propGraphFullFailureNotifiedRef.current = false;
      if (propGraphFullRetryTimerRef.current !== null) {
        window.clearTimeout(propGraphFullRetryTimerRef.current);
        propGraphFullRetryTimerRef.current = null;
      }
      updatePropGraphFullLoadStats();
    }
    propGraphListAutoLoadingRef.current = true;
    setPropGraphListAutoLoading(true);
    try {
      const rows = await context.load(propGraphListFilter, reset);
      queuePropGraphFullLoads(rows);
      return rows;
    } finally {
      if (token === propGraphListAutoLoadTokenRef.current) {
        propGraphListAutoLoadingRef.current = false;
        setPropGraphListAutoLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!cachedSimulationStrengthMap.size) {
      loadLatestSimulationStrength().catch((e) => {
        message.error(e?.message ?? "加载模拟强度失败");
      });
    }
    if (!types.length) loadTypes();
    if (!rarities.length) loadRarities();
    if (!tags.length) loadTags();
    if (!effects.length) loadEffects({}, true);
    if (!animations.length) loadAnimations({}, true);
    if (!cachedPropGraphNameIndex.length) {
      loadPropGraphNameIndex().catch((e) => {
        message.error(e?.message ?? "加载道具名称索引失败");
      });
    }
    loadVersion().catch((e) => {
      message.error(e?.message ?? "加载数据版本失败");
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const shouldReuseCachedRows = context.data.length > 0 && propGraphListFilterKeyRef.current === "";
    if (shouldReuseCachedRows) {
      propGraphListFilterKeyRef.current = propGraphListFilterKey;
      queuePropGraphFullLoads(context.data);
      return;
    }
    if (propGraphListFilterKeyRef.current === propGraphListFilterKey && context.data.length > 0) return;
    propGraphListFilterKeyRef.current = propGraphListFilterKey;
    loadPropGraphListBatch(true).catch((e) => {
      message.error(e?.message ?? "加载道具列表失败");
    });
  }, [propGraphListFilterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!context.hasMore) return;
    if (!propGraphListFilterKeyRef.current) return;
    if (propGraphListAutoLoadingRef.current) return;
    const token = propGraphListAutoLoadTokenRef.current;
    const timer = window.setTimeout(() => {
      if (token !== propGraphListAutoLoadTokenRef.current) return;
      if (!context.hasMore || propGraphListAutoLoadingRef.current) return;
      loadPropGraphListBatch(false).catch((e) => {
        if (token === propGraphListAutoLoadTokenRef.current) {
          message.error(e?.message ?? "加载道具列表失败");
        }
      });
    }, PROP_GRAPH_LIST_AUTO_LOAD_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [context.data.length, context.hasMore, propGraphListFilterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (strengthSeededRef.current || !types.length || !rarities.length) return;
    if (strengthContext.data.length) {
      strengthSeededRef.current = true;
      return;
    }
    strengthSeededRef.current = true;
    (async () => {
      const existing = await strengthContext.get({}, { rarity: 1 }, 1000);
      if (!existing.length) {
        await strengthContext.add(createDefaultPropStrengthRows(rarities, types));
      }
      await strengthContext.load({}, true);
    })().catch((e) => {
      message.error(e?.message ?? "加载强度配置失败");
    });
  }, [types, rarities]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = async () => {
    await Promise.all([loadPropGraphListBatch(true), loadLatestSimulationStrength(), loadVersion(), loadPropGraphNameIndex()]);
  };

  const propGraphIds = useMemo(
    () => context.data.map((item) => String(item._id)).filter(Boolean),
    [context.data],
  );
  const propGraphs = useMemo(
    () =>
      propGraphIds
        .map((id) => propGraphDetailMap.get(id))
        .filter((item): item is PropGraph => Boolean(item))
        .map(cloneGraph),
    [propGraphIds, propGraphDetailMap],
  );
  useEffect(() => {
    const missingIds = propGraphIds.filter((id) => !propGraphDetailMap.has(id));
    if (!missingIds.length) return;
    const immediateIds = missingIds.filter((id) => !propGraphFullFailedIdsRef.current.has(id));
    if (immediateIds.length) queueFullPropGraphLoads(immediateIds);
    if (missingIds.length > immediateIds.length) schedulePropGraphFullRetry();
  }, [propGraphIds, propGraphDetailMap, queueFullPropGraphLoads, schedulePropGraphFullRetry]);
  const propReferenceGraphs = useMemo(
    () => mergePropGraphNameIndex(propGraphNameIndex, propGraphs, Array.from(propGraphDetailMap.values())),
    [propGraphNameIndex, propGraphs, propGraphDetailMap],
  );
  const descriptionPropGraphs = propReferenceGraphs;

  useEffect(() => {
    queuePropGraphFullLoads(context.data).catch((e) => {
      message.error(e?.message ?? "加载道具详情失败");
    });
  }, [context.data, queuePropGraphFullLoads]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (propGraphFullRetryTimerRef.current !== null) {
        window.clearTimeout(propGraphFullRetryTimerRef.current);
        propGraphFullRetryTimerRef.current = null;
      }
    };
  }, []);

  const configOptions = useMemo<PropGraphConfigOptions>(
    () => ({ types, rarities, tags, props: propReferenceGraphs, descriptionProps: descriptionPropGraphs, strengths: strengthContext.data, effects, animations }),
    [types, rarities, tags, propReferenceGraphs, descriptionPropGraphs, strengthContext.data, effects, animations],
  );

  const openEditor = async (graph: PropGraph) => {
    const key = getTabKey(graph._id);
    if (tabs.some((tab) => tab.key === key)) {
      setActiveKey(key);
      return;
    }
    let normalized: PropGraph;
    try {
      normalized = await getFullPropGraph(graph);
    } catch (e: any) {
      message.error(e?.message ?? "加载道具详情失败");
      return;
    }
    setTabs((prev) => {
      if (prev.some((tab) => tab.key === key)) return prev;
      return prev.concat({
        key,
        graph: cloneGraph(normalized),
        savedGraph: cloneGraph(normalized),
      });
    });
    setActiveKey(key);
  };

  const closeTabImmediately = (key: string) => {
    setTabs((prev) => prev.filter((tab) => tab.key !== key));
    setManagementTabs((prev) => prev.filter((tab) => tab.key !== key));
    setActiveKey((current) => (current === key ? "list" : current));
  };

  const requestCloseTab = (key: string) => {
    const tab = tabs.find((item) => item.key === key);
    if (tab && isEditTabDirty(tab)) {
      setClosingTabKey(key);
      return;
    }
    closeTabImmediately(key);
  };

  const saveGraph = async (graph: PropGraph) => {
    const sanitized = sanitizePropGraph(graph);
    const validationIssues = getPropGraphValidationIssues(sanitized);
    const { _id, ...value } = sanitized;
    const res = await dataCall("editor/DataUpdate", {
      collection: "propgraphs",
      filter: { _id },
      update: { $set: value as Record<string, any> },
      multi: false,
    });
    if (!res.isSucc) throw res.err;
    const updatedGraph = await loadPropGraphDetailById(context, _id, {
      ...sanitized,
      updatedAt: res.res.timestamp,
    });
    writePropGraphDetails([updatedGraph]);
    cachedPropGraphNameIndex = mergePropGraphNameIndex(
      cachedPropGraphNameIndex.filter((item) => String(item._id) !== String(_id)),
      [normalizePropGraphNameIndexItem(updatedGraph)],
    );
    setPropGraphNameIndex(cachedPropGraphNameIndex.map(cloneGraph));
    setVersion(res.res.timestamp);
    setTabs((prev) =>
      prev.map((tab) =>
        tab.key === getTabKey(_id)
          ? { ...tab, graph: cloneGraph(updatedGraph), savedGraph: cloneGraph(updatedGraph) }
          : tab,
      ),
    );
    if (validationIssues.length) {
      message.warning(`已保存，但存在 ${validationIssues.length} 个配置问题`);
    } else {
      message.success("已保存");
    }
  };

  const createGraph = async (value: Partial<PropGraph>) => {
    const graph = sanitizePropGraph({
      ...createDefaultPropGraph(),
      ...value,
    });
    const { _id, ...insertValue } = graph;
    const res = await context.add(insertValue);
    await refresh();
    setCreateOpen(false);
    const insertedId = res.insertedIds[0];
    const insertedGraph = await loadPropGraphDetailById(context, insertedId, { ...insertValue, _id: insertedId });
    writePropGraphDetails([insertedGraph]);
    await openEditor(insertedGraph);
    message.success("已新增");
  };

  const removeGraph = async (id: string) => {
    await context.remove(id);
    cachedPropGraphDetailMap.delete(String(id));
    setPropGraphDetailMap(new Map(cachedPropGraphDetailMap));
    await refresh();
    closeTabImmediately(getTabKey(id));
    message.success("已删除");
  };

  const openManagementTab = (key: ManagementTabKey) => {
    const label = getManagementTabLabel(key);
    setManagementTabs((prev) =>
      prev.some((tab) => tab.key === key) ? prev : prev.concat({ key, label }),
    );
    setActiveKey(key);
  };

  const setCanvasClipboard = (payload: CanvasClipboard) => {
    canvasClipboardRef.current = payload;
    canvasPasteCountRef.current = 0;
    setCanvasClipboardVersion((version) => version + 1);
  };

  const nextCanvasPasteCount = () => {
    canvasPasteCountRef.current += 1;
    return canvasPasteCountRef.current;
  };

  const items: TabsProps["items"] = [
    {
      key: "list",
      label: "道具列表",
      closable: false,
      children: (
        <PropGraphList
          data={propGraphs}
          totalCount={propGraphIds.length}
          detailLoadingCount={propGraphFullLoadStats.loading}
          detailFailedCount={propGraphFullLoadStats.failed}
          version={version}
          configOptions={configOptions}
          simulationStrengthMap={simulationStrengthMap}
          listState={listState}
          onListStateChange={patchListState}
          onRefresh={refresh}
          hasMore={context.hasMore}
          backgroundLoading={propGraphListAutoLoading}
          onCreate={() => setCreateOpen(true)}
          onEdit={openEditor}
          onLoadFullGraph={getFullPropGraph}
          onSave={saveGraph}
          onDelete={removeGraph}
          onOpenManagement={openManagementTab}
        />
      ),
    },
    ...managementTabs.map((tab) => ({
      key: tab.key,
      label: tab.label,
      children: renderManagementTab(tab.key, () => requestCloseTab(tab.key), effectContext, animationContext),
    })),
    ...tabs.map((tab) => ({
      key: tab.key,
      label: tab.graph.name || "未命名道具",
      children: (
        <PropGraphCanvasEditor
          value={tab.graph}
          active={activeKey === tab.key}
          configOptions={configOptions}
          simulationStrength={simulationStrengthMap.get(tab.graph._id)}
          canvasClipboard={canvasClipboardRef.current}
          canvasClipboardVersion={canvasClipboardVersion}
          onSetCanvasClipboard={setCanvasClipboard}
          onNextCanvasPasteCount={nextCanvasPasteCount}
          onChange={(next) =>
            setTabs((prev) =>
              prev.map((item) =>
                item.key === tab.key ? { ...item, graph: next } : item,
              ),
            )
          }
          onSave={saveGraph}
          onClose={() => requestCloseTab(tab.key)}
        />
      ),
    })),
  ];

  const closingTab = closingTabKey
    ? tabs.find((tab) => tab.key === closingTabKey)
    : undefined;
  const closeConfirmModal = () => {
    if (closingSaving) return;
    setClosingTabKey(null);
  };
  const discardAndClose = () => {
    if (!closingTabKey) return;
    closeTabImmediately(closingTabKey);
    setClosingTabKey(null);
  };
  const saveAndClose = async () => {
    if (!closingTab || !closingTabKey) return;
    setClosingSaving(true);
    try {
      await saveGraph(closingTab.graph);
      closeTabImmediately(closingTabKey);
      setClosingTabKey(null);
    } catch (e: any) {
      message.error(e?.message ?? "保存失败");
    } finally {
      setClosingSaving(false);
    }
  };

  return (
    <PropGraphPageFrame vertical>
      <FullHeightTabs>
        <Tabs
          className="propgraph-tabs"
          type="editable-card"
          hideAdd
          activeKey={activeKey}
          items={items}
          onChange={setActiveKey}
          onEdit={(targetKey, action) => {
            if (action === "remove" && typeof targetKey === "string") {
              requestCloseTab(targetKey);
            }
          }}
        />
      </FullHeightTabs>
      <Modal
        open={Boolean(closingTab)}
        centered
        title="关闭确认"
        onCancel={closeConfirmModal}
        closable={!closingSaving}
        maskClosable={!closingSaving}
        footer={[
          <Button key="discard" danger disabled={closingSaving} onClick={discardAndClose}>
            放弃并关闭
          </Button>,
          <Button key="save" type="primary" loading={closingSaving} onClick={saveAndClose}>
            保存并关闭
          </Button>,
          <Button key="cancel" disabled={closingSaving} onClick={closeConfirmModal}>
            取消
          </Button>,
        ]}
      >
        当前道具有未保存的修改，关闭前请选择处理方式。
      </Modal>
      <CreatePropGraphModal
        open={createOpen}
        configOptions={configOptions}
        onCancel={() => setCreateOpen(false)}
        onSubmit={createGraph}
      />
    </PropGraphPageFrame>
  );
}

function PropGraphList({
  data,
  totalCount,
  detailLoadingCount,
  detailFailedCount,
  version,
  configOptions,
  simulationStrengthMap,
  listState,
  onListStateChange,
  onRefresh,
  hasMore,
  backgroundLoading,
  onCreate,
  onEdit,
  onLoadFullGraph,
  onSave,
  onDelete,
  onOpenManagement,
}: {
  data: PropGraph[];
  totalCount: number;
  detailLoadingCount: number;
  detailFailedCount: number;
  version: number;
  configOptions: PropGraphConfigOptions;
  simulationStrengthMap: Map<string, SimulationStrengthValues>;
  listState: PropGraphListState;
  onListStateChange: (patch: Partial<PropGraphListState>) => void;
  onRefresh: () => Promise<void>;
  hasMore: boolean;
  backgroundLoading: boolean;
  onCreate: () => void;
  onEdit: (graph: PropGraph) => Promise<void>;
  onLoadFullGraph: (graphOrId: PropGraph | string) => Promise<PropGraph>;
  onSave: (graph: PropGraph) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onOpenManagement: (key: ManagementTabKey) => void;
}) {
  const { message } = useFeedback()!;
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editingRow, setEditingRow] = useState<PropGraph | null>(null);
  const [savingRow, setSavingRow] = useState(false);
  const { types, rarities, tags, props, descriptionProps, strengths } = configOptions;
  const typeMap = useMemo(() => buildDictLabelMap(types), [types]);
  const rarityMap = useMemo(() => buildDictLabelMap(rarities), [rarities]);
  const tagMap = useMemo(() => buildDictLabelMap(tags), [tags]);
  const typeFilters = useMemo(() => buildDictFilters(types), [types]);
  const rarityFilters = useMemo(() => buildDictFilters(rarities), [rarities]);
  const tagFilters = useMemo(() => buildDictFilters(tags), [tags]);
  const modifierFilters = useMemo(() => buildModifierFilters(data), [data]);
  const propNameCandidates = useMemo(
    () => buildDescriptionPropNameCandidates(descriptionProps),
    [descriptionProps],
  );
  const descriptionMap = useMemo(() => {
    const map = new Map<string, string>();
    data.forEach((graph) => {
      map.set(graph._id, getPropGraphDescription(graph, { types, rarities, tags, props: descriptionProps }));
    });
    return map;
  }, [data, types, rarities, tags, descriptionProps]);
  const negativeDescriptionMap = useMemo(() => {
    const map = new Map<string, string[]>();
    data.forEach((graph) => {
      map.set(graph._id, getPropGraphNegativeDescriptionFragments(graph, { types, rarities, tags, props: descriptionProps }));
    });
    return map;
  }, [data, types, rarities, tags, descriptionProps]);
  const getDescriptionText = (graph: PropGraph) =>
    descriptionMap.get(graph._id) ?? getPropGraphDescription(graph, { types, rarities, tags, props: descriptionProps });
  const getNegativeDescriptionFragments = (graph: PropGraph) =>
    negativeDescriptionMap.get(graph._id) ??
    getPropGraphNegativeDescriptionFragments(graph, { types, rarities, tags, props: descriptionProps });

  const filtered = useMemo(() => {
    const pattern = listState.keyword.trim();
    if (!pattern) return data;
    return data.filter((item) =>
      testRegexFilter(pattern, item.name) ||
      testRegexFilter(pattern, descriptionMap.get(item._id) ?? ""),
    );
  }, [data, listState.keyword, descriptionMap]);

  const sortedData = useMemo(
    () => filtered.slice().sort(comparePropGraphListDefault),
    [filtered],
  );
  const strengthMap = useMemo(() => {
    const map = new Map<string, PropGraphStrengthResult>();
    sortedData.forEach((item) => {
      map.set(item._id, getPropGraphStrength(item, { props, strengths }));
    });
    return map;
  }, [sortedData, props, strengths]);
  const validationMap = useMemo(() => {
    const map = new Map<string, PropGraphValidationIssue[]>();
    data.forEach((item) => {
      map.set(item._id, getPropGraphValidationIssues(item));
    });
    return map;
  }, [data]);

  const patchEditingRow = (patch: Partial<PropGraph>) => {
    setEditingRow((current) => (current ? { ...current, ...patch } : current));
  };

  const beginInlineEdit = async (record: PropGraph) => {
    setSavingRow(true);
    try {
      const fullGraph = await onLoadFullGraph(record);
      setEditingRowId(record._id);
      setEditingRow(cloneGraph(fullGraph));
    } catch (e: any) {
      message.error(e?.message ?? "加载道具详情失败");
    } finally {
      setSavingRow(false);
    }
  };

  const cancelInlineEdit = () => {
    setEditingRowId(null);
    setEditingRow(null);
  };

  const submitInlineEdit = async () => {
    if (!editingRow) return;
    setSavingRow(true);
    try {
      await onSave(editingRow);
      cancelInlineEdit();
    } catch (e: any) {
      message.error(e?.message ?? "保存失败");
    } finally {
      setSavingRow(false);
    }
  };

  const copyGraphJson = async (record: PropGraph) => {
    let fullGraph: PropGraph;
    try {
      fullGraph = await onLoadFullGraph(record);
    } catch (e: any) {
      message.error(e?.message ?? "加载道具详情失败");
      return;
    }
    const text = JSON.stringify(fullGraph, null, 2);
    const copied = await copyTextToClipboard(text);
    if (copied) {
      message.success("已复制道具 JSON");
    } else {
      message.error("复制失败");
    }
  };

  const handleTableChange: TableProps<PropGraph>["onChange"] = (_pagination, filters, sorter) => {
    onListStateChange({
      filters: normalizePropGraphTableFilters(filters),
      sorter: normalizePropGraphTableSorter(sorter),
    });
  };

  const fullLoadTotal = Math.max(totalCount, data.length);
  const fullLoadStatus = detailFailedCount > 0
    ? `已加载 ${data.length}/${fullLoadTotal} 条，失败 ${detailFailedCount} 条，正在重试`
    : detailLoadingCount > 0 || backgroundLoading || hasMore
      ? `已加载 ${data.length}/${fullLoadTotal} 条`
      : `已加载 ${data.length} 条`;

  return (
    <Flex className="propgraph-list-root" vertical gap={12} style={{ height: "100%", minHeight: 0, overflow: "hidden" }}>
      <Flex className="propgraph-list-header" justify="space-between" align="center">
        <Flex className="propgraph-list-meta" align="center" gap={12}>
          <Title level={5} style={{ margin: 0 }}>
            炼金道具
          </Title>
          <Text type="secondary">{`[数据版本：${version}]`}</Text>
          <Text type="secondary">原子图结构：propgraphs</Text>
          <Text type="secondary">{fullLoadStatus}</Text>
        </Flex>
        <Flex className="propgraph-list-actions" gap={8} wrap="wrap" justify="flex-end">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="正则搜索名称 / 描述"
            value={listState.keyword}
            onChange={(e) => onListStateChange({ keyword: e.target.value })}
            style={{ width: 260 }}
          />
          <Button icon={<ReloadOutlined />} onClick={onRefresh}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={onCreate}>
            新增
          </Button>
          <Tooltip title="文档">
            <Button
              aria-label="文档"
              icon={<FileTextOutlined />}
              onClick={() => onOpenManagement("docs")}
            />
          </Tooltip>
          <Tooltip title="类型管理">
            <Button
              aria-label="类型管理"
              icon={<AppstoreOutlined />}
              onClick={() => onOpenManagement("type")}
            />
          </Tooltip>
          <Tooltip title="品质管理">
            <Button
              aria-label="品质管理"
              icon={<StarOutlined />}
              onClick={() => onOpenManagement("rarity")}
            />
          </Tooltip>
          <Tooltip title="标签管理">
            <Button
              aria-label="标签管理"
              icon={<TagsOutlined />}
              onClick={() => onOpenManagement("tag")}
            />
          </Tooltip>
          <Tooltip title="权重管理">
            <Button
              aria-label="权重管理"
              icon={<BarChartOutlined />}
              onClick={() => onOpenManagement("weight")}
            />
          </Tooltip>
          <Tooltip title="强度管理">
            <Button
              aria-label="强度管理"
              icon={<ApartmentOutlined />}
              onClick={() => onOpenManagement("strength")}
            />
          </Tooltip>
          <Tooltip title="特效管理">
            <Button
              aria-label="特效管理"
              icon={<BgColorsOutlined />}
              onClick={() => onOpenManagement("effect")}
            />
          </Tooltip>
          <Tooltip title="动画管理">
            <Button
              aria-label="动画管理"
              icon={<PlayCircleOutlined />}
              onClick={() => onOpenManagement("animation")}
            />
          </Tooltip>
        </Flex>
      </Flex>
      <PropGraphListTableWrap>
        <Table<PropGraph>
          rowKey="_id"
          pagination={false}
          dataSource={sortedData}
          tableLayout="fixed"
          scroll={{ x: 1640, y: "100%" }}
          onChange={handleTableChange}
          columns={[
            {
              title: "序号",
              width: 64,
              render: (_: any, __: PropGraph, index: number) => (
                <IdRender style={{ whiteSpace: "nowrap" }}>{index + 1}</IdRender>
              ),
            },
            {
              title: "名称",
              key: "name",
              dataIndex: "name",
              width: 130,
              filteredValue: getListColumnFilteredValue(listState, "name"),
              filterDropdown: renderRegexFilterDropdown("名称正则"),
              filterIcon: <SearchOutlined />,
              onFilter: (value, record) => testRegexFilter(value, record.name),
              render: (name: string, record) =>
                editingRowId === record._id && editingRow ? (
                  <Input
                    value={editingRow.name}
                    onChange={(e) => patchEditingRow({ name: e.target.value })}
                  />
                ) : (
                  name
                ),
            },
            {
              title: "资源ID",
              key: "assetId",
              dataIndex: "assetId",
              width: 110,
              filteredValue: getListColumnFilteredValue(listState, "assetId"),
              sortOrder: getListColumnSortOrder(listState, "assetId"),
              filterDropdown: renderRegexFilterDropdown("资源ID正则"),
              filterIcon: <SearchOutlined />,
              onFilter: (value, record) => testRegexFilter(value, record.assetId),
              sorter: (a, b) => String(a.assetId ?? "").localeCompare(String(b.assetId ?? "")),
              render: (assetId: string, record) =>
                editingRowId === record._id && editingRow ? (
                  <Input
                    value={editingRow.assetId}
                    onChange={(e) => patchEditingRow({ assetId: e.target.value })}
                  />
                ) : (
                  assetId
                ),
            },
            {
              title: "类型",
              key: "type",
              dataIndex: "type",
              width: 80,
              filteredValue: getListColumnFilteredValue(listState, "type"),
              sortOrder: getListColumnSortOrder(listState, "type"),
              filters: typeFilters,
              onFilter: (value, record) => Number(record.type) === Number(value),
              sorter: (a, b) => Number(a.type) - Number(b.type),
              render: (type: number, record) =>
                editingRowId === record._id && editingRow ? (
                  <Select
                    value={editingRow.type}
                    options={types}
                    onChange={(type) => patchEditingRow({ type })}
                    style={{ width: "100%" }}
                  />
                ) : (
                  formatDictLabel(typeMap, type)
                ),
            },
            {
              title: "品质",
              key: "rarity",
              dataIndex: "rarity",
              width: 80,
              filteredValue: getListColumnFilteredValue(listState, "rarity"),
              sortOrder: getListColumnSortOrder(listState, "rarity"),
              filters: rarityFilters,
              onFilter: (value, record) => Number(record.rarity) === Number(value),
              sorter: (a, b) => Number(a.rarity) - Number(b.rarity),
              render: (rarity: number, record) =>
                editingRowId === record._id && editingRow ? (
                  <Select
                    value={editingRow.rarity}
                    options={rarities}
                    onChange={(rarity) => patchEditingRow({ rarity })}
                    style={{ width: "100%" }}
                  />
                ) : (
                  formatDictLabel(rarityMap, rarity)
                ),
            },
            {
              title: "标签",
              key: "tags",
              dataIndex: "tags",
              width: 90,
              filteredValue: getListColumnFilteredValue(listState, "tags"),
              filters: tagFilters,
              onFilter: (value, record) =>
                (record.tags ?? []).map(Number).includes(Number(value)),
              render: (tagValues: number[], record) =>
                editingRowId === record._id && editingRow ? (
                  <Select
                    mode="multiple"
                    value={editingRow.tags ?? []}
                    options={tags}
                    onChange={(tags) => patchEditingRow({ tags })}
                    style={{ width: "100%" }}
                  />
                ) : (
                  <Flex gap={4} wrap="wrap">
                    {(tagValues ?? []).map((tag) => (
                      <Tag key={tag} style={{ marginInlineEnd: 0 }}>
                        {formatDictLabel(tagMap, tag)}
                      </Tag>
                    ))}
                  </Flex>
                ),
            },
            {
              title: "衍生物",
              dataIndex: "derived",
              width: 70,
              render: (derived: boolean, record) =>
                editingRowId === record._id && editingRow ? (
                  <Checkbox
                    checked={Boolean(editingRow.derived)}
                    onChange={(e) => patchEditingRow({ derived: e.target.checked })}
                  />
                ) : (
                  <Tag color={derived ? "blue" : "default"} style={{ marginInlineEnd: 0 }}>
                    {derived ? "是" : "否"}
                  </Tag>
                ),
            },
            {
              title: "产出",
              dataIndex: "yield",
              width: 70,
              render: (yieldValue: number, record) =>
                editingRowId === record._id && editingRow ? (
                  <InputNumber
                    min={-999}
                    max={999}
                    value={editingRow.yield}
                    onChange={(value) => patchEditingRow({ yield: Number(value ?? 0) })}
                    style={{ width: "100%" }}
                  />
                ) : (
                  yieldValue
                ),
            },
            {
              title: "强度",
              key: "strength",
              width: 260,
              render: (_, record) => {
                const graphForStrength =
                  editingRowId === record._id && editingRow ? editingRow : record;
                const strength =
                  graphForStrength === record
                    ? strengthMap.get(record._id) ?? getPropGraphStrength(record, { props, strengths })
                    : getPropGraphStrength(graphForStrength, { props, strengths });
                return (
                  <StrengthCell
                    strength={strength}
                    simulationStrength={simulationStrengthMap.get(record._id)}
                    graph={graphForStrength}
                    props={props}
                    strengths={strengths}
                  />
                );
              },
            },
            {
              title: "修改",
              key: "updatedBy",
              dataIndex: "updatedAt",
              width: 130,
              filteredValue: getListColumnFilteredValue(listState, "updatedBy"),
              sortOrder: getListColumnSortOrder(listState, "updatedBy"),
              filters: modifierFilters,
              filterSearch: true,
              onFilter: (value, record) => String(record.updatedBy ?? "") === String(value),
              sorter: (a, b) => Number(a.updatedAt ?? 0) - Number(b.updatedAt ?? 0),
              render: (_: number, record) => (
                <Flex vertical gap={2} style={{ minWidth: 0 }}>
                  <Text ellipsis title={record.updatedBy || "-"}>
                    {record.updatedBy || "-"}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {formatPropGraphUpdatedTime(record.updatedAt)}
                  </Text>
                </Flex>
              ),
            },
            {
              title: "描述",
              key: "description",
              className: "propgraph-description-column",
              filteredValue: getListColumnFilteredValue(listState, "description"),
              filterDropdown: renderRegexFilterDropdown("描述正则"),
              filterIcon: <SearchOutlined />,
              onFilter: (value, record) => testRegexFilter(value, getDescriptionText(record)),
              render: (_, record) => {
                const description = getDescriptionText(record);
                const validationIssues = validationMap.get(record._id) ?? [];
                const validationText = formatValidationIssueText(validationIssues);
                return (
                  <Flex gap={6} align="flex-start" style={{ minWidth: 0 }}>
                    {validationIssues.length > 0 && (
                      <Tooltip title={validationText}>
                        <WarningOutlined style={{ color: "#ff4d4f", flex: "0 0 auto", marginTop: 2 }} />
                      </Tooltip>
                    )}
                    <span
                      style={{
                        minWidth: 0,
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                        lineHeight: 1.45,
                      }}
                    >
                      <PropDescriptionText
                        text={description}
                        configOptions={configOptions}
                        candidates={propNameCandidates}
                        negativeFragments={getNegativeDescriptionFragments(record)}
                      />
                    </span>
                  </Flex>
                );
              },
            },
            {
              title: "操作",
              width: 160,
              className: "propgraph-action-column",
              render: (_, record) =>
                editingRowId === record._id ? (
                  <Space className="propgraph-action-buttons">
                    <Tooltip title="保存行内修改">
                      <Button
                        type="link"
                        icon={<CheckOutlined />}
                        loading={savingRow}
                        onClick={submitInlineEdit}
                      />
                    </Tooltip>
                    <Tooltip title="取消行内编辑">
                      <Button
                        type="link"
                        icon={<CloseOutlined />}
                        disabled={savingRow}
                        onClick={cancelInlineEdit}
                      />
                    </Tooltip>
                  </Space>
                ) : (
                  <Space className="propgraph-action-buttons">
                    <Tooltip title="复制 JSON">
                      <Button
                        type="link"
                        icon={<CopyOutlined />}
                        onClick={() => copyGraphJson(record)}
                      />
                    </Tooltip>
                    <Tooltip title="行内编辑">
                      <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => beginInlineEdit(record)}
                      />
                    </Tooltip>
                    <Tooltip title="编辑图">
                      <Button
                        type="link"
                        icon={<FormOutlined />}
                        onClick={() => onEdit(record)}
                      />
                    </Tooltip>
                    <Popconfirm
                      title="确认删除这个新道具？"
                      okText="确定"
                      cancelText="取消"
                      onConfirm={() => onDelete(record._id)}
                    >
                      <Button danger type="link" icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </Space>
                ),
            },
          ]}
        />
      </PropGraphListTableWrap>
    </Flex>
  );
}

function getManagementTabLabel(key: ManagementTabKey) {
  switch (key) {
    case "docs":
      return "文档";
    case "type":
      return "类型管理";
    case "rarity":
      return "品质管理";
    case "tag":
      return "标签管理";
    case "weight":
      return "权重管理";
    case "strength":
      return "强度管理";
    case "effect":
      return "特效管理";
    case "animation":
      return "动画管理";
  }
}

function renderManagementTab(
  key: ManagementTabKey,
  onBack: () => void,
  effectContext: ContextType<EffectConfig>,
  animationContext: ContextType<AnimationConfig>,
) {
  switch (key) {
    case "docs":
      return <PropGraphDocsTab onClose={onBack} />;
    case "type":
      return (
        <PageKeyValue
          title="道具类型管理"
          collection="proptypes"
          backText="返回道具列表"
          onBack={onBack}
          closeButton
        />
      );
    case "rarity":
      return (
        <PageKeyValue
          title="道具品质管理"
          collection="proprarities"
          backText="返回道具列表"
          onBack={onBack}
          closeButton
        />
      );
    case "tag":
      return (
        <PageKeyValue
          title="道具标签管理"
          collection="proptags"
          backText="返回道具列表"
          onBack={onBack}
          closeButton
        />
      );
    case "weight":
      return (
        <PagePropRarityWight
          backText="返回道具列表"
          onBack={onBack}
          closeButton
        />
      );
    case "strength":
      return (
        <PagePropStrength
          backText="返回道具列表"
          onBack={onBack}
          closeButton
        />
      );
    case "effect":
      return (
        <EffectManagementPanel
          context={effectContext}
          onBack={onBack}
          closeButton
        />
      );
    case "animation":
      return (
        <AnimationManagementPanel
          context={animationContext}
          onBack={onBack}
          closeButton
        />
      );
  }
}

function DescriptionText({ text, compact }: { text: ReactNode; compact?: boolean }) {
  if (typeof text !== "string" && typeof text !== "number") {
    return <PropGraphDescriptionText $compact={compact}>{text}</PropGraphDescriptionText>;
  }

  const lines = splitDescriptionLines(String(text));
  return (
    <PropGraphDescriptionText $compact={compact}>
      {lines.map((line, index) => (
        <span className="propgraph-description-line" key={`${index}:${line}`}>
          {line}
        </span>
      ))}
    </PropGraphDescriptionText>
  );
}

function splitDescriptionLines(text: string) {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (!normalized) return [""];

  const primaryParts = normalized.match(/[^。；;]+[。；;]?/g) ?? [normalized];
  return primaryParts.flatMap((part) => splitLongDescriptionPart(part.trim())).filter(Boolean);
}

function splitLongDescriptionPart(text: string) {
  if (text.length <= 44 || !text.includes("，")) return [text];
  const commaParts = text.match(/[^，]+，?/g) ?? [text];
  const lines: string[] = [];
  let current = "";

  commaParts.forEach((part) => {
    const next = `${current}${part}`;
    if (current && next.length > 44) {
      lines.push(current);
      current = part;
      return;
    }
    current = next;
  });

  if (current) lines.push(current);
  return lines;
}

function cloneEditTabs(tabs: EditTab[]) {
  return tabs.map((tab) => ({
    key: tab.key,
    graph: cloneGraph(tab.graph),
    savedGraph: cloneGraph(tab.savedGraph),
  }));
}

function cloneManagementTabs(tabs: ManagementTab[]) {
  return tabs.map((tab) => ({ ...tab }));
}

function clonePropGraphListState(state: PropGraphListState): PropGraphListState {
  return {
    keyword: state.keyword,
    filters: { ...state.filters },
    sorter: state.sorter ? { ...state.sorter } : null,
  };
}

async function loadPropGraphDetailById(context: ContextType<PropGraph>, id: string, fallback: PropGraph) {
  const rows = await context.get({ _id: id }, undefined, 1);
  return rows[0] ? normalizePropGraph(rows[0]) : normalizePropGraph(fallback);
}

function normalizePropGraphTableFilters(filters: Record<string, any[] | null>): PropGraphListState["filters"] {
  return Object.fromEntries(
    Object.entries(filters).map(([key, value]) => [key, value?.length ? [...value] : null]),
  );
}

function normalizePropGraphTableSorter(sorter: any): PropGraphListState["sorter"] {
  const activeSorter = Array.isArray(sorter)
    ? sorter.find((item) => item?.order)
    : sorter;
  if (!activeSorter?.order) return null;
  return {
    columnKey: String(activeSorter.columnKey ?? activeSorter.field ?? ""),
    order: activeSorter.order,
  };
}

function getListColumnFilteredValue(state: PropGraphListState, columnKey: string) {
  return state.filters[columnKey] ?? null;
}

function getListColumnSortOrder(state: PropGraphListState, columnKey: string) {
  return state.sorter?.columnKey === columnKey ? state.sorter.order : null;
}

function createPropGraphListServerFilter(state: PropGraphListState) {
  const filter: Record<string, any> = {};

  const nameFilter = getFirstFilterValue(state.filters.name);
  if (nameFilter && isValidRegexPattern(nameFilter)) {
    filter.name = { $regex: nameFilter, $options: "i" };
  }

  const assetFilter = getFirstFilterValue(state.filters.assetId);
  if (assetFilter && isValidRegexPattern(assetFilter)) {
    filter.assetId = { $regex: assetFilter, $options: "i" };
  }

  const typeValues = toNumberFilterValues(state.filters.type);
  if (typeValues.length) filter.type = { $in: typeValues };

  const rarityValues = toNumberFilterValues(state.filters.rarity);
  if (rarityValues.length) filter.rarity = { $in: rarityValues };

  const tagValues = toNumberFilterValues(state.filters.tags);
  if (tagValues.length) filter.tags = { $in: tagValues };

  const updatedByValues = toStringFilterValues(state.filters.updatedBy);
  if (updatedByValues.length) filter.updatedBy = { $in: updatedByValues };

  return filter;
}

function getFirstFilterValue(values: any[] | null | undefined) {
  const value = values?.[0];
  return value === undefined || value === null ? "" : String(value);
}

function toNumberFilterValues(values: any[] | null | undefined) {
  return (values ?? [])
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));
}

function toStringFilterValues(values: any[] | null | undefined) {
  return (values ?? [])
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);
}

function isValidRegexPattern(pattern: string) {
  try {
    new RegExp(pattern);
    return true;
  } catch {
    return false;
  }
}

function buildDictLabelMap(data: Array<{ value: number | string; label?: string }>) {
  return new Map(data.map((item) => [Number(item.value), item.label || String(item.value)]));
}

function formatDictLabel(map: Map<number, string>, value: number | string) {
  const numericValue = Number(value);
  return map.get(numericValue) ?? String(value);
}

function buildDictFilters(data: DictItem[]) {
  return data.map((item) => ({
    text: item.label || String(item.value),
    value: item.value,
  }));
}

function buildModifierFilters(data: PropGraph[]) {
  return Array.from(
    new Set(
      data
        .map((item) => String(item.updatedBy ?? "").trim())
        .filter(Boolean),
    ),
  )
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({
      text: value,
      value,
    }));
}

function formatPropGraphUpdatedTime(value: unknown) {
  const timestamp = Number(value);
  if (!Number.isFinite(timestamp) || timestamp <= 0) return "-";
  const date = new Date(timestamp);
  const pad = (input: number) => String(input).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function renderRegexFilterDropdown(placeholder: string) {
  return ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
    <div style={{ padding: 8, width: 220 }}>
      <Input
        allowClear
        autoFocus
        placeholder={placeholder}
        value={selectedKeys[0]}
        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
        onPressEnter={() => confirm()}
      />
      <Space style={{ marginTop: 8 }}>
        <Button
          type="primary"
          size="small"
          icon={<SearchOutlined />}
          onClick={() => confirm()}
        >
          筛选
        </Button>
        <Button
          size="small"
          onClick={() => {
            clearFilters?.();
            confirm({ closeDropdown: true });
          }}
        >
          重置
        </Button>
      </Space>
    </div>
  );
}

function testRegexFilter(value: unknown, target: unknown) {
  const pattern = String(value ?? "").trim();
  if (!pattern) return true;
  const text = String(target ?? "");
  try {
    return new RegExp(pattern, "i").test(text);
  } catch (_) {
    return text.toLowerCase().includes(pattern.toLowerCase());
  }
}

type DescriptionPropNameCandidate = {
  name: string;
  prop: PropGraph;
};

type DescriptionTextSegment =
  | { type: "text"; text: string; negative?: boolean }
  | { type: "prop"; text: string; prop: PropGraph; negative?: boolean };

function PropDescriptionText({
  text,
  configOptions,
  candidates,
  negativeFragments = [],
}: {
  text: string;
  configOptions: PropGraphConfigOptions;
  candidates: DescriptionPropNameCandidate[];
  negativeFragments?: string[];
}) {
  const segments = useMemo(
    () => splitDescriptionByProps(text, candidates, negativeFragments),
    [text, candidates, negativeFragments],
  );

  if (!segments.length) return null;
  return (
    <>
      {segments.map((segment, index) => {
        if (segment.type === "text") {
          return segment.negative ? (
            <NegativeDescriptionText key={index}>{segment.text}</NegativeDescriptionText>
          ) : (
            <span key={index}>{segment.text}</span>
          );
        }
        return (
          <Popover
            key={`${segment.prop._id}-${index}`}
            trigger={["hover", "click"]}
            placement="top"
            mouseEnterDelay={0.2}
            content={<PropPreviewContent prop={segment.prop} configOptions={configOptions} />}
          >
            <DescriptionPropLink $negative={segment.negative}>{segment.text}</DescriptionPropLink>
          </Popover>
        );
      })}
    </>
  );
}

function PropPreviewContent({
  prop,
  configOptions,
}: {
  prop: PropGraph;
  configOptions: PropGraphConfigOptions;
}) {
  const [resolvedProp, setResolvedProp] = useState<PropGraph>(() => prop);
  const [loading, setLoading] = useState(false);
  const { types, rarities, tags, props, descriptionProps, strengths } = configOptions;
  const typeMap = useMemo(() => buildDictLabelMap(types), [types]);
  const rarityMap = useMemo(() => buildDictLabelMap(rarities), [rarities]);
  const tagMap = useMemo(() => buildDictLabelMap(tags), [tags]);
  const resolvedDescriptionProps = useMemo(
    () => mergePropGraphNameIndex(descriptionProps, [resolvedProp]),
    [descriptionProps, resolvedProp],
  );

  useEffect(() => {
    let disposed = false;
    const id = String(prop._id || "");
    setResolvedProp(prop);
    if (!id || hasPropGraphLogicDocument(prop)) {
      setLoading(false);
      return () => { disposed = true; };
    }

    const cached = cachedPropGraphDetailMap.get(id);
    if (cached) {
      setResolvedProp(cloneGraph(cached));
      setLoading(false);
      return () => { disposed = true; };
    }

    setLoading(true);
    dataCall("editor/DataFind", {
      collection: "propgraphs",
      filter: { _id: id },
      limit: 1,
    }).then((res) => {
      if (disposed || !res.isSucc) return;
      const fullGraph = res.res.data[0] as PropGraph | undefined;
      if (!fullGraph) return;
      const normalized = normalizePropGraph(fullGraph);
      cachedPropGraphDetailMap.set(id, normalized);
      setResolvedProp(cloneGraph(normalized));
    }).finally(() => {
      if (!disposed) setLoading(false);
    });

    return () => { disposed = true; };
  }, [prop]);

  const description = useMemo(
    () => getPropGraphDescription(resolvedProp, { types, rarities, tags, props: resolvedDescriptionProps }),
    [resolvedProp, types, rarities, tags, resolvedDescriptionProps],
  );
  const negativeDescriptionFragments = useMemo(
    () => getPropGraphNegativeDescriptionFragments(resolvedProp, { types, rarities, tags, props: resolvedDescriptionProps }),
    [resolvedProp, types, rarities, tags, resolvedDescriptionProps],
  );
  const propNameCandidates = useMemo(
    () => buildDescriptionPropNameCandidates(resolvedDescriptionProps),
    [resolvedDescriptionProps],
  );
  const strength = useMemo(
    () => getPropGraphStrength(resolvedProp, { props, strengths }),
    [resolvedProp, props, strengths],
  );

  return (
    <PropPreviewPanel>
      <PropPreviewTitle>
        <Text strong ellipsis style={{ minWidth: 0 }}>
          {prop.name || "未命名道具"}
        </Text>
        <Tag color={prop.derived ? "blue" : "default"} style={{ marginInlineEnd: 0 }}>
          {prop.derived ? "衍生物" : "基础"}
        </Tag>
      </PropPreviewTitle>
      <PropPreviewMeta>
        <Text type="secondary">资源ID</Text>
        <span>{prop.assetId || "-"}</span>
        <Text type="secondary">类型</Text>
        <span>{formatDictLabel(typeMap, prop.type)}</span>
        <Text type="secondary">品质</Text>
        <span>{formatDictLabel(rarityMap, prop.rarity)}</span>
        <Text type="secondary">标签</Text>
        <span>
          {(prop.tags ?? []).length
            ? (prop.tags ?? []).map((tag) => formatDictLabel(tagMap, tag)).join("、")
            : "-"}
        </span>
        <Text type="secondary">产出</Text>
        <span>{formatStrengthValue(Number(prop.yield ?? 0))}</span>
        <Text type="secondary">强度</Text>
        <span>
          {formatStrengthValue(strength.value)} / {formatStrengthValue(strength.expected)}
          <StrengthOffsetText $tone={getStrengthOffsetTone(strength.offset)} style={{ marginLeft: 6 }}>
            {formatSignedStrengthValue(strength.offset)}
          </StrengthOffsetText>
        </span>
      </PropPreviewMeta>
      {loading && !description && <Text type="secondary">描述加载中...</Text>}
      {description && (
        <PropPreviewDescription>
          <PropDescriptionText
            text={description}
            configOptions={configOptions}
            candidates={propNameCandidates}
            negativeFragments={negativeDescriptionFragments}
          />
        </PropPreviewDescription>
      )}
    </PropPreviewPanel>
  );
}

function buildDescriptionPropNameCandidates(props: PropGraph[]) {
  const seen = new Set<string>();
  return props
    .map((prop) => ({ name: String(prop.name ?? "").trim(), prop }))
    .filter((item) => item.name.length >= 2 && !seen.has(item.name) && seen.add(item.name))
    .sort((a, b) => b.name.length - a.name.length || a.name.localeCompare(b.name, "zh-Hans-CN"));
}

function splitDescriptionByProps(
  text: string,
  candidates: DescriptionPropNameCandidate[],
  negativeFragments: string[] = [],
): DescriptionTextSegment[] {
  if (!text) return [];
  const negativeMatches = Array.from(new Set(negativeFragments.map((item) => item.trim()).filter(Boolean)))
    .sort((a, b) => b.length - a.length);
  const segments: DescriptionTextSegment[] = [];
  let index = 0;
  let buffer = "";

  const flushBuffer = () => {
    if (!buffer) return;
    segments.push({ type: "text", text: buffer });
    buffer = "";
  };

  while (index < text.length) {
    const negativeMatch = findDescriptionTextAt(text, index, negativeMatches);
    if (negativeMatch) {
      flushBuffer();
      appendDescriptionTextWithProps(segments, negativeMatch, candidates, true);
      index += negativeMatch.length;
      continue;
    }

    const propMatch = findDescriptionPropAt(text, index, candidates);
    if (propMatch) {
      flushBuffer();
      segments.push({ type: "prop", text: propMatch.name, prop: propMatch.prop });
      index += propMatch.name.length;
    } else {
      buffer += text[index];
      index += 1;
    }
  }

  flushBuffer();
  return segments;
}

function appendDescriptionTextWithProps(
  segments: DescriptionTextSegment[],
  text: string,
  candidates: DescriptionPropNameCandidate[],
  negative: boolean,
) {
  let index = 0;
  let buffer = "";
  const flushBuffer = () => {
    if (!buffer) return;
    segments.push({ type: "text", text: buffer, negative });
    buffer = "";
  };

  while (index < text.length) {
    const propMatch = findDescriptionPropAt(text, index, candidates);
    if (propMatch) {
      flushBuffer();
      segments.push({ type: "prop", text: propMatch.name, prop: propMatch.prop, negative });
      index += propMatch.name.length;
    } else {
      buffer += text[index];
      index += 1;
    }
  }

  flushBuffer();
}

function findDescriptionTextAt(text: string, index: number, fragments: string[]) {
  for (const fragment of fragments) {
    if (text.startsWith(fragment, index)) return fragment;
  }
  return "";
}

function findDescriptionPropAt(
  text: string,
  index: number,
  candidates: DescriptionPropNameCandidate[],
) {
  for (const candidate of candidates) {
    if (text.startsWith(candidate.name, index)) return candidate;
  }
  return undefined;
}

function comparePropGraphListDefault(a: PropGraph, b: PropGraph) {
  return (
    compareNumber(a.type, b.type) ||
    compareNumberArray(a.tags ?? [], b.tags ?? []) ||
    compareNumber(a.rarity, b.rarity) ||
    compareBoolean(a.derived, b.derived) ||
    String(a._id ?? "").localeCompare(String(b._id ?? ""))
  );
}

function compareNumber(a: unknown, b: unknown) {
  const left = Number(a);
  const right = Number(b);
  if (Number.isNaN(left) && Number.isNaN(right)) return 0;
  if (Number.isNaN(left)) return 1;
  if (Number.isNaN(right)) return -1;
  return left - right;
}

function compareBoolean(a: unknown, b: unknown) {
  return Number(Boolean(a)) - Number(Boolean(b));
}

function compareNumberArray(a: number[], b: number[]) {
  const left = a.map(Number).sort((x, y) => x - y);
  const right = b.map(Number).sort((x, y) => x - y);
  const length = Math.max(left.length, right.length);
  for (let index = 0; index < length; index += 1) {
    if (left[index] === undefined) return -1;
    if (right[index] === undefined) return 1;
    const diff = compareNumber(left[index], right[index]);
    if (diff !== 0) return diff;
  }
  return 0;
}

function StrengthCell({
  strength,
  simulationStrength,
  graph,
  props,
  strengths,
}: {
  strength: PropGraphStrengthResult;
  simulationStrength?: SimulationStrengthValues;
  graph?: PropGraph;
  props?: PropGraph[];
  strengths?: PropStrength[];
}) {
  const [detailOpen, setDetailOpen] = useState(false);
  const debug = useMemo(
    () => (detailOpen && graph ? getPropGraphStrengthDebug(graph, { props, strengths }) : undefined),
    [detailOpen, graph, props, strengths],
  );
  return (
    <Popover
      open={detailOpen}
      onOpenChange={setDetailOpen}
      placement="left"
      trigger={["hover", "click"]}
      title="强度详情"
      content={(
        <StrengthDetailContent
          strength={strength}
          simulationStrength={simulationStrength}
          debug={debug}
        />
      )}
    >
      <StrengthCellWrap>
        <StrengthCellRow>
          <StrengthMainText>
            {formatStrengthValue(strength.value)} / {formatStrengthValue(strength.expected)}
          </StrengthMainText>
          <StrengthOffsetText $tone={getStrengthOffsetTone(strength.offset)}>
            {formatSignedStrengthValue(strength.offset)}
            {strength.expected ? `（${formatSignedStrengthValue(strength.offsetPercent)}%）` : ""}
          </StrengthOffsetText>
        </StrengthCellRow>
        <StrengthCellRow>
          <StrengthMainText>
            平衡模拟 {formatOptionalStrengthValue(simulationStrength?.final)}
          </StrengthMainText>
          {simulationStrength?.confidenceLow !== undefined && simulationStrength?.confidenceHigh !== undefined ? (
            <StrengthSimulationText>
              {formatStrengthValue(simulationStrength.confidenceLow)}~{formatStrengthValue(simulationStrength.confidenceHigh)}
            </StrengthSimulationText>
          ) : null}
        </StrengthCellRow>
        <StrengthCellRow>
          <StrengthSimulationText>
            自身 {formatOptionalStrengthValue(simulationStrength?.intrinsic)}
          </StrengthSimulationText>
          <StrengthSimulationText>
            承接 {formatOptionalStrengthValue(simulationStrength?.body)}
          </StrengthSimulationText>
          <StrengthSimulationText>
            来源 {formatOptionalStrengthValue(simulationStrength?.source)}
          </StrengthSimulationText>
        </StrengthCellRow>
      </StrengthCellWrap>
    </Popover>
  );
}

function StrengthInline({
  strength,
  simulationStrength,
  graph,
  props,
  strengths,
}: {
  strength: PropGraphStrengthResult;
  simulationStrength?: SimulationStrengthValues;
  graph?: PropGraph;
  props?: PropGraph[];
  strengths?: PropStrength[];
}) {
  const [detailOpen, setDetailOpen] = useState(false);
  const debug = useMemo(
    () => (detailOpen && graph ? getPropGraphStrengthDebug(graph, { props, strengths }) : undefined),
    [detailOpen, graph, props, strengths],
  );
  return (
    <Popover
      open={detailOpen}
      onOpenChange={setDetailOpen}
      placement="bottom"
      trigger={["hover", "click"]}
      title="强度详情"
      content={(
        <StrengthDetailContent
          strength={strength}
          simulationStrength={simulationStrength}
          debug={debug}
        />
      )}
    >
      <GraphStrengthInline>
        <span>强度 {formatStrengthValue(strength.value)}</span>
        <span>/ 目标 {formatStrengthValue(strength.expected)}</span>
        <StrengthOffsetText $tone={getStrengthOffsetTone(strength.offset)}>
          {formatSignedStrengthValue(strength.offset)}
        </StrengthOffsetText>
        {simulationStrength?.final !== undefined ? (
          <span>平衡 {formatStrengthValue(simulationStrength.final)}</span>
        ) : null}
      </GraphStrengthInline>
    </Popover>
  );
}

function StrengthDetailContent({
  strength,
  simulationStrength,
  debug,
}: {
  strength: PropGraphStrengthResult;
  simulationStrength?: SimulationStrengthValues;
  debug?: PropGraphStrengthDebugResult;
}) {
  const simulation = Number.isFinite(simulationStrength?.balancedSimulationStrength)
    ? Number(simulationStrength?.balancedSimulationStrength)
    : Number.isFinite(simulationStrength?.final)
      ? Number(simulationStrength?.final)
      : undefined;
  const snapshot = Number.isFinite(simulationStrength?.actualCalculatedStrength)
    ? Number(simulationStrength?.actualCalculatedStrength)
    : undefined;
  const normalizationTurns = debug?.entries.reduce(
    (sum, entry) => sum + entry.weight * entry.normalizationTurns,
    0,
  );
  const items = [
    {
      key: "summary",
      label: "汇总",
      children: (
        <StrengthDetailSection>
          <StrengthDetailRows
            rows={[
              ["计算强度", formatStrengthValue(strength.value)],
              ["基础产出贡献", formatStrengthValue(strength.baseValue)],
              ["节点效果贡献", formatStrengthValue(strength.graphValue)],
              ["一次性贡献", formatStrengthValue(strength.oneShotValue ?? 0)],
              ["总贡献", formatStrengthValue(strength.totalValue)],
              ["归一轮数", normalizationTurns === undefined ? "打开后计算" : formatStrengthValue(normalizationTurns)],
              ["最终公式", "(基础产出贡献 + 节点效果贡献) / 归一轮数 + 一次性贡献"],
              ["预期强度", formatStrengthValue(strength.expected)],
              ["偏移量", formatSignedStrengthValue(strength.offset)],
              ["偏移比例", strength.expected ? `${formatSignedStrengthValue(strength.offsetPercent)}%` : "0%"],
              ["模拟快照", snapshot === undefined ? "暂无" : formatStrengthValue(snapshot)],
              ["平衡模拟", simulation === undefined ? "暂无" : formatStrengthValue(simulation)],
              ["置信区间", simulationStrength?.confidenceLow === undefined || simulationStrength?.confidenceHigh === undefined
                ? "暂无"
                : `${formatStrengthValue(simulationStrength.confidenceLow)} ~ ${formatStrengthValue(simulationStrength.confidenceHigh)}`],
              ["样本数", simulationStrength?.sampleCount === undefined ? "暂无" : formatStrengthValue(simulationStrength.sampleCount)],
              ["评价窗口", `${strength.turns} 轮`],
            ]}
          />
        </StrengthDetailSection>
      ),
    },
    {
      key: "entries",
      label: `入口权重 ${debug?.entries.length ?? 0}`,
      children: debug?.entries.length ? (
        <StrengthDetailSection>
          {debug.entries.map((entry, index) => (
            <StrengthDetailCard key={`${entry.entryTurn}-${index}`}>
              <StrengthDetailCardTitle>
                第 {entry.entryTurn} 轮入手，权重 {formatPercent(entry.weight)}
              </StrengthDetailCardTitle>
              <StrengthDetailRows
                rows={[
                  ["基础产出贡献", formatStrengthValue(entry.baseValue)],
                  ["节点效果贡献", formatStrengthValue(entry.graphValue)],
                  ["一次性贡献", formatStrengthValue(entry.oneShotValue ?? 0)],
                  ["归一轮数", formatStrengthValue(entry.normalizationTurns)],
                ]}
              />
              {entry.events.length ? (
                <StrengthDetailSection style={{ marginTop: 6 }}>
                  {entry.events.map((event) => (
                    <StrengthDetailRows
                      key={`${event.nodeId}-${event.type}`}
                      rows={[
                        ["事件节点", `${getDebugNodeTypeLabel(event.type)} (${event.nodeId})`],
                        ["触发轮次", formatDebugTurns(event.triggerTurns)],
                        ["事件贡献", formatStrengthValue(event.value)],
                        ...(event.scoringDescription === undefined ? [] : [["计分方式", event.scoringDescription] as [string, string]]),
                        ...(event.interval === undefined ? [] : [["间隔", String(event.interval)] as [string, string]]),
                        ...(event.loop === undefined || event.loop <= 0 ? [] : [["循环", String(event.loop)] as [string, string]]),
                      ]}
                    />
                  ))}
                </StrengthDetailSection>
              ) : null}
            </StrengthDetailCard>
          ))}
        </StrengthDetailSection>
      ) : (
        <StrengthDetailValue>暂无入口明细</StrengthDetailValue>
      ),
    },
    {
      key: "actions",
      label: `动作节点 ${debug?.actions.length ?? 0}`,
      children: debug?.actions.length ? (
        <StrengthDetailSection>
          {debug.actions.map((action) => (
            <StrengthDetailCard key={action.nodeId}>
              <StrengthDetailCardTitle>
                {getDebugNodeTypeLabel(action.type)} ({action.nodeId})
              </StrengthDetailCardTitle>
              <StrengthDetailRows
                rows={[
                  ["调用次数", String(action.calls)],
                  ["总贡献", formatStrengthValue(action.contribution)],
                  ["动作效果", formatStrengthValue(action.effectValue)],
                  ["完成分支", formatStrengthValue(action.completeValue)],
                  ["失败分支", formatStrengthValue(action.failValue)],
                  ["每次分支", formatStrengthValue(action.eachValue)],
                  ["条件概率", formatPercent(action.conditionProbability)],
                  ["目标存在率", formatPercent(action.targetExistenceProbability)],
                  ["成功率", formatPercent(action.successProbability)],
                  ["失败率", formatPercent(action.failProbability)],
                  ["目标数量", formatStrengthValue(action.targetCount)],
                  ["每次次数", formatStrengthValue(action.eachCount)],
                  ["目标强度", formatStrengthValue(action.targetStrength)],
                  ["负面参数", action.negative ? "是" : "否"],
                  ...action.details.map((detail) => [
                    detail.label,
                    typeof detail.value === "number" ? formatStrengthValue(detail.value) : detail.value,
                  ] as [string, string]),
                ]}
              />
            </StrengthDetailCard>
          ))}
        </StrengthDetailSection>
      ) : (
        <StrengthDetailValue>暂无动作明细</StrengthDetailValue>
      ),
    },
  ];

  return (
    <StrengthDetailPanel aria-label={getStrengthTooltip(strength, simulationStrength)}>
      <Collapse size="small" defaultActiveKey={["summary", "actions"]} items={items} />
    </StrengthDetailPanel>
  );
}

function StrengthDetailRows({ rows }: { rows: Array<[string, string]> }) {
  return (
    <StrengthDetailGrid>
      {rows.map(([label, value], index) => (
        <Fragment key={`${label}-${index}`}>
          <StrengthDetailLabel>{label}</StrengthDetailLabel>
          <StrengthDetailValue>{value}</StrengthDetailValue>
        </Fragment>
      ))}
    </StrengthDetailGrid>
  );
}

function formatPercent(value: number) {
  if (!Number.isFinite(value)) return "0%";
  return `${formatStrengthValue(value * 100)}%`;
}

function formatDebugTurns(turns: number[]) {
  if (!turns.length) return "按概率估算";
  if (turns.length <= 10) return turns.join("、");
  return `${turns.slice(0, 8).join("、")} ... ${turns[turns.length - 1]}`;
}

function getDebugNodeTypeLabel(type: string) {
  const labels: Record<string, string> = {
    eventTurnStart: "回合开始",
    eventAcquire: "持有触发",
    eventUse: "主动使用",
    eventEliminated: "被消除",
    actionGrow: "成长",
    actionExtraYield: "额外产出",
    actionAccelerate: "加速",
    actionTransform: "转化",
    actionSpawn: "生成",
    actionEliminate: "消除",
    actionProtect: "保护",
    actionPurify: "净化",
    actionCollect: "收集",
    actionHarvest: "收获",
    actionRarityWeight: "品质权重",
    actionExtendCommission: "延长调和",
    actionSwapPosition: "交换位置",
    actionPermanentBoost: "永久加成",
  };
  return labels[type] ?? type;
}

function getStrengthTooltip(
  strength: PropGraphStrengthResult,
  simulationStrength?: SimulationStrengthValues,
) {
  const simulation = Number.isFinite(simulationStrength?.balancedSimulationStrength)
    ? Number(simulationStrength?.balancedSimulationStrength)
    : Number.isFinite(simulationStrength?.final)
      ? Number(simulationStrength?.final)
      : undefined;
  const snapshot = Number.isFinite(simulationStrength?.actualCalculatedStrength)
    ? Number(simulationStrength?.actualCalculatedStrength)
    : undefined;
  return [
    `计算强度：${formatStrengthValue(strength.value)}`,
    snapshot === undefined ? "模拟时计算快照：暂无最新模拟数据" : `模拟时计算快照：${formatStrengthValue(snapshot)}`,
    simulation === undefined
      ? "平衡模拟强度：暂无最新模拟数据"
      : `平衡模拟强度：${formatStrengthValue(simulation)}`,
    `预期强度：${formatStrengthValue(strength.expected)}`,
    `偏移量：${formatSignedStrengthValue(strength.offset)}`,
    strength.expected ? `偏移比例：${formatSignedStrengthValue(strength.offsetPercent)}%` : "",
    simulationStrength?.balance === undefined ? "" : `观测边际：${formatStrengthValue(simulationStrength.balance)}`,
    simulationStrength?.pool === undefined ? "" : `池/协同修正：${formatSignedStrengthValue(simulationStrength.pool)}`,
    simulationStrength?.confidenceLow === undefined || simulationStrength?.confidenceHigh === undefined ? "" : `置信区间：${formatStrengthValue(simulationStrength.confidenceLow)} ~ ${formatStrengthValue(simulationStrength.confidenceHigh)}`,
    simulationStrength?.sampleCount === undefined ? "" : `样本数：${formatStrengthValue(simulationStrength.sampleCount)}`,
    simulationStrength?.intrinsic === undefined ? "自身模拟强度：暂无最新模拟数据" : `自身模拟强度：${formatStrengthValue(simulationStrength.intrinsic)}`,
    simulationStrength?.body === undefined ? "承接模拟强度：暂无最新模拟数据" : `承接模拟强度：${formatStrengthValue(simulationStrength.body)}`,
    simulationStrength?.source === undefined ? "来源模拟强度：暂无最新模拟数据" : `来源模拟强度：${formatStrengthValue(simulationStrength.source)}`,
    `评价窗口：${strength.turns} 轮`,
  ]
    .filter(Boolean)
    .join("；");
}

function formatStrengthValue(value: number) {
  if (!Number.isFinite(value)) return "0";
  return String(Number(value.toFixed(2)));
}

function formatOptionalStrengthValue(value?: number) {
  if (value === undefined || !Number.isFinite(value)) return "暂无";
  return formatStrengthValue(value);
}

function formatSignedStrengthValue(value: number) {
  const text = formatStrengthValue(Math.abs(value));
  if (value > 0) return `+${text}`;
  if (value < 0) return `-${text}`;
  return "0";
}

function getStrengthOffsetTone(value: number): "positive" | "negative" | "neutral" {
  if (value > 0) return "positive";
  if (value < 0) return "negative";
  return "neutral";
}

function CreatePropGraphModal({
  open,
  configOptions,
  onCancel,
  onSubmit,
}: {
  open: boolean;
  configOptions: PropGraphConfigOptions;
  onCancel: () => void;
  onSubmit: (value: Partial<PropGraph>) => Promise<void>;
}) {
  const [form] = Form.useForm<Partial<PropGraph>>();
  const { types, rarities, tags } = configOptions;

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue({
      type: 1,
      rarity: 1,
      tags: [],
      yield: 0,
      assetId: "Empty_001",
    });
  }, [form, open]);

  return (
    <Modal
      open={open}
      centered
      title="新增炼金道具"
      okText="创建"
      cancelText="取消"
      onCancel={onCancel}
      onOk={() => form.submit()}
      destroyOnHidden
    >
      <Form form={form} labelCol={{ span: 6 }} onFinish={onSubmit}>
        <Form.Item
          name="name"
          label="名称"
          rules={[{ required: true, message: "请输入名称" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="assetId"
          label="资源ID"
          rules={[{ required: true, message: "请输入资源ID" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="type" label="类型">
          <Select options={types} />
        </Form.Item>
        <Form.Item name="rarity" label="品质">
          <Select options={rarities} />
        </Form.Item>
        <Form.Item name="tags" label="标签">
          <Select mode="multiple" options={tags} />
        </Form.Item>
        <Form.Item name="yield" label="产出">
          <InputNumber min={-999} max={999} style={{ width: "100%" }} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function PropGraphCanvasEditor({
  value,
  active,
  configOptions,
  simulationStrength,
  canvasClipboard,
  canvasClipboardVersion,
  onSetCanvasClipboard,
  onNextCanvasPasteCount,
  onChange,
  onSave,
  onClose,
}: {
  value: PropGraph;
  active: boolean;
  configOptions: PropGraphConfigOptions;
  simulationStrength?: SimulationStrengthValues;
  canvasClipboard: CanvasClipboard | null;
  canvasClipboardVersion: number;
  onSetCanvasClipboard: (payload: CanvasClipboard) => void;
  onNextCanvasPasteCount: () => number;
  onChange: (value: PropGraph) => void;
  onSave: (value: PropGraph) => Promise<void>;
  onClose: () => void;
}) {
  const { message } = useFeedback()!;
  const { types, rarities, tags, props, descriptionProps, strengths } = configOptions;
  const canvasRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ nodeId: string; dx: number; dy: number } | null>(null);
  const dragPreviewRef = useRef<{ nodeId: string; position: PropGraphPosition } | null>(null);
  const pendingDragPreviewRef = useRef<{ nodeId: string; position: PropGraphPosition } | null>(null);
  const dragFrameRef = useRef<number | null>(null);
  const viewportRef = useRef({ x: 0, y: 0, scale: 1 });
  const pendingViewportRef = useRef<{ x: number; y: number; scale: number } | null>(null);
  const viewportFrameRef = useRef<number | null>(null);
  const panRef = useRef<{
    pointerId: number;
    startClientX: number;
    startClientY: number;
    startX: number;
    startY: number;
  } | null>(null);
  const marqueeRef = useRef<SelectionMarquee | null>(null);
  const connectionDragRef = useRef<ConnectionDraft | null>(null);
  const deleteSelectionRef = useRef<() => boolean>(() => false);
  const keyboardHandlerRef = useRef<(event: KeyboardEvent) => void>(() => {});
  const lastCanvasPointerRef = useRef<{
    screen: { x: number; y: number };
    world: PropGraphPosition;
  } | null>(null);
  const [selection, setSelection] = useState<Selection>(() =>
    getGraphNodeList(value.graph)[0]
      ? { type: "node", id: getGraphNodeList(value.graph)[0].id }
      : null,
  );
  const [viewport, setViewport] = useState({ x: 0, y: 0, scale: 1 });
  const [isPanning, setIsPanning] = useState(false);
  const [connectionDraft, setConnectionDraft] = useState<ConnectionDraft | null>(null);
  const [connectionHover, setConnectionHover] = useState<PortReference | null>(null);
  const [selectionMarquee, setSelectionMarquee] = useState<SelectionMarquee | null>(null);
  const [dragPreview, setDragPreview] = useState<{
    nodeId: string;
    position: PropGraphPosition;
  } | null>(null);
  const [nodeCreateMenu, setNodeCreateMenu] = useState<{
    screen: { x: number; y: number };
    world: PropGraphPosition;
  } | null>(null);
  const [keyboardPalette, setKeyboardPalette] = useState<KeyboardPaletteState | null>(null);

  const nodes = useMemo(() => getGraphNodeList(value.graph), [value.graph]);
  const nodeMap = useMemo(
    () => new Map(nodes.map((node) => [node.id, node])),
    [nodes],
  );
  const edges = useMemo(
    () => getGraphEdgeList(value.graph),
    [value.graph],
  );
  const editorNodes = value.editor.nodes;
  const editorNodeMap = useMemo(
    () => new Map(editorNodes.map((item) => [item.nodeId, item])),
    [editorNodes],
  );
  const nodeEditorStateMap = useMemo(() => {
    const map = new Map<string, PropGraphNodeEditorState>();
    nodes.forEach((node, index) => {
      const current =
        editorNodeMap.get(node.id) ??
        createDefaultNodeEditorState(node, index);
      map.set(
        node.id,
        dragPreview?.nodeId === node.id
          ? { ...current, position: dragPreview.position }
          : current,
      );
    });
    return map;
  }, [nodes, editorNodeMap, dragPreview]);
  const graphDescription = useMemo(
    () => getPropGraphDescription(value, { types, rarities, tags, props: descriptionProps }),
    [value, types, rarities, tags, descriptionProps],
  );
  const negativeDescriptionFragments = useMemo(
    () => getPropGraphNegativeDescriptionFragments(value, { types, rarities, tags, props: descriptionProps }),
    [value, types, rarities, tags, descriptionProps],
  );
  const propNameCandidates = useMemo(
    () => buildDescriptionPropNameCandidates(descriptionProps),
    [descriptionProps],
  );
  const graphStrength = useMemo(
    () => getPropGraphStrength(value, { props, strengths }),
    [value, props, strengths],
  );
  const validationIssues = useMemo(
    () => getPropGraphValidationIssues(value),
    [value],
  );
  const nodeValidationMap = useMemo(() => {
    const map = new Map<string, PropGraphValidationIssue[]>();
    validationIssues.forEach((issue) => {
      if (!issue.nodeId) return;
      map.set(issue.nodeId, (map.get(issue.nodeId) ?? []).concat(issue));
    });
    return map;
  }, [validationIssues]);

  const nodeOptions = useMemo(
    () =>
      nodes.map((node) => ({
        value: node.id,
        label: getDisplayNodeTitle(node, configOptions),
      })),
    [nodes, configOptions],
  );
  const keyboardNodeOptions = useMemo(() => getKeyboardNodeOptions(), []);
  const keyboardPaletteOptions = useMemo(
    () => filterKeyboardNodeOptions(keyboardNodeOptions, keyboardPalette),
    [keyboardNodeOptions, keyboardPalette],
  );

  useEffect(() => {
    if (!keyboardPalette) return;
    const maxIndex = Math.max(0, keyboardPaletteOptions.length - 1);
    if (keyboardPalette.activeIndex <= maxIndex) return;
    setKeyboardPalette({ ...keyboardPalette, activeIndex: maxIndex });
  }, [keyboardPalette, keyboardPaletteOptions.length]);
  const selectedIds = useMemo(() => getSelectionIds(selection), [selection]);
  const selectedNode =
    selectedIds.nodeIds.size === 1 && selectedIds.edgeIds.size === 0
      ? nodeMap.get(Array.from(selectedIds.nodeIds)[0])
      : undefined;
  const selectedEdge =
    selectedIds.edgeIds.size === 1 && selectedIds.nodeIds.size === 0
      ? edges.find((edge) => edge.id === Array.from(selectedIds.edgeIds)[0])
      : undefined;
  const selectedCount = selectedIds.nodeIds.size + selectedIds.edgeIds.size;
  const getPortConnectionState = (
    node: PropNode,
    port: PropGraphPort,
    direction: PortDirection,
  ): PortConnectionState => {
    if (!connectionDraft) return "idle";
    const reference = { nodeId: node.id, portId: port.id, direction };
    const hovered = isSamePortReference(connectionHover, reference);
    if (isSamePortReference(connectionDraft.from, reference)) return "source";
    if (canConnectPortReferences(nodes, connectionDraft.from, reference)) {
      return hovered ? "hoverConnectable" : "connectable";
    }
    return hovered ? "hoverInvalid" : "unavailable";
  };

  const updateDragPreview = (
    preview: { nodeId: string; position: PropGraphPosition } | null,
  ) => {
    dragPreviewRef.current = preview;
    setDragPreview(preview);
  };

  const cancelDragFrame = () => {
    if (dragFrameRef.current === null) return;
    window.cancelAnimationFrame(dragFrameRef.current);
    dragFrameRef.current = null;
  };

  const scheduleDragPreview = (
    nodeId: string,
    position: PropGraphPosition,
  ) => {
    pendingDragPreviewRef.current = { nodeId, position };
    if (dragFrameRef.current !== null) return;

    dragFrameRef.current = window.requestAnimationFrame(() => {
      dragFrameRef.current = null;
      const pending = pendingDragPreviewRef.current;
      pendingDragPreviewRef.current = null;
      if (!pending || dragRef.current?.nodeId !== pending.nodeId) return;
      updateDragPreview(pending);
    });
  };

  const takeDragPreview = () => {
    cancelDragFrame();
    const pending = pendingDragPreviewRef.current;
    pendingDragPreviewRef.current = null;
    return pending ?? dragPreviewRef.current;
  };

  const scheduleViewport = (nextViewport: { x: number; y: number; scale: number }) => {
    viewportRef.current = nextViewport;
    pendingViewportRef.current = nextViewport;
    if (viewportFrameRef.current !== null) return;

    viewportFrameRef.current = window.requestAnimationFrame(() => {
      viewportFrameRef.current = null;
      const pending = pendingViewportRef.current;
      pendingViewportRef.current = null;
      if (pending) setViewport(pending);
    });
  };

  const patchGraph = (patch: Partial<PropGraph>) => {
    onChange({ ...value, ...patch });
  };

  const patchDocument = (patch: Partial<PropGraph["graph"]>) => {
    patchGraph({
      graph: {
        ...value.graph,
        ...patch,
      },
    });
  };

  const patchEditor = (patch: Partial<PropGraphEditorState>) => {
    patchGraph({
      editor: {
        ...value.editor,
        ...patch,
      },
    });
  };

  const getNodeEditorState = (node: PropNode) => {
    return (
      nodeEditorStateMap.get(node.id) ??
      createDefaultNodeEditorState(node, nodes.findIndex((item) => item.id === node.id))
    );
  };

  const patchNodeEditorState = (
    nodeId: string,
    patch: Partial<PropGraphNodeEditorState>,
  ) => {
    const node = nodeMap.get(nodeId);
    if (!node) return;
    const exists = editorNodeMap.has(nodeId);
    const next = exists
      ? editorNodes.map((item) =>
          item.nodeId === nodeId ? { ...item, ...patch, nodeId } : item,
        )
      : editorNodes.concat({
          ...createDefaultNodeEditorState(node, nodes.indexOf(node)),
          ...patch,
          nodeId,
        });
    patchEditor({ nodes: next });
  };

  const patchNode = (nodeId: string, patch: Partial<PropNode>) => {
    const nextNodes = nodes.map((node) =>
      node.id === nodeId ? ({ ...node, ...patch } as PropNode) : node,
    );
    const nextEdges = getValidCanvasEdges(edges, nextNodes);
    patchDocument({
      nodes: createPropNodeMap(nextNodes),
      ...buildGraphConnections(nextEdges),
    });
  };

  const patchEdge = (edgeId: string, patch: Partial<PropEdge>) => {
    const nextEdges = edges.map((edge) =>
      edge.id === edgeId ? normalizeCanvasEdge({ ...edge, ...patch }, nodes) : edge,
    );
    patchDocument(buildGraphConnections(getValidCanvasEdges(nextEdges, nodes)));
  };

  const addNode = (
    type: PropNodeType,
    position?: PropGraphPosition,
    options: { autoConnect?: boolean } = {},
  ) => {
    const node = createDefaultPropNode(type);
    const editorState = createDefaultNodeEditorState(node, nodes.length);
    const rect = canvasRef.current?.getBoundingClientRect();
    if (position) {
      editorState.position = {
        x: Math.round(position.x - NODE_WIDTH / 2),
        y: Math.round(position.y - NODE_HEIGHT / 2),
      };
    } else if (rect) {
      const offset = (nodes.length % 6) * 28;
      editorState.position = {
        x: Math.round((rect.width / 2 - viewport.x) / viewport.scale - NODE_WIDTH / 2 + offset),
        y: Math.round((rect.height / 2 - viewport.y) / viewport.scale - NODE_HEIGHT / 2 + offset),
      };
    }
    const nextNodes = nodes.concat(node);
    const autoConnection = options.autoConnect && selectedNode
      ? createAutoConnectionEdge(selectedNode, node, nextNodes, edges)
      : null;
    const nextEdges = autoConnection
      ? getValidCanvasEdges(
          (
            autoConnection.inputPort.multiple
              ? edges
              : edges.filter(
                  (edge) =>
                    edge.toNodeId !== autoConnection.edge.toNodeId ||
                    edge.toPortId !== autoConnection.edge.toPortId,
                )
          ).concat(autoConnection.edge),
          nextNodes,
        )
      : edges;

    patchGraph({
      graph: {
        ...value.graph,
        ...buildGraphConnections(nextEdges),
        nodes: createPropNodeMap(nextNodes),
        entryNodeIds:
          isEventNodeType(node.type)
            ? value.graph.entryNodeIds.concat(node.id)
            : value.graph.entryNodeIds,
      },
      editor: {
        ...value.editor,
        nodes: editorNodes.concat(editorState),
      },
    });
    setSelection({ type: "node", id: node.id });
    setNodeCreateMenu(null);
  };

  const deleteNode = (nodeId: string) => {
    const nextNodes = nodes.filter((node) => node.id !== nodeId);
    const nextConnections = buildGraphConnections(
      edges.filter((edge) => edge.fromNodeId !== nodeId && edge.toNodeId !== nodeId),
    );
    patchGraph({
      graph: {
        ...value.graph,
        ...nextConnections,
        nodes: createPropNodeMap(nextNodes),
        entryNodeIds: value.graph.entryNodeIds.filter((id) => id !== nodeId),
      },
      editor: {
        ...value.editor,
        nodes: editorNodes.filter((item) => item.nodeId !== nodeId),
      },
    });
    setSelection(nextNodes[0] ? { type: "node", id: nextNodes[0].id } : null);
  };

  const connectPorts = (output: PortReference, input: PortReference) => {
    const outputInfo = findPortInfo(nodes, output);
    const inputInfo = findPortInfo(nodes, input);
    if (!outputInfo || !inputInfo) return false;
    if (outputInfo.node.id === inputInfo.node.id) return false;
    if (!isCompatibleConnection(outputInfo.port, inputInfo.port)) return false;

    const existing = edges.find(
      (edge) =>
        edge.fromNodeId === output.nodeId &&
        edge.fromPortId === output.portId &&
        edge.toNodeId === input.nodeId &&
        edge.toPortId === input.portId,
    );
    if (existing) {
      setSelection({ type: "edge", id: existing.id });
      return true;
    }

    const edge: PropEdge = {
      id: createPropGraphId("edge"),
      kind: outputInfo.port.kind,
      fromNodeId: output.nodeId,
      fromPortId: output.portId,
      toNodeId: input.nodeId,
      toPortId: input.portId,
    };
    const nextEdges = inputInfo.port.multiple
      ? edges
      : edges.filter(
          (item) =>
            item.toNodeId !== input.nodeId ||
            item.toPortId !== input.portId,
        );
    patchDocument(buildGraphConnections(nextEdges.concat(edge)));
    setSelection({ type: "edge", id: edge.id });
    return true;
  };

  const deleteEdge = (edgeId: string) => {
    patchDocument(buildGraphConnections(edges.filter((edge) => edge.id !== edgeId)));
    setSelection(null);
  };

  const deleteSelectedElements = () => {
    if (selectedIds.nodeIds.size === 0 && selectedIds.edgeIds.size === 0) return false;
    const nextNodes = nodes.filter((node) => !selectedIds.nodeIds.has(node.id));
    const nextEdges = edges.filter(
      (edge) =>
        !selectedIds.edgeIds.has(edge.id) &&
        !selectedIds.nodeIds.has(edge.fromNodeId) &&
        !selectedIds.nodeIds.has(edge.toNodeId),
    );
    patchGraph({
      graph: {
        ...value.graph,
        ...buildGraphConnections(nextEdges),
        nodes: createPropNodeMap(nextNodes),
        entryNodeIds: value.graph.entryNodeIds.filter((id) => !selectedIds.nodeIds.has(id)),
      },
      editor: {
        ...value.editor,
        nodes: editorNodes.filter((item) => !selectedIds.nodeIds.has(item.nodeId)),
      },
    });
    setSelection(null);
    return true;
  };

  const copyCanvasSelection = () => {
    const payload = createCanvasClipboard(selection, nodes, edges, editorNodes, value.graph.entryNodeIds);
    if (!payload) {
      message.warning("请先选择要复制的节点或连线");
      return;
    }
    onSetCanvasClipboard(payload);
    message.success("已复制到画布剪贴板");
  };

  const pasteCanvasClipboardAt = (center?: WorldPoint) => {
    const clipboard = canvasClipboard;
    if (!clipboard) {
      message.warning("画布剪贴板为空");
      return;
    }
    const pasted = pasteCanvasClipboardPayload(clipboard, center ?? getVisibleCenterWorld(), onNextCanvasPasteCount());
    const nextNodes = nodes.concat(pasted.nodes);
    const nextEdges = getValidCanvasEdges(edges.concat(pasted.edges), nextNodes);
    patchGraph({
      graph: {
        ...value.graph,
        ...buildGraphConnections(nextEdges),
        nodes: createPropNodeMap(nextNodes),
        entryNodeIds: value.graph.entryNodeIds.concat(pasted.entryNodeIds),
      },
      editor: {
        ...value.editor,
        nodes: editorNodes.concat(pasted.editorNodes),
      },
    });
    setSelection(toSelection(pasted.nodes.map((node) => node.id), pasted.edges.map((edge) => edge.id)));
  };

  const pasteCanvasClipboard = () => {
    pasteCanvasClipboardAt(getVisibleCenterWorld());
  };

  const duplicateCanvasSelection = () => {
    const payload = createCanvasClipboard(selection, nodes, edges, editorNodes, value.graph.entryNodeIds);
    if (!payload) {
      message.warning("请先选择要复制的节点或连线");
      return false;
    }
    onSetCanvasClipboard(payload);
    const center = lastCanvasPointerRef.current?.world ?? getVisibleCenterWorld();
    const pasted = pasteCanvasClipboardPayload(payload, center, onNextCanvasPasteCount());
    const nextNodes = nodes.concat(pasted.nodes);
    const nextEdges = getValidCanvasEdges(edges.concat(pasted.edges), nextNodes);
    patchGraph({
      graph: {
        ...value.graph,
        ...buildGraphConnections(nextEdges),
        nodes: createPropNodeMap(nextNodes),
        entryNodeIds: value.graph.entryNodeIds.concat(pasted.entryNodeIds),
      },
      editor: {
        ...value.editor,
        nodes: editorNodes.concat(pasted.editorNodes),
      },
    });
    setSelection(toSelection(pasted.nodes.map((node) => node.id), pasted.edges.map((edge) => edge.id)));
    return true;
  };

  const selectAllCanvasElements = () => {
    setSelection(toSelection(nodes.map((node) => node.id), edges.map((edge) => edge.id)));
  };

  const focusCanvasSelection = () => {
    const targetNodeIds = selectedIds.nodeIds.size
      ? Array.from(selectedIds.nodeIds)
      : nodes.map((node) => node.id);
    const targetEditorNodes = targetNodeIds
      .map((nodeId) => nodeEditorStateMap.get(nodeId))
      .filter((item): item is PropGraphNodeEditorState => Boolean(item));
    if (!targetEditorNodes.length) {
      resetViewport();
      return;
    }
    const bounds = getEditorNodeBounds(targetEditorNodes);
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const padding = 96;
    const nextScale = clamp(
      Math.min(
        rect.width / Math.max(1, bounds.width + padding * 2),
        rect.height / Math.max(1, bounds.height + padding * 2),
        1.25,
      ),
      MIN_CANVAS_SCALE,
      MAX_CANVAS_SCALE,
    );
    const nextViewport = {
      scale: nextScale,
      x: rect.width / 2 - (bounds.x + bounds.width / 2) * nextScale,
      y: rect.height / 2 - (bounds.y + bounds.height / 2) * nextScale,
    };
    viewportRef.current = nextViewport;
    setViewport(nextViewport);
  };

  deleteSelectionRef.current = () => {
    return deleteSelectedElements();
  };

  useEffect(() => {
    viewportRef.current = viewport;
  }, [viewport]);

  useEffect(() => {
    if (!active) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      keyboardHandlerRef.current(event);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [active]);

  useEffect(() => {
    const handleContextMenu = (event: MouseEvent) => {
      event.preventDefault();
    };
    document.addEventListener("contextmenu", handleContextMenu);
    return () => document.removeEventListener("contextmenu", handleContextMenu);
  }, []);

  useEffect(
    () => () => {
      if (dragFrameRef.current !== null) {
        window.cancelAnimationFrame(dragFrameRef.current);
        dragFrameRef.current = null;
      }
      if (viewportFrameRef.current !== null) {
        window.cancelAnimationFrame(viewportFrameRef.current);
        viewportFrameRef.current = null;
      }
    },
    [],
  );

  const getCanvasPoint = (event: CanvasPoint) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const getWorldPoint = (event: CanvasPoint) => {
    const point = getCanvasPoint(event);
    if (!point) return null;
    const currentViewport = viewportRef.current;
    return {
      x: (point.x - currentViewport.x) / currentViewport.scale,
      y: (point.y - currentViewport.y) / currentViewport.scale,
    };
  };

  const getVisibleCenterWorld = (): WorldPoint => {
    const rect = canvasRef.current?.getBoundingClientRect();
    const currentViewport = viewportRef.current;
    if (!rect) return { x: 0, y: 0 };
    return {
      x: (rect.width / 2 - currentViewport.x) / currentViewport.scale,
      y: (rect.height / 2 - currentViewport.y) / currentViewport.scale,
    };
  };

  const getVisibleCenterScreen = () => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
  };

  const getKeyboardCreateWorld = (): PropGraphPosition => {
    if (selectedNode) {
      const editorState = getNodeEditorState(selectedNode);
      return {
        x: editorState.position.x + NODE_WIDTH + 72 + NODE_WIDTH / 2,
        y: editorState.position.y + estimateNodeHeight(selectedNode) / 2,
      };
    }
    return lastCanvasPointerRef.current?.world ?? getVisibleCenterWorld();
  };

  const getKeyboardPaletteScreen = () => {
    const screen = lastCanvasPointerRef.current?.screen ?? getVisibleCenterScreen();
    return clampKeyboardPalettePosition(screen.x, screen.y);
  };

  const openKeyboardPalette = (mode: KeyboardPaletteMode, categoryKey?: KeyboardCategoryKey) => {
    setNodeCreateMenu(null);
    setKeyboardPalette({
      mode,
      categoryKey,
      query: "",
      activeIndex: 0,
      screen: getKeyboardPaletteScreen(),
      world: getKeyboardCreateWorld(),
    });
  };

  const closeKeyboardPalette = () => {
    setKeyboardPalette(null);
  };

  const createNodeFromKeyboardPalette = (type: PropNodeType) => {
    const world = keyboardPalette?.world ?? getKeyboardCreateWorld();
    addNode(type, world, { autoConnect: true });
    setKeyboardPalette(null);
  };

  const updateKeyboardPaletteQuery = (query: string) => {
    setKeyboardPalette((current) =>
      current ? { ...current, query, activeIndex: 0 } : current,
    );
  };

  const updateKeyboardPaletteActiveIndex = (index: number) => {
    setKeyboardPalette((current) =>
      current ? { ...current, activeIndex: clamp(index, 0, Math.max(0, keyboardPaletteOptions.length - 1)) } : current,
    );
  };

  const getPortReferenceFromPoint = (event: CanvasPoint): PortReference | null => {
    const element = document.elementFromPoint(event.clientX, event.clientY);
    const portElement = element?.closest<HTMLElement>("[data-prop-graph-port='true']");
    if (!portElement) return null;
    const { nodeId, portId, direction } = portElement.dataset;
    if (!nodeId || !portId) return null;
    if (direction !== "input" && direction !== "output") return null;
    return {
      nodeId,
      portId,
      direction,
    };
  };

  const handlePointerDown = (
    event: ReactPointerEvent<HTMLDivElement>,
    node: PropNode,
  ) => {
    const point = getWorldPoint(event);
    if (!point) return;
    const editorState = getNodeEditorState(node);
    event.preventDefault();
    event.stopPropagation();
    cancelDragFrame();
    pendingDragPreviewRef.current = null;
    updateDragPreview(null);
    dragRef.current = {
      nodeId: node.id,
      dx: point.x - editorState.position.x,
      dy: point.y - editorState.position.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    setSelection({ type: "node", id: node.id });
  };

  const handlePortPointerDown = (
    event: ReactPointerEvent<HTMLButtonElement>,
    node: PropNode,
    port: PropGraphPort,
    direction: PortDirection,
  ) => {
    if (event.button !== 0) return;
    const point = getWorldPoint(event);
    if (!point) return;
    event.preventDefault();
    event.stopPropagation();
    const draft: ConnectionDraft = {
      from: {
        nodeId: node.id,
        portId: port.id,
        direction,
      },
      pointer: point,
    };
    connectionDragRef.current = draft;
    setConnectionDraft(draft);
    setConnectionHover(null);
    setSelection({ type: "node", id: node.id });
    try {
      canvasRef.current?.setPointerCapture(event.pointerId);
    } catch {
      // The browser may reject capture if the pointer is already released.
    }
  };

  const handleCanvasPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    setNodeCreateMenu(null);
    const world = getWorldPoint(event);
    if (event.ctrlKey && world) {
      const marquee = {
        pointerId: event.pointerId,
        start: world,
        current: world,
      };
      marqueeRef.current = marquee;
      setSelectionMarquee(marquee);
      setSelection(null);
      event.currentTarget.setPointerCapture(event.pointerId);
      return;
    }

    setSelection(null);
    panRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startX: viewportRef.current.x,
      startY: viewportRef.current.y,
    };
    setIsPanning(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleCanvasContextMenu = (event: ReactMouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    const target = event.target as HTMLElement | null;
    if (target?.closest("[data-prop-graph-node='true'], svg")) return;
    const world = getWorldPoint(event);
    if (!world) return;
    setSelection(null);
    setNodeCreateMenu({
      screen: clampNodeCreateMenuPosition(event.clientX, event.clientY),
      world,
    });
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const currentWorld = getWorldPoint(event);
    if (currentWorld) {
      lastCanvasPointerRef.current = {
        screen: { x: event.clientX, y: event.clientY },
        world: currentWorld,
      };
    }

    if (marqueeRef.current) {
      const point = currentWorld;
      if (!point) return;
      const marquee = {
        ...marqueeRef.current,
        current: point,
      };
      marqueeRef.current = marquee;
      setSelectionMarquee(marquee);
      return;
    }

    if (connectionDragRef.current) {
      const point = currentWorld;
      if (!point) return;
      const draft = {
        ...connectionDragRef.current,
        pointer: point,
      };
      connectionDragRef.current = draft;
      setConnectionDraft(draft);
      setConnectionHover(getPortReferenceFromPoint(event));
      return;
    }

    if (dragRef.current) {
      const point = currentWorld;
      if (!point) return;
      scheduleDragPreview(dragRef.current.nodeId, {
        x: Math.round(point.x - dragRef.current.dx),
        y: Math.round(point.y - dragRef.current.dy),
      });
      return;
    }

    if (!panRef.current) return;
    const pan = panRef.current;
    scheduleViewport({
      ...viewportRef.current,
      x: pan.startX + event.clientX - pan.startClientX,
      y: pan.startY + event.clientY - pan.startClientY,
    });
  };

  const finishPointerInteraction = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (marqueeRef.current) {
      const marquee = marqueeRef.current;
      const rect = normalizeWorldRect(marquee.start, marquee.current);
      const nextSelection = selectCanvasElementsInRect(rect, nodes, edges, nodeEditorStateMap);
      setSelection(nextSelection);
      marqueeRef.current = null;
      setSelectionMarquee(null);
    }

    if (connectionDragRef.current) {
      const start = connectionDragRef.current.from;
      const target = connectionHover ?? getPortReferenceFromPoint(event);
      if (target) {
        const output = start.direction === "output" ? start : target;
        const input = start.direction === "input" ? start : target;
        if (output.direction === "output" && input.direction === "input") {
          connectPorts(output, input);
        }
      }
      connectionDragRef.current = null;
      setConnectionDraft(null);
      setConnectionHover(null);
    }

    if (dragRef.current) {
      const preview = takeDragPreview();
      if (preview && preview.nodeId === dragRef.current.nodeId) {
        patchNodeEditorState(preview.nodeId, { position: preview.position });
      }
      updateDragPreview(null);
    }
    dragRef.current = null;
    if (panRef.current?.pointerId === event.pointerId) {
      panRef.current = null;
      setIsPanning(false);
    }
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleWheel = (event: ReactWheelEvent<HTMLDivElement>) => {
    event.preventDefault();
    const point = getCanvasPoint(event);
    if (!point) return;
    const currentViewport = viewportRef.current;
    const nextScale = clamp(
      currentViewport.scale * Math.exp(-event.deltaY * 0.001),
      MIN_CANVAS_SCALE,
      MAX_CANVAS_SCALE,
    );
    const worldX = (point.x - currentViewport.x) / currentViewport.scale;
    const worldY = (point.y - currentViewport.y) / currentViewport.scale;
    const nextViewport = {
      scale: nextScale,
      x: point.x - worldX * nextScale,
      y: point.y - worldY * nextScale,
    };
    viewportRef.current = nextViewport;
    setViewport(nextViewport);
  };

  const resetViewport = () => {
    const nextViewport = { x: 0, y: 0, scale: 1 };
    viewportRef.current = nextViewport;
    setViewport(nextViewport);
  };

  const autoArrangeCanvas = () => {
    const nextEditorNodes = arrangeEditorNodes(nodes, edges, editorNodes, value.graph.entryNodeIds);
    patchEditor({ nodes: nextEditorNodes });
    const nextViewport = { x: 0, y: 0, scale: 1 };
    viewportRef.current = nextViewport;
    setViewport(nextViewport);
  };

  const handleSave = async () => {
    const firstIssue = validationIssues[0];
    if (firstIssue) {
      if (firstIssue.nodeId) setSelection({ type: "node", id: firstIssue.nodeId });
      message.warning(firstIssue.message);
    }

    try {
      await onSave(value);
    } catch (e: any) {
      message.error(e?.message ?? "保存失败");
    }
  };

  const copyGraphDescription = async () => {
    const copied = await copyTextToClipboard(graphDescription);
    if (copied) {
      message.success("已复制描述");
    } else {
      message.error("复制失败");
    }
  };

  const copyGraphJson = async () => {
    const text = JSON.stringify(sanitizePropGraph(value), null, 2);
    const copied = await copyTextToClipboard(text);
    if (copied) {
      message.success("已复制道具 JSON");
    } else {
      message.error("复制失败");
    }
  };

  keyboardHandlerRef.current = (event: KeyboardEvent) => {
    if (!active) return;

    const key = event.key;
    const normalizedKey = key.toLowerCase();

    if (keyboardPalette) {
      if (key === "Escape") {
        event.preventDefault();
        closeKeyboardPalette();
        return;
      }
      if (key === "ArrowDown") {
        event.preventDefault();
        updateKeyboardPaletteActiveIndex(keyboardPalette.activeIndex + 1);
        return;
      }
      if (key === "ArrowUp") {
        event.preventDefault();
        updateKeyboardPaletteActiveIndex(keyboardPalette.activeIndex - 1);
        return;
      }
      if (key === "Enter") {
        const option = keyboardPaletteOptions[keyboardPalette.activeIndex] ?? keyboardPaletteOptions[0];
        if (option) {
          event.preventDefault();
          createNodeFromKeyboardPalette(option.value);
        }
        return;
      }
      if (
        keyboardPalette.mode === "category" &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey &&
        key.length === 1
      ) {
        const option = findKeyboardShortcutOption(keyboardPaletteOptions, key);
        if (option) {
          event.preventDefault();
          createNodeFromKeyboardPalette(option.value);
        }
      }
      return;
    }

    if (isEditableElement(event.target)) return;

    if (event.ctrlKey || event.metaKey) {
      if (normalizedKey === "c") {
        if (selectedCount > 0) {
          event.preventDefault();
          copyCanvasSelection();
        }
        return;
      }
      if (normalizedKey === "v") {
        event.preventDefault();
        pasteCanvasClipboardAt(lastCanvasPointerRef.current?.world ?? getVisibleCenterWorld());
        return;
      }
      if (normalizedKey === "d") {
        event.preventDefault();
        duplicateCanvasSelection();
        return;
      }
      if (normalizedKey === "a") {
        event.preventDefault();
        selectAllCanvasElements();
        return;
      }
      if (normalizedKey === "l") {
        event.preventDefault();
        autoArrangeCanvas();
        return;
      }
      if (normalizedKey === "s") {
        event.preventDefault();
        void handleSave();
        return;
      }
      return;
    }

    if (event.shiftKey && normalizedKey === "f") {
      event.preventDefault();
      focusCanvasSelection();
      return;
    }

    if (key === "Delete") {
      if (deleteSelectionRef.current()) event.preventDefault();
      return;
    }

    if (key === "Escape") {
      if (nodeCreateMenu) {
        event.preventDefault();
        setNodeCreateMenu(null);
        return;
      }
      if (selection) {
        event.preventDefault();
        setSelection(null);
      }
      return;
    }

    if (event.code === "Space" || key === " ") {
      event.preventDefault();
      openKeyboardPalette("search");
      return;
    }

    if (!event.altKey && !event.shiftKey && key.length === 1) {
      const categoryKey = keyboardCategoryShortcuts[normalizedKey];
      if (categoryKey) {
        event.preventDefault();
        openKeyboardPalette("category", categoryKey);
      }
    }
  };

  return (
    <Flex className="propgraph-editor-root" vertical gap={4} style={{ height: "100%", minHeight: 0 }}>
      <Flex
        className="propgraph-editor-header"
        justify="space-between"
        align="center"
        gap={8}
        style={{ flex: "0 0 auto", minHeight: 28 }}
      >
        <Flex className="propgraph-editor-fields" gap={6} align="center" wrap="nowrap" style={{ flex: "1 1 auto", minWidth: 0 }}>
          <Input
            size="small"
            value={value.name}
            onChange={(e) => patchGraph({ name: e.target.value })}
            style={{ width: 168 }}
            placeholder="名称"
            status={getGraphFieldError(validationIssues, "name") ? "error" : undefined}
          />
          <Input
            size="small"
            value={value.assetId}
            onChange={(e) => patchGraph({ assetId: e.target.value })}
            style={{ width: 132 }}
            placeholder="资源ID"
            status={getGraphFieldError(validationIssues, "assetId") ? "error" : undefined}
          />
          <Select
            size="small"
            value={value.type}
            options={types}
            onChange={(type) => patchGraph({ type })}
            style={{ width: 112 }}
            placeholder="类型"
            status={getGraphFieldError(validationIssues, "type") ? "error" : undefined}
          />
          <Select
            size="small"
            value={value.rarity}
            options={rarities}
            onChange={(rarity) => patchGraph({ rarity })}
            style={{ width: 112 }}
            placeholder="品质"
            status={getGraphFieldError(validationIssues, "rarity") ? "error" : undefined}
          />
          <Select
            size="small"
            mode="multiple"
            value={value.tags}
            options={tags}
            onChange={(nextTags) => patchGraph({ tags: nextTags })}
            maxTagCount="responsive"
            style={{ width: 180, minWidth: 150 }}
            placeholder="标签"
          />
          <InputNumber
            size="small"
            value={value.yield}
            onChange={(yieldValue) => patchGraph({ yield: Number(yieldValue ?? 0) })}
            style={{ width: 92 }}
            placeholder="产出"
          />
          <Checkbox
            checked={value.derived}
            onChange={(e) => patchGraph({ derived: e.target.checked })}
            style={{ whiteSpace: "nowrap" }}
          >
            衍生物
          </Checkbox>
        </Flex>
        <Space size={6} style={{ flex: "0 0 auto" }}>
          <Tooltip title="保存">
            <Button size="small" type="primary" icon={<SaveOutlined />} onClick={handleSave} />
          </Tooltip>
          <Tooltip title="关闭">
            <Button size="small" icon={<CloseOutlined />} onClick={onClose} />
          </Tooltip>
        </Space>
      </Flex>
      <GraphDescriptionBar className="propgraph-description-bar">
        <GraphDescriptionText>
          <PropDescriptionText
            text={graphDescription}
            configOptions={configOptions}
            candidates={propNameCandidates}
            negativeFragments={negativeDescriptionFragments}
          />
        </GraphDescriptionText>
        <StrengthInline
          strength={graphStrength}
          simulationStrength={simulationStrength}
          graph={value}
          props={props}
          strengths={strengths}
        />
        <Tooltip title="复制道具 JSON">
          <Button
            size="small"
            icon={<CodeOutlined />}
            aria-label="复制道具 JSON"
            onClick={copyGraphJson}
          />
        </Tooltip>
        <Tooltip title="复制描述文本">
          <Button
            size="small"
            icon={<FileTextOutlined />}
            aria-label="复制描述文本"
            onClick={copyGraphDescription}
            disabled={!graphDescription}
          />
        </Tooltip>
      </GraphDescriptionBar>

      <Flex className="propgraph-editor-workspace" gap={8} style={{ minHeight: 0, flex: 1 }}>
        <div
          className="propgraph-canvas-shell"
          style={{
            flex: 1,
            minWidth: 0,
            position: "relative",
            border: "1px solid #e5e7eb",
            borderRadius: 6,
            overflow: "hidden",
            backgroundColor: "#fbfbfb",
          }}
        >
          <CanvasToolbar
            onAddNode={addNode}
            scale={viewport.scale}
            onResetViewport={resetViewport}
            onAutoArrange={autoArrangeCanvas}
            selectedCount={selectedCount}
            canCopy={selectedCount > 0}
            canPaste={Boolean(canvasClipboard) || canvasClipboardVersion > 0}
            onCopySelection={copyCanvasSelection}
            onPasteClipboard={pasteCanvasClipboard}
          />
          <div
            ref={canvasRef}
            onPointerDown={handleCanvasPointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={finishPointerInteraction}
            onPointerCancel={finishPointerInteraction}
            onPointerLeave={() => {
              if (!dragRef.current && !panRef.current && !connectionDragRef.current && !marqueeRef.current) {
                lastCanvasPointerRef.current = null;
              }
            }}
            onWheel={handleWheel}
            onContextMenu={handleCanvasContextMenu}
            style={{
              position: "absolute",
              top: 38,
              right: 0,
              bottom: 0,
              left: 0,
              overflow: "hidden",
              backgroundImage:
                "linear-gradient(#edf2f7 1px, transparent 1px), linear-gradient(90deg, #edf2f7 1px, transparent 1px)",
              backgroundPosition: `${viewport.x}px ${viewport.y}px`,
              backgroundSize: `${GRID_SIZE * viewport.scale}px ${GRID_SIZE * viewport.scale}px`,
              cursor: isPanning ? "grabbing" : "grab",
              touchAction: "none",
              userSelect: "none",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                width: 1,
                height: 1,
                overflow: "visible",
                pointerEvents: "none",
                transform: `translate3d(${viewport.x}px, ${viewport.y}px, 0) scale(${viewport.scale})`,
                transformOrigin: "0 0",
              }}
            >
              <svg
                width={1}
                height={1}
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  overflow: "visible",
                  pointerEvents: "none",
                }}
              >
                {edges.map((edge) => {
                  const from = nodeMap.get(edge.fromNodeId);
                  const to = nodeMap.get(edge.toNodeId);
                  if (!from || !to) return null;
                  const fromPort = getPropNodePorts(from).outputs.find((port) => port.id === edge.fromPortId);
                  const color = getPortColor(fromPort?.kind ?? "flow");
                  const fromPoint = getPortAnchor(from, getNodeEditorState(from), "output", edge.fromPortId);
                  const toPoint = getPortAnchor(to, getNodeEditorState(to), "input", edge.toPortId);
                  const path = buildEdgePath(fromPoint, toPoint);
                  const selected = selectedIds.edgeIds.has(edge.id);
                  return (
                    <g key={edge.id}>
                      <path
                        d={path}
                        stroke="transparent"
                        strokeWidth={14}
                        fill="none"
                        style={{ cursor: "pointer", pointerEvents: "stroke" }}
                        onPointerDown={(event) => {
                          event.stopPropagation();
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelection({ type: "edge", id: edge.id });
                        }}
                      />
                      <path
                        d={path}
                        stroke={selected ? "#1677ff" : color}
                        strokeWidth={selected ? 3 : 2}
                        fill="none"
                        style={{ pointerEvents: "none" }}
                      />
                      <circle
                        cx={toPoint.x}
                        cy={toPoint.y}
                        r={5}
                        fill={selected ? "#1677ff" : color}
                      />
                    </g>
                  );
                })}
                {connectionDraft &&
                  (() => {
                    const source = nodes.find(
                      (node) => node.id === connectionDraft.from.nodeId,
                    );
                    if (!source) return null;
                    const sourcePort = findPortInfo(nodes, connectionDraft.from)?.port;
                    const color = getPortColor(sourcePort?.kind ?? "flow");
                    const fromPoint = getPortAnchor(
                      source,
                      getNodeEditorState(source),
                      connectionDraft.from.direction,
                      connectionDraft.from.portId,
                    );
                    const path = buildEdgePath(fromPoint, connectionDraft.pointer);
                    return (
                      <path
                        d={path}
                        stroke={color}
                        strokeWidth={2}
                        strokeDasharray="6 5"
                        fill="none"
                        style={{ pointerEvents: "none" }}
                      />
                    );
                  })()}
              </svg>

              {nodes.map((node) => (
                <GraphNodeCard
                  key={node.id}
                  node={node}
                  configOptions={configOptions}
                  editorState={getNodeEditorState(node)}
                  selected={selectedIds.nodeIds.has(node.id)}
                  validationIssues={nodeValidationMap.get(node.id) ?? []}
                  getPortConnectionState={getPortConnectionState}
                  onPointerDown={handlePointerDown}
                  onPortPointerDown={handlePortPointerDown}
                  onSelect={(id) => setSelection({ type: "node", id })}
                />
              ))}
              {selectionMarquee && (
                <SelectionMarqueeBox rect={normalizeWorldRect(selectionMarquee.start, selectionMarquee.current)} />
              )}
            </div>
            {nodeCreateMenu && (
              <div
                style={{
                  position: "fixed",
                  left: nodeCreateMenu.screen.x,
                  top: nodeCreateMenu.screen.y,
                  zIndex: 1000,
                }}
                onPointerDown={(event) => event.stopPropagation()}
                onWheel={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
              >
                <NodeCreateMenu
                  groups={nodeCreateGroups}
                  onAddNode={(type) => addNode(type, nodeCreateMenu.world)}
                />
              </div>
            )}
            {keyboardPalette && (
              <div
                style={{
                  position: "fixed",
                  left: keyboardPalette.screen.x,
                  top: keyboardPalette.screen.y,
                  zIndex: 1100,
                }}
              >
                <KeyboardNodePalette
                  palette={keyboardPalette}
                  options={keyboardPaletteOptions}
                  onQueryChange={updateKeyboardPaletteQuery}
                  onActiveIndexChange={updateKeyboardPaletteActiveIndex}
                  onCreate={createNodeFromKeyboardPalette}
                  onClose={closeKeyboardPalette}
                />
              </div>
            )}
          </div>
        </div>

        <InspectorPanel
          graph={value}
          configOptions={configOptions}
          selectedNode={selectedNode}
          selectedNodeEditorState={selectedNode ? getNodeEditorState(selectedNode) : undefined}
          selectedEdge={selectedEdge}
          selectedCount={selectedCount}
          validationIssues={validationIssues}
          nodeOptions={nodeOptions}
          patchDocument={patchDocument}
          patchNode={patchNode}
          patchNodeEditorState={patchNodeEditorState}
          patchEdge={patchEdge}
          deleteNode={deleteNode}
          deleteEdge={deleteEdge}
        />
      </Flex>
    </Flex>
  );
}

function CanvasToolbar({
  onAddNode,
  scale,
  onResetViewport,
  onAutoArrange,
  selectedCount,
  canCopy,
  canPaste,
  onCopySelection,
  onPasteClipboard,
}: {
  onAddNode: (type: PropNodeType) => void;
  scale: number;
  onResetViewport: () => void;
  onAutoArrange: () => void;
  selectedCount: number;
  canCopy: boolean;
  canPaste: boolean;
  onCopySelection: () => void;
  onPasteClipboard: () => void;
}) {
  const [helpOpen, setHelpOpen] = useState(false);

  return (
    <Flex
      className="propgraph-canvas-toolbar"
      align="center"
      gap={8}
      wrap="nowrap"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        minHeight: 38,
        padding: "4px 8px",
        borderBottom: "1px solid #e5e7eb",
        background: "#fff",
        zIndex: 2,
        overflowX: "auto",
        overflowY: "hidden",
      }}
    >
      {nodeCreateGroups.map((group) => {
        const shortcut = getKeyboardCategoryShortcut(group.key);
        const shortcutText = shortcut ? `快捷键 ${shortcut}` : "无快捷键";
        return (
          <Popover
            key={group.key}
            trigger={["hover", "click"]}
            placement="bottomLeft"
            content={<NodeCreateMenu groups={group.children} onAddNode={onAddNode} />}
          >
            <Tooltip title={`${group.label}节点，${shortcutText}`}>
              <Button size="small" icon={<PlusOutlined />}>
                {shortcut ? `${group.label}(${shortcut})` : group.label}
              </Button>
            </Tooltip>
          </Popover>
        );
      })}
      <Divider type="vertical" style={{ marginInline: 2 }} />
      <Tooltip title="复制选中节点和连线">
        <Button
          size="small"
          icon={<CopyOutlined />}
          disabled={!canCopy}
          onClick={onCopySelection}
        />
      </Tooltip>
      <Tooltip title="粘贴到可见区域">
        <Button
          size="small"
          icon={<SnippetsOutlined />}
          disabled={!canPaste}
          onClick={onPasteClipboard}
        />
      </Tooltip>
      {selectedCount > 0 && <Tag style={{ marginInlineEnd: 0 }}>已选 {selectedCount}</Tag>}
      <Divider type="vertical" style={{ marginInline: 2 }} />
      <Tag style={{ marginInlineEnd: 0 }}>{Math.round(scale * 100)}%</Tag>
      <Tooltip title="自动整理画布">
        <Button size="small" icon={<ApartmentOutlined />} onClick={onAutoArrange} />
      </Tooltip>
      <Tooltip title="重置视图">
        <Button size="small" icon={<ReloadOutlined />} onClick={onResetViewport} />
      </Tooltip>
      <Tooltip title="画布使用说明和快捷键">
        <Button
          size="small"
          icon={<QuestionCircleOutlined />}
          onClick={() => setHelpOpen(true)}
          style={{ marginLeft: "auto" }}
        />
      </Tooltip>
      <Modal
        title="画布使用说明"
        open={helpOpen}
        onCancel={() => setHelpOpen(false)}
        footer={null}
        centered
        width={720}
      >
        <CanvasHelpContent />
      </Modal>
    </Flex>
  );
}

function CanvasHelpContent() {
  const categoryRows = nodeCreateGroups
    .map((group) => ({
      key: getKeyboardCategoryShortcut(group.key),
      label: `${group.label}节点菜单`,
    }))
    .filter((row) => row.key);
  const shortcutRows = [
    { key: "Space", label: "打开节点搜索面板" },
    ...categoryRows,
    { key: "↑ / ↓", label: "在节点搜索或分类菜单中切换选项" },
    { key: "Enter", label: "创建当前高亮节点" },
    { key: "Esc", label: "关闭菜单、取消选择" },
    { key: "Ctrl + C", label: "复制选中节点和连线" },
    { key: "Ctrl + V", label: "粘贴到鼠标位置；鼠标不在画布时粘贴到可见区域中心" },
    { key: "Ctrl + D", label: "复制并粘贴选中节点和连线" },
    { key: "Ctrl + A", label: "全选画布节点和连线" },
    { key: "Ctrl + S", label: "保存当前道具" },
    { key: "Delete", label: "删除选中节点或连线" },
    { key: "Ctrl + L", label: "自动整理画布" },
    { key: "Shift + F", label: "聚焦选中节点；未选中时适配全部节点" },
  ];
  const mouseRows = [
    { key: "左键拖动画布空白处", label: "拖动画布" },
    { key: "滚轮", label: "缩放画布" },
    { key: "右键画布空白处", label: "打开节点创建菜单" },
    { key: "Ctrl + 左键拖动", label: "框选多个节点和连线" },
    { key: "拖动端口", label: "连接可兼容端口" },
  ];

  return (
    <Flex vertical gap={16}>
      <div>
        <Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>创建节点</Title>
        <Text type="secondary">
          按 Space 可搜索全部节点；按 E/T/F/A/N/C/M 可直接打开对应分类。分类菜单打开后，按选项左侧的第二个键即可创建节点。
        </Text>
      </div>
      <HelpShortcutGrid rows={shortcutRows} />
      <div>
        <Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>鼠标操作</Title>
        <HelpShortcutGrid rows={mouseRows} />
      </div>
      <div>
        <Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>创建位置</Title>
        <Text type="secondary">
          鼠标在画布内时，新节点创建到鼠标所在位置；鼠标不在画布内时，创建到可见区域中心；已选中节点时，快捷键创建会放到选中节点右侧，并尝试自动连接兼容端口。
        </Text>
      </div>
    </Flex>
  );
}

function HelpShortcutGrid({
  rows,
}: {
  rows: Array<{ key: string; label: string }>;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "max-content minmax(0, 1fr)",
        gap: "8px 12px",
        alignItems: "center",
      }}
    >
      {rows.map((row) => (
        <Fragment key={`${row.key}:${row.label}`}>
          <KeyboardShortcutBadge>{row.key}</KeyboardShortcutBadge>
          <Text>{row.label}</Text>
        </Fragment>
      ))}
    </div>
  );
}

function NodeCreateMenu({
  groups,
  onAddNode,
}: {
  groups: Array<DescribedOption<PropNodeType> | NodeCreateGroup>;
  onAddNode: (type: PropNodeType) => void;
}) {
  return (
    <NodeCreateMenuSurface onWheel={(event) => event.stopPropagation()}>
      {groups.map((item) => {
        if (isNodeCreateGroup(item)) {
          const accent = getNodeCreateGroupColor(item.key);
          const shortcut = getKeyboardCategoryShortcut(item.key);
          const description = getNodeCreateGroupDescription(item);
          return (
            <Popover
              key={item.key}
              trigger="hover"
              placement="rightTop"
              content={<NodeCreateMenu groups={item.children} onAddNode={onAddNode} />}
            >
              <NodeCreateMenuRow type="button" $accent={accent}>
                <NodeCreateMenuSwatch $accent={accent} />
                <NodeCreateMenuText>
                  <NodeCreateMenuLabel>{item.label}</NodeCreateMenuLabel>
                  <Tooltip title={<DescriptionText text={description} compact />} placement="right" overlayStyle={{ maxWidth: 420 }} mouseEnterDelay={0.35}>
                    <NodeCreateMenuDescription><DescriptionText text={description} compact /></NodeCreateMenuDescription>
                  </Tooltip>
                </NodeCreateMenuText>
                <NodeCreateMenuTrailing>
                  {shortcut && <KeyboardShortcutBadge>{shortcut}</KeyboardShortcutBadge>}
                  <NodeCreateMenuArrow>{">"}</NodeCreateMenuArrow>
                </NodeCreateMenuTrailing>
              </NodeCreateMenuRow>
            </Popover>
          );
        }

        const accent = getNodeColor(item.value);
        return (
          <NodeCreateMenuRow
            key={item.value}
            type="button"
            $accent={accent}
            onClick={(event) => {
              event.stopPropagation();
              onAddNode(item.value);
            }}
          >
            <NodeCreateMenuSwatch $accent={accent} />
            <NodeCreateMenuText>
              <NodeCreateMenuLabel>{item.label}</NodeCreateMenuLabel>
              <Tooltip title={<DescriptionText text={item.description} compact />} placement="right" overlayStyle={{ maxWidth: 420 }} mouseEnterDelay={0.35}>
                <NodeCreateMenuDescription><DescriptionText text={item.description} compact /></NodeCreateMenuDescription>
              </Tooltip>
            </NodeCreateMenuText>
            <span />
          </NodeCreateMenuRow>
        );
      })}
    </NodeCreateMenuSurface>
  );
}

function KeyboardNodePalette({
  palette,
  options,
  onQueryChange,
  onActiveIndexChange,
  onCreate,
  onClose,
}: {
  palette: KeyboardPaletteState;
  options: KeyboardNodeOption[];
  onQueryChange: (query: string) => void;
  onActiveIndexChange: (index: number) => void;
  onCreate: (type: PropNodeType) => void;
  onClose: () => void;
}) {
  const inputRef = useRef<any>(null);

  useEffect(() => {
    if (palette.mode !== "search") return;
    window.setTimeout(() => inputRef.current?.focus?.(), 0);
  }, [palette.mode]);

  const title =
    palette.mode === "search"
      ? "创建节点"
      : `${keyboardCategoryLabels[palette.categoryKey ?? "action"]}节点`;
  const hint = palette.mode === "search" ? "输入名称、拼音或英文" : "按第二个键直接创建";

  return (
    <KeyboardPaletteSurface
      onPointerDown={(event) => event.stopPropagation()}
      onWheel={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <KeyboardPaletteHeader>
        <KeyboardPaletteTitle>{title}</KeyboardPaletteTitle>
        <KeyboardPaletteHint>{hint}</KeyboardPaletteHint>
      </KeyboardPaletteHeader>
      {palette.mode === "search" && (
        <Input
          ref={inputRef}
          size="small"
          value={palette.query}
          placeholder="搜索节点"
          onChange={(event) => onQueryChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              onClose();
            }
          }}
        />
      )}
      <KeyboardPaletteList>
        {options.length ? (
          options.map((item, index) => {
            const accent = getNodeColor(item.value);
            return (
              <KeyboardPaletteRow
                key={`${item.groupKey}:${item.value}`}
                type="button"
                $active={index === palette.activeIndex}
                $accent={accent}
                onMouseEnter={() => onActiveIndexChange(index)}
                onClick={() => onCreate(item.value)}
              >
                <KeyboardShortcutBadge>{item.shortcut ?? ""}</KeyboardShortcutBadge>
                <NodeCreateMenuSwatch $accent={accent} />
                <NodeCreateMenuText>
                  <NodeCreateMenuLabel>
                    {item.label}
                    <span style={{ color: "#94a3b8", fontWeight: 500 }}> / {item.groupLabel}</span>
                  </NodeCreateMenuLabel>
                  <Tooltip title={<DescriptionText text={item.description} compact />} placement="right" overlayStyle={{ maxWidth: 420 }} mouseEnterDelay={0.35}>
                    <NodeCreateMenuDescription><DescriptionText text={item.description} compact /></NodeCreateMenuDescription>
                  </Tooltip>
                </NodeCreateMenuText>
              </KeyboardPaletteRow>
            );
          })
        ) : (
          <KeyboardPaletteHint>没有匹配的节点</KeyboardPaletteHint>
        )}
      </KeyboardPaletteList>
    </KeyboardPaletteSurface>
  );
}

function SelectionMarqueeBox({ rect }: { rect: LayoutRect }) {
  return (
    <div
      style={{
        position: "absolute",
        left: rect.x,
        top: rect.y,
        width: rect.width,
        height: rect.height,
        border: "1px solid #1677ff",
        background: "rgba(22, 119, 255, 0.1)",
        boxShadow: "inset 0 0 0 1px rgba(22, 119, 255, 0.14)",
        pointerEvents: "none",
      }}
    />
  );
}

function getNodeCreateGroupColor(key: string) {
  switch (key) {
    case "event":
      return "#0f766e";
    case "target":
      return "#2563eb";
    case "filter":
      return "#0d9488";
    case "action":
      return "#c2410c";
    case "logic":
      return "#7c3aed";
    case "calculate":
      return "#0891b2";
    case "integer":
    case "float":
    case "rarity":
    case "tag":
    case "value":
      return "#0891b2";
    default:
      return "#475569";
  }
}

function getNodeCreateGroupDescription(group: NodeCreateGroup) {
  const count = group.children.length;
  if (group.key === "integer") return "整数常量、变量和属性";
  if (group.key === "float") return "浮点常量、变量和属性";
  if (group.key === "rarity") return "品质配置常量";
  if (group.key === "tag") return "标签配置常量";
  if (group.key === "target") return "选择当前道具、盘面目标或道具原型";
  if (group.key === "filter") return "按条件筛选目标";
  if (group.key === "value") return "输出整数、浮点数、品质或标签";
  if (group.key === "calculate") return "对数值进行运算和取整";
  if (group.key === "logic") return "输出可连接到动作条件端口的布尔条件";
  return `${count} 个节点类型`;
}

function getKeyboardNodeOptions() {
  return nodeCreateGroups.flatMap((group) =>
    flattenKeyboardNodeOptions(group.children, group.key as KeyboardCategoryKey, group.label),
  );
}

function flattenKeyboardNodeOptions(
  items: Array<DescribedOption<PropNodeType> | NodeCreateGroup>,
  groupKey: KeyboardCategoryKey,
  groupLabel: string,
): KeyboardNodeOption[] {
  return items.flatMap((item) => {
    if (isNodeCreateGroup(item)) {
      return flattenKeyboardNodeOptions(item.children, groupKey, groupLabel);
    }
    return [{
      ...item,
      groupKey,
      groupLabel,
      shortcut: nodeSecondKeyShortcuts[item.value],
      aliases: getNodeSearchAliases(item, groupLabel),
    }];
  });
}

function getNodeSearchAliases(option: DescribedOption<PropNodeType>, groupLabel: string) {
  return [
    option.value,
    option.label,
    option.description ?? "",
    groupLabel,
    ...(nodeSearchAliases[option.value] ?? []),
  ].map((item) => normalizeSearchText(String(item)));
}

function filterKeyboardNodeOptions(
  options: KeyboardNodeOption[],
  palette: KeyboardPaletteState | null,
) {
  if (!palette) return [];
  const scoped = palette.mode === "category" && palette.categoryKey
    ? options.filter((item) => item.groupKey === palette.categoryKey)
    : options;
  const query = normalizeSearchText(palette.query);
  if (!query) return scoped;
  return scoped.filter((item) => item.aliases.some((alias) => alias.includes(query)));
}

function normalizeSearchText(text: string) {
  return text.trim().toLowerCase().replace(/\s+/g, "");
}

function findKeyboardShortcutOption(options: KeyboardNodeOption[], key: string) {
  const normalized = key.toUpperCase();
  return options.find((item) => item.shortcut?.toUpperCase() === normalized);
}

function GraphNodeCard({
  node,
  configOptions,
  editorState,
  selected,
  validationIssues,
  getPortConnectionState,
  onPointerDown,
  onPortPointerDown,
  onSelect,
}: {
  node: PropNode;
  configOptions: PropGraphConfigOptions;
  editorState: PropGraphNodeEditorState;
  selected: boolean;
  validationIssues: PropGraphValidationIssue[];
  getPortConnectionState: (
    node: PropNode,
    port: PropGraphPort,
    direction: PortDirection,
  ) => PortConnectionState;
  onPointerDown: (event: ReactPointerEvent<HTMLDivElement>, node: PropNode) => void;
  onPortPointerDown: (
    event: ReactPointerEvent<HTMLButtonElement>,
    node: PropNode,
    port: PropGraphPort,
    direction: PortDirection,
  ) => void;
  onSelect: (id: string) => void;
}) {
  const color = getNodeColor(node.type);
  const enabled = !editorState.disabled;
  const hasErrors = validationIssues.length > 0;
  const negativeEffect = isNegativeEffectNode(node);
  const activeColor = enabled ? color : "#94a3b8";
  const ports = getPropNodePorts(node);
  const portRows = Math.max(ports.inputs.length, ports.outputs.length);
  const propertyRows = getNodePropertyRows(node, configOptions);
  const nodeHeight = estimateNodeHeight(node);
  return (
    <div
      onPointerDown={(event) => onPointerDown(event, node)}
      onClick={(event) => {
        event.stopPropagation();
        onSelect(node.id);
      }}
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        transform: `translate3d(${editorState.position.x}px, ${editorState.position.y}px, 0)`,
        width: NODE_WIDTH,
        minHeight: nodeHeight,
        border: `2px solid ${hasErrors ? "#ff4d4f" : selected ? "#1677ff" : activeColor}`,
        borderRadius: 8,
        background: enabled ? (negativeEffect ? "#fff1f2" : "#fff") : "#f8fafc",
        boxShadow: selected
          ? "0 8px 24px rgba(22, 119, 255, 0.18)"
          : hasErrors
            ? "0 8px 22px rgba(255, 77, 79, 0.16)"
          : enabled
            ? "0 4px 14px rgba(15, 23, 42, 0.08)"
            : "0 3px 10px rgba(100, 116, 139, 0.12)",
        cursor: "move",
        overflow: "hidden",
        pointerEvents: "auto",
        contain: "layout paint style",
        willChange: selected ? "transform" : undefined,
      }}
      data-prop-graph-node="true"
    >
      <Flex
        justify="space-between"
        align="center"
        style={{
          height: NODE_HEADER_HEIGHT,
          padding: "0 9px",
          background: activeColor,
          color: "#fff",
        }}
      >
        <strong style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {getDisplayNodeTitle(node, configOptions)}
        </strong>
        <Space size={5}>
          <span style={{ fontSize: 12 }}>{getNodeTypeLabel(node.type)}</span>
          {hasErrors && (
            <Tooltip title={formatValidationIssueText(validationIssues)}>
              <WarningOutlined style={{ color: "#fff", fontSize: 13 }} />
            </Tooltip>
          )}
        </Space>
      </Flex>
      <div
        style={{
          position: "relative",
          padding: NODE_BODY_PADDING,
          paddingTop:
            NODE_BODY_PADDING +
            (portRows > 0 ? portRows * PORT_ROW_HEIGHT + 8 : 0),
        }}
      >
        <PortRail
          node={node}
          enabled={enabled}
          direction="input"
          ports={ports.inputs}
          validationIssues={validationIssues}
          getPortConnectionState={getPortConnectionState}
          onPortPointerDown={onPortPointerDown}
        />
        <PortRail
          node={node}
          enabled={enabled}
          direction="output"
          ports={ports.outputs}
          validationIssues={validationIssues}
          getPortConnectionState={getPortConnectionState}
          onPortPointerDown={onPortPointerDown}
        />
        <NodePropertyList rows={propertyRows} />
      </div>
    </div>
  );
}

function PortRail({
  node,
  enabled,
  ports,
  direction,
  validationIssues,
  getPortConnectionState,
  onPortPointerDown,
}: {
  node: PropNode;
  enabled: boolean;
  ports: PropGraphPort[];
  direction: PortDirection;
  validationIssues: PropGraphValidationIssue[];
  getPortConnectionState: (
    node: PropNode,
    port: PropGraphPort,
    direction: PortDirection,
  ) => PortConnectionState;
  onPortPointerDown: (
    event: ReactPointerEvent<HTMLButtonElement>,
    node: PropNode,
    port: PropGraphPort,
    direction: PortDirection,
  ) => void;
}) {
  const isInput = direction === "input";
  return (
    <div
      style={{
        position: "absolute",
        top: NODE_BODY_PADDING,
        left: isInput ? 0 : undefined,
        right: isInput ? undefined : 0,
        width: 126,
      }}
    >
      {ports.map((port) => {
        const color = enabled ? getPortColor(port.kind) : "#94a3b8";
        const portIssue = validationIssues.find(
          (issue) => issue.kind === "port" && issue.portId === port.id,
        );
        const connectionState = getPortConnectionState(node, port, direction);
        const style = getPortConnectionStyle(connectionState, portIssue ? "#ff4d4f" : color);
        return (
          <div
            key={port.id}
            style={{
              display: "flex",
              flexDirection: isInput ? "row" : "row-reverse",
              alignItems: "center",
              gap: 5,
              height: PORT_ROW_HEIGHT,
              paddingLeft: isInput ? 0 : 6,
              paddingRight: isInput ? 6 : 0,
            }}
          >
            <Tooltip title={portIssue?.message ?? formatPortDescription(node, port, direction)}>
              <button
                type="button"
                data-prop-graph-port="true"
                data-node-id={node.id}
                data-port-id={port.id}
                data-direction={direction}
                onPointerDown={(event) => onPortPointerDown(event, node, port, direction)}
                onClick={(event) => event.stopPropagation()}
                style={{
                  width: PORT_HIT_SIZE,
                  height: PORT_HIT_SIZE,
                  borderRadius: PORT_HIT_SIZE,
                  border: 0,
                  background: "transparent",
                  cursor:
                    connectionState === "hoverInvalid"
                      ? "not-allowed"
                      : connectionState === "connectable" ||
                          connectionState === "hoverConnectable"
                        ? "copy"
                        : "crosshair",
                  padding: 0,
                  flex: "0 0 auto",
                  display: "grid",
                  placeItems: "center",
                  opacity: style.opacity,
                }}
              >
                <span
                  style={{
                    width: PORT_HANDLE_SIZE,
                    height: PORT_HANDLE_SIZE,
                    borderRadius: PORT_HANDLE_SIZE,
                    border: `2px solid ${style.borderColor}`,
                    background: portIssue ? "#fff1f0" : style.background,
                    boxShadow: portIssue ? "0 0 0 4px rgba(255, 77, 79, 0.16)" : style.boxShadow,
                    transform: style.scale ? `scale(${style.scale})` : undefined,
                    transition: "border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease, background 120ms ease",
                  }}
                />
              </button>
            </Tooltip>
            <span
              style={{
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: portIssue ? "#ff4d4f" : style.labelColor ?? (enabled ? "#475569" : "#94a3b8"),
                fontSize: 11,
                textAlign: isInput ? "left" : "right",
                opacity: style.opacity,
              }}
            >
              {getDisplayPortName(port)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function NodePropertyList({ rows }: { rows: NodeDisplayRow[] }) {
  if (!rows.length) return null;

  return (
    <div
      style={{
        borderTop: "1px solid #edf2f7",
        paddingTop: 6,
      }}
    >
      {rows.map((row) => (
        <div
          key={row.label}
          style={{
            display: "grid",
            gridTemplateColumns: "56px minmax(0, 1fr)",
            gap: 6,
            alignItems: "start",
            minHeight: 18,
            fontSize: 11,
            lineHeight: 1.25,
          }}
        >
          <span style={{ color: "#64748b" }}>{row.label}</span>
          <span
            style={{
              color: row.muted ? "#94a3b8" : "#111827",
              overflowWrap: "anywhere",
              whiteSpace: "pre-wrap",
            }}
          >
            {row.value}
          </span>
        </div>
      ))}
    </div>
  );
}

function InspectorPanel({
  graph,
  configOptions,
  selectedNode,
  selectedNodeEditorState,
  selectedEdge,
  selectedCount,
  validationIssues,
  nodeOptions,
  patchDocument,
  patchNode,
  patchNodeEditorState,
  patchEdge,
  deleteNode,
  deleteEdge,
}: {
  graph: PropGraph;
  configOptions: PropGraphConfigOptions;
  selectedNode?: PropNode;
  selectedNodeEditorState?: PropGraphNodeEditorState;
  selectedEdge?: PropEdge;
  selectedCount: number;
  validationIssues: PropGraphValidationIssue[];
  nodeOptions: { value: string; label: string }[];
  patchDocument: (patch: Partial<PropGraph["graph"]>) => void;
  patchNode: (nodeId: string, patch: Partial<PropNode>) => void;
  patchNodeEditorState: (
    nodeId: string,
    patch: Partial<PropGraphNodeEditorState>,
  ) => void;
  patchEdge: (edgeId: string, patch: Partial<PropEdge>) => void;
  deleteNode: (nodeId: string) => void;
  deleteEdge: (edgeId: string) => void;
}) {
  const nodes = getGraphNodeList(graph.graph);
  const fromNode = nodes.find((node) => node.id === selectedEdge?.fromNodeId);
  const toNode = nodes.find((node) => node.id === selectedEdge?.toNodeId);
  const selectedNodeIssues = selectedNode
    ? validationIssues.filter((issue) => issue.nodeId === selectedNode.id)
    : [];

  return (
    <div
      className="propgraph-inspector"
      style={{
        width: 360,
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        background: "#fff",
        overflowY: "auto",
        padding: 12,
      }}
    >
      <Title level={5} style={{ marginTop: 0 }}>
        属性
      </Title>
      {!selectedNode && !selectedEdge && selectedCount === 0 && (
        <Text type="secondary">选择画布上的节点或连线。</Text>
      )}
      {!selectedNode && !selectedEdge && selectedCount > 0 && (
        <Text type="secondary">已选择 {selectedCount} 个画布元素，可使用工具栏复制、粘贴或按 Delete 删除。</Text>
      )}

      {selectedNode && (
        <>
          {selectedNodeIssues.length > 0 && (
            <div
              style={{
                border: "1px solid #ffccc7",
                background: "#fff2f0",
                color: "#cf1322",
                borderRadius: 6,
                padding: 8,
                marginBottom: 10,
                fontSize: 12,
                lineHeight: 1.5,
              }}
            >
              {selectedNodeIssues.map((issue) => (
                <div key={`${issue.kind}-${issue.field ?? issue.portId ?? issue.message}`}>
                  {issue.message}
                </div>
              ))}
            </div>
          )}
          <Field label="启用">
            <Checkbox
              checked={!selectedNodeEditorState?.disabled}
              onChange={(e) =>
                patchNodeEditorState(selectedNode.id, { disabled: !e.target.checked })
              }
            />
          </Field>
          <NodeDataInspector
            node={selectedNode}
            configOptions={configOptions}
            validationIssues={selectedNodeIssues}
            onChange={(node) => patchNode(selectedNode.id, node)}
          />
          <Divider />
          <Popconfirm
            title="确认删除这个节点？"
            okText="确定"
            cancelText="取消"
            onConfirm={() => deleteNode(selectedNode.id)}
          >
            <Button danger block icon={<DeleteOutlined />}>
              删除节点
            </Button>
          </Popconfirm>
        </>
      )}

      {selectedEdge && (
        <>
          <Field label="起点节点">
            <Select
              value={selectedEdge.fromNodeId}
              options={nodeOptions}
              onChange={(fromNodeId) =>
                patchEdge(selectedEdge.id, { fromNodeId, fromPortId: "" })
              }
            />
          </Field>
          <Field label="起点端口">
            <Select
              value={selectedEdge.fromPortId}
              options={(fromNode ? getPropNodePorts(fromNode).outputs : []).map((port) => ({
                value: port.id,
                label: formatPortOption(port),
              }))}
              onChange={(fromPortId) => patchEdge(selectedEdge.id, { fromPortId })}
            />
          </Field>
          <Field label="终点节点">
            <Select
              value={selectedEdge.toNodeId}
              options={nodeOptions}
              onChange={(toNodeId) =>
                patchEdge(selectedEdge.id, { toNodeId, toPortId: "" })
              }
            />
          </Field>
          <Field label="终点端口">
            <Select
              value={selectedEdge.toPortId}
              options={(toNode ? getPropNodePorts(toNode).inputs : []).map((port) => ({
                value: port.id,
                label: formatPortOption(port),
              }))}
              onChange={(toPortId) => patchEdge(selectedEdge.id, { toPortId })}
            />
          </Field>
          <Popconfirm
            title="确认删除这条连线？"
            okText="确定"
            cancelText="取消"
            onConfirm={() => deleteEdge(selectedEdge.id)}
          >
            <Button danger block icon={<DeleteOutlined />}>
              删除连线
            </Button>
          </Popconfirm>
        </>
      )}
    </div>
  );
}

function NodeDataInspector({
  node,
  configOptions,
  validationIssues,
  onChange,
}: {
  node: PropNode;
  configOptions: PropGraphConfigOptions;
  validationIssues: PropGraphValidationIssue[];
  onChange: (node: Partial<PropNode>) => void;
}) {
  switch (node.type) {
    case "eventTurnStart":
      return <TurnStartEventInspector data={node.data} validationIssues={validationIssues} onChange={(data) => onChange({ data } as Partial<PropNode>)} />;
    case "eventAcquire":
    case "eventUse":
    case "eventEliminated":
    case "targetSelf":
    case "targetFilter":
    case "filterRarity":
    case "filterTag":
    case "filterPrototype":
    case "filterRandom":
    case "intQuantity":
    case "intKindCount":
    case "intRandom":
    case "floatRandom":
      return null;
    case "intOperation":
      return <IntegerOperationInspector data={node.data} validationIssues={validationIssues} onChange={(data) => onChange({ data } as Partial<PropNode>)} />;
    case "floatOperation":
      return <FloatOperationInspector data={node.data} validationIssues={validationIssues} onChange={(data) => onChange({ data } as Partial<PropNode>)} />;
    case "roundNumber":
      return <RoundNumberInspector data={node.data} validationIssues={validationIssues} onChange={(data) => onChange({ data } as Partial<PropNode>)} />;
    case "prototypeLibrary":
      return <PrototypeLibraryInspector data={node.data} validationIssues={validationIssues} onChange={(data) => onChange({ data } as Partial<PropNode>)} />;
    case "actionGrow":
    case "actionExtraYield":
    case "actionPermanentBoost":
    case "actionPurify":
    case "actionProtect":
    case "actionAccelerate":
    case "actionTransform":
    case "actionSpawn":
    case "actionEliminate":
    case "actionCollect":
    case "actionHarvest":
    case "actionRarityWeight":
    case "actionExtendCommission":
    case "actionSwapPosition":
    case "actionTriggerVisual":
      return <ActionEffectInspector data={node.data} effects={configOptions.effects} animations={configOptions.animations} onChange={(data) => onChange({ data } as Partial<PropNode>)} />;
    case "targetBoard":
      return <BoardMaterialInspector data={node.data} validationIssues={validationIssues} onChange={(data) => onChange({ data } as Partial<PropNode>)} />;
    case "prototypeSelect":
      return <PrototypeSelectInspector data={node.data} props={configOptions.props} validationIssues={validationIssues} onChange={(data) => onChange({ data } as Partial<PropNode>)} />;
    case "intConstant":
      return <IntegerConstantInspector data={node.data} validationIssues={validationIssues} onChange={(data) => onChange({ data } as Partial<PropNode>)} />;
    case "floatConstant":
      return <FloatConstantInspector data={node.data} validationIssues={validationIssues} onChange={(data) => onChange({ data } as Partial<PropNode>)} />;
    case "rarityConstant":
      return <DictConstantInspector data={node.data} label="品质" options={configOptions.rarities} validationIssues={validationIssues} onChange={(data) => onChange({ data } as Partial<PropNode>)} />;
    case "tagConstant":
      return <DictConstantInspector data={node.data} label="标签" options={configOptions.tags} validationIssues={validationIssues} onChange={(data) => onChange({ data } as Partial<PropNode>)} />;
    case "intAttribute":
    case "floatAttribute":
      return <AttributeValueInspector data={node.data} validationIssues={validationIssues} onChange={(data) => onChange({ data } as Partial<PropNode>)} />;
    case "logicCompareInt":
    case "logicCompareFloat":
      return <CompareInspector data={node.data} validationIssues={validationIssues} onChange={(data) => onChange({ data } as Partial<PropNode>)} />;
    case "logicContains":
      return null;
    case "logicProbability":
      return <ProbabilityInspector data={node.data} validationIssues={validationIssues} onChange={(data) => onChange({ data } as Partial<PropNode>)} />;
  }
}

function getFieldError(issues: PropGraphValidationIssue[], field: string) {
  return issues.find((issue) => issue.kind === "field" && issue.field === field)?.message;
}

function getGraphFieldError(issues: PropGraphValidationIssue[], field: string) {
  return issues.find((issue) => issue.kind === "graphField" && issue.field === field)?.message;
}

function formatValidationIssueText(issues: PropGraphValidationIssue[]) {
  return issues.map((issue) => issue.message).join("\n");
}

function TurnStartEventInspector({
  data,
  validationIssues,
  onChange,
}: {
  data: EventTurnStartNodeData;
  validationIssues: PropGraphValidationIssue[];
  onChange: (data: EventTurnStartNodeData) => void;
}) {
  return (
    <>
      <Field label="间隔" error={getFieldError(validationIssues, "interval")}>
        <InputNumber min={0} precision={0} value={data.interval ?? 0} onChange={(interval) => onChange({ ...data, interval: Number(interval ?? 0) })} />
      </Field>
      <Field label="循环" error={getFieldError(validationIssues, "loop")}>
        <InputNumber min={0} precision={0} value={data.loop} onChange={(loop) => onChange({ ...data, loop: loop === null ? undefined : Number(loop) })} />
      </Field>
    </>
  );
}

function ActionEffectInspector({
  data,
  effects,
  animations,
  onChange,
}: {
  data: ActionEffectNodeData | ActionPermanentBoostNodeData;
  effects: EffectConfig[];
  animations: AnimationConfig[];
  onChange: (data: ActionEffectNodeData | ActionPermanentBoostNodeData) => void;
}) {
  const selfEffects = normalizeEffectIds(data.selfEffects, data.selfEffectId);
  const targetEffects = normalizeEffectIds(data.targetEffects, data.targetEffectId);
  const selfAnimations = normalizeAnimationIds(data.selfAnimations);
  const targetAnimations = normalizeAnimationIds(data.targetAnimations);
  const effectOptions = getEffectSelectOptions(effects, [
    ...selfEffects,
    ...targetEffects,
  ]);
  const animationOptions = getAnimationSelectOptions(animations, [
    ...selfAnimations,
    ...targetAnimations,
  ]);

  return (
    <>
      <Field label="负面" help="标记该动作产生的是负面效果。净化类逻辑和运行器可以据此识别并移除负面效果。">
        <Checkbox
          checked={Boolean(data.negative)}
          onChange={(event) => onChange({ ...data, negative: event.target.checked })}
        >
          负面效果
        </Checkbox>
      </Field>
      <Field label="自身特效" help="动作触发时，在执行动作的当前道具自身播放的特效。运行时图只保留特效资源路径。">
        <Select
          mode="multiple"
          allowClear
          showSearch
          value={selfEffects}
          options={effectOptions}
          optionFilterProp="label"
          placeholder="无"
          {...describedSelectProps}
          onChange={(nextSelfEffects) => onChange({
            ...data,
            selfEffects: normalizeEffectIds(nextSelfEffects),
            selfEffectId: undefined,
          })}
        />
      </Field>
      <Field label="目标特效" help="动作触发时，在动作实际作用到的目标对象上播放的特效。运行时图只保留特效资源路径。">
        <Select
          mode="multiple"
          allowClear
          showSearch
          value={targetEffects}
          options={effectOptions}
          optionFilterProp="label"
          placeholder="无"
          {...describedSelectProps}
          onChange={(nextTargetEffects) => onChange({
            ...data,
            targetEffects: normalizeEffectIds(nextTargetEffects),
            targetEffectId: undefined,
          })}
        />
      </Field>
      <Field label="自身动画" help="动作触发时，在执行动作的当前道具自身播放的动画。该字段仅保存动画资源 ID，暂不接入游戏核心逻辑。">
        <Select
          mode="multiple"
          allowClear
          showSearch
          value={selfAnimations}
          options={animationOptions}
          optionFilterProp="label"
          placeholder="无"
          {...describedSelectProps}
          onChange={(nextSelfAnimations) => onChange({
            ...data,
            selfAnimations: normalizeAnimationIds(nextSelfAnimations),
          })}
        />
      </Field>
      <Field label="目标动画" help="动作触发时，在动作实际作用到的目标对象上播放的动画。该字段仅保存动画资源 ID，暂不接入游戏核心逻辑。">
        <Select
          mode="multiple"
          allowClear
          showSearch
          value={targetAnimations}
          options={animationOptions}
          optionFilterProp="label"
          placeholder="无"
          {...describedSelectProps}
          onChange={(nextTargetAnimations) => onChange({
            ...data,
            targetAnimations: normalizeAnimationIds(nextTargetAnimations),
          })}
        />
      </Field>
      {"mutexTag" in data ? (
        <Field label="互斥标签" help="同一目标上相同互斥标签的永久加成不会叠加，新触发的效果会覆盖旧效果；不同互斥标签可以同时存在。">
          <Input
            value={data.mutexTag}
            placeholder="默认"
            onChange={(event) => onChange({ ...data, mutexTag: event.target.value })}
          />
        </Field>
      ) : null}
    </>
  );
}

function IntegerConstantInspector({
  data,
  validationIssues,
  onChange,
}: {
  data: IntConstantNodeData;
  validationIssues: PropGraphValidationIssue[];
  onChange: (data: IntConstantNodeData) => void;
}) {
  return (
    <Field label="值" error={getFieldError(validationIssues, "value")}>
      <InputNumber precision={0} value={data.value} onChange={(value) => onChange({ value: Number(value ?? 0) })} />
    </Field>
  );
}

function FloatConstantInspector({
  data,
  validationIssues,
  onChange,
}: {
  data: FloatConstantNodeData;
  validationIssues: PropGraphValidationIssue[];
  onChange: (data: FloatConstantNodeData) => void;
}) {
  return (
    <Field label="值" error={getFieldError(validationIssues, "value")}>
      <InputNumber value={data.value} onChange={(value) => onChange({ value: Number(value ?? 0) })} />
    </Field>
  );
}

function DictConstantInspector({
  data,
  label,
  options,
  validationIssues,
  onChange,
}: {
  data: RarityConstantNodeData | TagConstantNodeData;
  label: string;
  options: DictItem[];
  validationIssues: PropGraphValidationIssue[];
  onChange: (data: RarityConstantNodeData | TagConstantNodeData) => void;
}) {
  return (
    <Field label={label} error={getFieldError(validationIssues, "value")}>
      <Select
        value={data.value}
        options={options}
        onChange={(value) => onChange({ value: Number(value) })}
      />
    </Field>
  );
}

function AttributeValueInspector({
  data,
  validationIssues,
  onChange,
}: {
  data: AttributeValueNodeData;
  validationIssues: PropGraphValidationIssue[];
  onChange: (data: AttributeValueNodeData) => void;
}) {
  return (
    <Field label="属性" error={getFieldError(validationIssues, "attribute")}>
      <Select value={data.attribute} options={targetAttributeOptions} {...describedSelectProps} onChange={(attribute) => onChange({ attribute })} />
    </Field>
  );
}

function CompareInspector({
  data,
  validationIssues,
  onChange,
}: {
  data: CompareNodeData;
  validationIssues: PropGraphValidationIssue[];
  onChange: (data: CompareNodeData) => void;
}) {
  return (
    <Field label="比较" error={getFieldError(validationIssues, "op")}>
      <Select value={data.op} options={compareOpOptions} {...describedSelectProps} onChange={(op) => onChange({ op })} />
    </Field>
  );
}

function IntegerOperationInspector({
  data,
  validationIssues,
  onChange,
}: {
  data: IntegerOperationNodeData;
  validationIssues: PropGraphValidationIssue[];
  onChange: (data: IntegerOperationNodeData) => void;
}) {
  return (
    <Field label="运算" error={getFieldError(validationIssues, "op")}>
      <Select value={data.op} options={integerOperationOptions} {...describedSelectProps} onChange={(op) => onChange({ op })} />
    </Field>
  );
}

function FloatOperationInspector({
  data,
  validationIssues,
  onChange,
}: {
  data: FloatOperationNodeData;
  validationIssues: PropGraphValidationIssue[];
  onChange: (data: FloatOperationNodeData) => void;
}) {
  return (
    <Field label="运算" error={getFieldError(validationIssues, "op")}>
      <Select value={data.op} options={floatOperationOptions} {...describedSelectProps} onChange={(op) => onChange({ op })} />
    </Field>
  );
}

function RoundNumberInspector({
  data,
  validationIssues,
  onChange,
}: {
  data: RoundNumberNodeData;
  validationIssues: PropGraphValidationIssue[];
  onChange: (data: RoundNumberNodeData) => void;
}) {
  return (
    <Field label="取整方式" error={getFieldError(validationIssues, "mode")}>
      <Select value={data.mode} options={roundModeOptions} {...describedSelectProps} onChange={(mode) => onChange({ mode })} />
    </Field>
  );
}

function ProbabilityInspector({
  data,
  validationIssues,
  onChange,
}: {
  data: ProbabilityNodeData;
  validationIssues: PropGraphValidationIssue[];
  onChange: (data: ProbabilityNodeData) => void;
}) {
  return (
    <Field label="概率" error={getFieldError(validationIssues, "probability")}>
      <InputNumber
        min={0}
        max={1}
        step={0.01}
        precision={4}
        value={data.probability}
        onChange={(probability) => onChange({ probability: Number(probability ?? 0) })}
      />
    </Field>
  );
}

function BoardMaterialInspector({
  data,
  validationIssues,
  onChange,
}: {
  data: TargetBoardNodeData;
  validationIssues: PropGraphValidationIssue[];
  onChange: (data: TargetBoardNodeData) => void;
}) {
  const positionOptions = data.scope === "absolute" ? absolutePositionOptions : relativePositionOptions;
  const positions = normalizeBoardMaterialPositionArray(data.position);
  const includeEmptyPatch = data.includeEmpty ? { includeEmpty: true } : {};

  return (
    <>
      <Field label="范围" error={getFieldError(validationIssues, "scope")}>
        <Select
          value={data.scope}
          options={boardMaterialScopeOptions}
          {...describedSelectProps}
          onChange={(scope) => onChange(scope === "all" ? { scope, ...includeEmptyPatch } : { scope, position: getDefaultBoardMaterialPositions(scope), ...includeEmptyPatch })}
        />
      </Field>
      {data.scope !== "all" && (
        <Field label="位置" error={getFieldError(validationIssues, "position")}>
          <Select
            mode="multiple"
            value={positions}
            options={positionOptions}
            {...describedSelectProps}
            onChange={(position) => onChange({ ...data, position })}
          />
        </Field>
      )}
      <Field label="包含空位置">
        <Checkbox
          checked={Boolean(data.includeEmpty)}
          onChange={(event) => onChange({ ...data, includeEmpty: event.target.checked || undefined })}
        >
          将空位置作为目标输出
        </Checkbox>
      </Field>
    </>
  );
}

function PrototypeLibraryInspector({
  data,
  validationIssues,
  onChange,
}: {
  data: PrototypeLibraryNodeData;
  validationIssues: PropGraphValidationIssue[];
  onChange: (data: PrototypeLibraryNodeData) => void;
}) {
  return (
    <Field label="类型" error={getFieldError(validationIssues, "propType")}>
      <Select
        value={data.propType ?? 1}
        options={prototypeLibraryTypeOptions}
        {...describedSelectProps}
        onChange={(propType) => onChange({ propType })}
      />
    </Field>
  );
}

function PrototypeSelectInspector({
  data,
  props,
  validationIssues,
  onChange,
}: {
  data: PrototypeSelectNodeData;
  props: PropGraph[];
  validationIssues: PropGraphValidationIssue[];
  onChange: (data: PrototypeSelectNodeData) => void;
}) {
  const options = props.map((prop) => ({
    value: prop._id,
    label: prop.name || prop.assetId || prop._id,
    description: [prop.assetId, prop._id].filter(Boolean).join(" / "),
  }));

  return (
    <Field label="道具原型" error={getFieldError(validationIssues, "prototypeId")}>
      <Select
        allowClear
        showSearch
        value={data.prototypeId}
        options={options}
        optionFilterProp="label"
        {...describedSelectProps}
        onChange={(prototypeId) => onChange({ prototypeId })}
      />
    </Field>
  );
}

const inspectorFieldHelp: Record<string, string> = {
  启用: "控制当前节点是否参与运行。关闭后节点会保留在画布上，但运行逻辑应忽略它及其输出影响。",
  起点节点: "连线的输出节点。运行时数据或流程会从这个节点的指定输出端口流出。",
  起点端口: "连线使用的输出端口。端口类型必须与终点端口兼容，例如流程连流程、数值连数值。",
  终点节点: "连线的输入节点。运行时数据或流程会进入这个节点的指定输入端口。",
  终点端口: "连线使用的输入端口。它决定这条连线把来源数据写入目标节点的哪个输入。",
  事件: "事件节点是整张图的入口。回合开始按轮次触发；持有触发在道具被持有期间每轮触发，移除后停止；主动使用在玩家使用本道具时触发；消除触发每轮开始注册输入目标监听，目标在本轮被消除时触发，并在回合结束注销监听。",
  间隔: "控制事件触发间隔。0 表示不跳过回合，每次回合开始都可触发；大于 0 表示两次触发之间至少间隔对应回合数。",
  循环: "限制回合开始事件最多循环多少次。留空或 0 表示不限制触发次数。",
  最大次数: "限制事件节点最多触发多少次。0 或留空表示不限制触发次数。",
  来源: "决定当前节点从哪里取得目标或数值。筛选节点用于选择目标，取值节点用于读取数值。",
  范围: "限定筛选目标的空间范围，例如相邻、同行、同列、对角线或全场。",
  数量限制: "限制筛选结果数量。0 表示不限制，正数表示最多保留对应数量的目标。",
  值: "字段匹配、变量写入或动作参数使用的具体值。会按输入内容自动转换为数字、布尔或文本。",
  模式: "条件节点的判断方式。概率用于随机通过，比较用于比较数值，存在目标和数量用于判断目标集合。",
  概率: "条件通过的概率，范围 0 到 1。1 表示必定通过，0 表示不会通过，0.5 表示 50% 概率通过。",
  条件: "布尔条件输入。未连接时动作默认不受条件限制；连接后运行器可按该条件决定动作是否执行。",
  比较: "比较运算符，用来判断左值和右值之间的关系，并输出布尔条件。",
  右值: "比较节点右侧的固定值。输入内容会自动转换为数字、布尔或文本。",
  字段: "从目标属性中读取的属性，可选类型、品质、标签、产出或数量。",
  属性: "从目标读取的具体属性。目前支持品质和标签，输出整数或对应节点声明的数值类型。",
  常量: "固定写在节点里的值，不依赖输入目标或变量。",
  变量: "图内变量。变量可在多个节点之间共享，用于暂存运行过程中的值。",
  运算: "计算节点使用的运算方式。除数为 0 时运行器会返回 0，并在编辑器中保留配置提示。",
  常量操作数: "计算节点额外使用的固定数字列表。可输入多个数字，逗号分隔。",
  动作: "动作节点用于改变目标输入端口收到的目标集合。成长会永久增加产出；额外产出会让目标本次额外产出魔力，并可使用数量或倍率输入；保护会让目标不被消除；加速会减少轮次间隔；转化会变更目标道具；生成会在输入位置添加指定目标；消除会移除指定数量的目标。",
  目标: "由节点的目标输入端口提供，不再保存为动作属性。",
  位置: "生成动作使用的位置输入。实际数据类型仍是目标数组，由运行器解释为生成位置。",
  道具编号: "道具资源 ID，用于在配置中定位具体道具。",
  数量: "用于描述数量参数或数量输入。随机筛选时表示最多保留的目标数量；成长时表示永久增加的产出；额外产出时表示本次额外产出的魔力，可不连接；加速时表示减少的轮次间隔；消除时表示最多消除的目标数量，未连接时消除全部输入目标。",
  品质: "道具品质配置值。",
  倍率: "数值倍率。1 表示不改变，大于 1 提高，0 到 1 降低。",
  流程: "流程控制节点的运行方式，例如顺序、分支、并行、循环、延迟或停止。",
  类型: "变量的数据类型。运行器会按这个类型理解变量初始值和后续写入值。",
  初始值: "变量在图开始运行前的默认值。未被动作修改前会使用这个值。",
  取整方式: "将浮点数转换为整数的方式，用于把比例、除法或随机浮点结果接入整数端口。",
};

function Field({
  label,
  children,
  help,
  error,
}: {
  label: ReactNode;
  children: ReactNode;
  help?: ReactNode;
  error?: ReactNode;
}) {
  const resolvedHelp = help ?? (typeof label === "string" ? inspectorFieldHelp[label] : undefined);
  const labelNode = resolvedHelp ? (
    <Popover
      title={label}
      content={<div style={{ maxWidth: 280, lineHeight: 1.6 }}>{resolvedHelp}</div>}
    >
      <span style={{ cursor: "help", textDecoration: "underline dotted" }}>{label}</span>
    </Popover>
  ) : (
    label
  );

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ color: error ? "#cf1322" : "#64748b", fontSize: 12, marginBottom: 4 }}>{labelNode}</div>
      <InspectorFieldControl>{children}</InspectorFieldControl>
      {error && (
        <div style={{ color: "#cf1322", fontSize: 12, marginTop: 4, lineHeight: 1.4 }}>
          {error}
        </div>
      )}
    </div>
  );
}

function getDisplayNodeTitle(node: PropNode, configOptions?: PropGraphConfigOptions) {
  switch (node.type) {
    case "eventTurnStart":
      return joinTitleParts([
        getTurnStartTitle(node.data),
        node.data.loop ? `循环 ${node.data.loop} 次` : "",
      ]);
    case "eventAcquire":
      return "持有触发";
    case "eventUse":
      return "主动使用";
    case "eventEliminated":
      return "消除触发";
    case "targetSelf":
      return "当前道具";
    case "targetBoard":
      return getBoardMaterialTitle(node.data);
    case "prototypeLibrary":
      return joinTitleParts(["原型库", optionLabel(prototypeLibraryTypeOptions, node.data.propType)]);
    case "prototypeSelect":
      return joinTitleParts(["原型", getPropOptionLabel(configOptions?.props, node.data.prototypeId)]);
    case "targetFilter":
      return "条件筛选";
    case "filterRarity":
      return "品质筛选";
    case "filterTag":
      return "标签筛选";
    case "filterPrototype":
      return "原型筛选";
    case "filterRandom":
      return "随机筛选";
    case "actionGrow":
      return "增加魔力产出";
    case "actionExtraYield":
      return "额外产出魔力";
    case "actionPermanentBoost":
      return "永久加成";
    case "actionPurify":
      return "净化负面效果";
    case "actionProtect":
      return "保护目标";
    case "actionAccelerate":
      return "减少轮次间隔";
    case "actionTransform":
      return "转化为原型";
    case "actionSpawn":
      return "生成目标";
    case "actionEliminate":
      return "消除目标";
    case "actionCollect":
      return "收集魔力";
    case "actionHarvest":
      return "收获魔力";
    case "actionRarityWeight":
      return "调整品质权重";
    case "actionExtendCommission":
      return "延长调和";
    case "actionSwapPosition":
      return "调换位置";
    case "actionTriggerVisual":
      return "触发表现";
    case "intConstant":
      return joinTitleParts(["固定整数", formatDisplayValue(node.data.value)]);
    case "intAttribute":
      return joinTitleParts(["目标整数属性", optionLabel(targetAttributeOptions, node.data.attribute)]);
    case "intQuantity":
      return "目标数量";
    case "intKindCount":
      return "目标种类";
    case "intRandom":
      return "随机整数";
    case "intOperation":
      return joinTitleParts(["整数运算", optionLabel(integerOperationOptions, node.data.op)]);
    case "floatConstant":
      return joinTitleParts(["固定浮点数", formatDisplayValue(node.data.value)]);
    case "floatAttribute":
      return joinTitleParts(["目标浮点属性", optionLabel(targetAttributeOptions, node.data.attribute)]);
    case "floatRandom":
      return "随机浮点数";
    case "floatOperation":
      return joinTitleParts(["浮点运算", optionLabel(floatOperationOptions, node.data.op)]);
    case "roundNumber":
      return joinTitleParts(["取整", optionLabel(roundModeOptions, node.data.mode)]);
    case "rarityConstant":
      return joinTitleParts(["\u54c1\u8d28\u5e38\u91cf", getDictLabel(configOptions?.rarities, node.data.value)]);
    case "tagConstant":
      return joinTitleParts(["\u6807\u7b7e\u5e38\u91cf", getDictLabel(configOptions?.tags, node.data.value)]);
    case "logicCompareInt":
      return joinTitleParts(["整数比较", optionLabel(compareOpOptions, node.data.op)]);
    case "logicCompareFloat":
      return joinTitleParts(["浮点比较", optionLabel(compareOpOptions, node.data.op)]);
    case "logicContains":
      return "包含";
    case "logicProbability":
      return joinTitleParts(["概率", formatProbabilityDisplay(node.data.probability)]);
  }
}

function formatProbabilityDisplay(probability: number) {
  const value = Number(probability ?? 0);
  if (!Number.isFinite(value)) return "未设置";
  const percent = value * 100;
  return Number.isInteger(percent) ? `${percent}%` : `${Number(percent.toFixed(2))}%`;
}

function getTurnStartTitle(data: EventTurnStartNodeData) {
  const interval = Math.max(0, Number(data.interval ?? 0));
  if (interval === 0) return "每轮开始";
  return `每 ${interval + 1} 轮开始`;
}

function getBoardMaterialTitle(data: TargetBoardNodeData) {
  if (data.scope === "relative") return joinTitleParts([boardMaterialPositionLabels(relativePositionOptions, data.position), "盘面原料"]);
  if (data.scope === "absolute") return joinTitleParts([boardMaterialPositionLabels(absolutePositionOptions, data.position), "盘面原料"]);
  return "全部盘面原料";
}

function getDefaultBoardMaterialPosition(scope: BoardMaterialScope): BoardMaterialPosition | undefined {
  if (scope === "relative") return "adjacent";
  if (scope === "absolute") return "corner";
  return undefined;
}

function getDefaultBoardMaterialPositions(scope: BoardMaterialScope): BoardMaterialPosition[] {
  const position = getDefaultBoardMaterialPosition(scope);
  return position ? [position] : [];
}

function normalizeBoardMaterialPositionArray(position: TargetBoardNodeData["position"]): BoardMaterialPosition[] {
  return Array.isArray(position) ? position : position ? [position] : [];
}

function boardMaterialPositionLabels(
  options: Array<{ value: string | number; label: ReactNode }>,
  value: TargetBoardNodeData["position"],
) {
  const values = normalizeBoardMaterialPositionArray(value);
  return values.length ? values.map((item) => optionLabel(options, item)).join("、") : "未设置";
}

function getPropOptionLabel(props: PropGraph[] | undefined, propId: string | undefined) {
  if (!propId) return "未设置";
  const prop = props?.find((item) => item._id === propId);
  return prop?.name || prop?.assetId || propId;
}

function getEffectSelectOptions(effects: EffectConfig[], selectedIds: Array<string | undefined>): DescribedOption<string>[] {
  const options: DescribedOption<string>[] = effects.map((effect) => ({
    value: String(effect._id),
    label: getEffectOptionLabel(effect),
    description: effect.remark || effect.path || effect._id,
  }));
  const knownIds = new Set(options.map((option) => option.value));
  selectedIds
    .map((id) => String(id ?? ""))
    .filter((id) => id && !knownIds.has(id))
    .forEach((id) => {
      knownIds.add(id);
      options.push({
        value: id,
        label: `已删除特效：${id}`,
        description: "该特效配置不存在或已被删除，保存后运行时图编译会失败。",
      });
    });
  return options;
}

function getEffectOptionLabel(effect: EffectConfig) {
  return effect.name || effect.path || String(effect._id);
}

function normalizeEffectIds(value: unknown, legacyValue?: unknown): string[] {
  const rawValues = Array.isArray(value) ? value : value === undefined || value === null || value === "" ? [] : [value];
  if (!rawValues.length && legacyValue !== undefined && legacyValue !== null && legacyValue !== "") rawValues.push(legacyValue);
  const ids = rawValues.map((item) => String(item ?? "").trim()).filter(Boolean);
  return ids.filter((id, index) => ids.indexOf(id) === index);
}

function getEffectDisplayLabels(effects: EffectConfig[] | undefined, effectIds: unknown, legacyEffectId?: unknown) {
  const ids = normalizeEffectIds(effectIds, legacyEffectId);
  if (!ids.length) return "无";
  return ids.map((effectId) => {
    const effect = effects?.find((item) => String(item._id) === String(effectId));
    return effect ? getEffectOptionLabel(effect) : `已删除特效：${effectId}`;
  }).join("、");
}

function getAnimationSelectOptions(animations: AnimationConfig[], selectedIds: Array<string | undefined>): DescribedOption<string>[] {
  const options: DescribedOption<string>[] = animations.map((animation) => ({
    value: String(animation._id),
    label: getAnimationOptionLabel(animation),
    description: animation.remark || animation.path || animation._id,
  }));
  const knownIds = new Set(options.map((option) => option.value));
  selectedIds
    .map((id) => String(id ?? ""))
    .filter((id) => id && !knownIds.has(id))
    .forEach((id) => {
      knownIds.add(id);
      options.push({
        value: id,
        label: `已删除动画：${id}`,
        description: "该动画配置不存在或已被删除。",
      });
    });
  return options;
}

function getAnimationOptionLabel(animation: AnimationConfig) {
  return animation.name || animation.path || String(animation._id);
}

function normalizeAnimationIds(value: unknown): string[] {
  return normalizeEffectIds(value);
}

function getAnimationDisplayLabels(animations: AnimationConfig[] | undefined, animationIds: unknown) {
  const ids = normalizeAnimationIds(animationIds);
  if (!ids.length) return "无";
  return ids.map((animationId) => {
    const animation = animations?.find((item) => String(item._id) === String(animationId));
    return animation ? getAnimationOptionLabel(animation) : `已删除动画：${animationId}`;
  }).join("、");
}

function joinTitleParts(parts: Array<string | number | boolean | null | undefined>) {
  return parts
    .map((part) => (part === null || part === undefined ? "" : String(part).trim()))
    .filter((part) => part && part !== "未设置")
    .join(" · ");
}

function translateDefaultNodeTitle(title: string, type: PropNodeType) {
  return title || getNodeTypeLabel(type);
}

function getDisplayPortName(port: PropGraphPort) {
  const legacyNames: Record<string, string> = {
    Flow: "流程",
    Targets: "目标",
    Source: "来源目标",
    Value: "数值",
    True: "成立",
    False: "不成立",
    Target: "目标",
    Amount: "数量",
    Alt: "备用流程",
    A: "左值",
    B: "右值",
  };
  return legacyNames[port.name] ?? port.name;
}

function formatPortOption(port: PropGraphPort) {
  return `${getDisplayPortName(port)}（${getPortKindLabel(port.kind)}）`;
}

function formatPortDescription(
  node: PropNode,
  port: PropGraphPort,
  direction: PortDirection,
) {
  const detail = getSpecificPortDescription(node, port, direction);
  const directionLabel = direction === "input" ? "输入" : "输出";
  return `${getDisplayPortName(port)}（${directionLabel}）：${detail}`;
}

function getSpecificPortDescription(
  node: PropNode,
  port: PropGraphPort,
  direction: PortDirection,
) {
  const specific = getPropGraphPortSpecificDescription(node.type, port, direction);
  if (specific) return specific;

  if (direction === "input") {
    if (port.id === "flow") return "接收上游流程，收到后执行当前节点。";
    if (node.type === "eventEliminated" && port.id === "target") return "接收要监听的目标；该目标被消除时触发事件。";
    if (node.type === "logicContains" && port.id === "targets") return "接收要检查的目标数组。";
    if (node.type === "logicContains" && port.id === "target") return "接收要判断是否被包含的目标。";
    if (node.type === "actionSpawn" && port.id === "position") return "接收生成位置；实际按目标数组传递，由运行器解释为可放置位置。";
    if (node.type === "actionSpawn" && port.id === "targets") return "接收要生成的目标或道具原型。";
    if (node.type === "actionCollect" && port.id === "targets") return "接收被收集的目标；每个目标提供指定数量的可收获魔力，目标自身不会减少产出。";
    if (node.type === "actionHarvest" && port.id === "flow") return "接收使用流程，收到后释放当前道具已收集的魔力。";
    if (node.type === "actionRarityWeight" && port.id === "rarity") return "接收要调整抽取权重的品质。";
    if (node.type === "actionRarityWeight" && port.id === "multiplier") return "接收品质权重倍率，例如 1.5 表示该品质权重乘以 1.5。";
    if (node.type === "actionSwapPosition" && port.id === "positions") return "接收目标位置候选，运行时会与目标交换坐标。";
    if (port.id === "targets" || port.id === "target") return "接收一个或多个道具对象，运行时统一按数组处理。";
    if (port.id === "prototypes") return "接收一个或多个转化目标，用于决定当前目标要转化成什么道具。";
    if ((node.type === "intRandom" || node.type === "floatRandom") && port.id === "min") return "接收随机范围的最小值。";
    if ((node.type === "intRandom" || node.type === "floatRandom") && port.id === "max") return "接收随机范围的最大值。";
    if ((node.type === "intOperation" || node.type === "floatOperation") && port.id === "left") return "接收运算左侧的数值。";
    if ((node.type === "intOperation" || node.type === "floatOperation") && port.id === "right") return "接收运算右侧的数值。";
    if (node.type === "roundNumber" && port.id === "value") return "接收需要取整的浮点数。";
    if (node.type === "filterRandom" && port.id === "count") return "接收随机筛选要保留的目标数量。";
    if (node.type === "filterPrototype" && port.id === "prototype") return "接收一个或多个指定道具原型，用于保留同原型的目标。";
    if (node.type === "actionExtraYield" && port.id === "amount") return "接收可选整数数量，用于指定本次额外产出的魔力点数；不连接时仅使用倍率或运行器默认逻辑。";
    if (node.type === "actionExtraYield" && port.id === "multiplier") return "接收可选浮点倍率，用于按倍率调整本次额外产出；不连接时不应用倍率。";
    if (node.type === "actionPermanentBoost" && port.id === "amount") return "接收可选整数数量，用于指定目标此后的固定产出加成；不连接时仅使用倍率或运行器默认逻辑。";
    if (node.type === "actionPermanentBoost" && port.id === "multiplier") return "接收可选浮点倍率，用于指定目标此后的倍率产出加成；不连接时不应用倍率。";
    if (node.type === "actionEliminate" && port.id === "amount") return "接收可选整数数量，用于限制最多消除多少个目标；不连接时消除全部输入目标。";
    if (port.id === "amount") return "接收整数数量，用于成长、额外产出、净化、加速、生成或消除的数量。";
    if (port.id === "condition") return "接收一个或多个布尔条件；全部条件成立时当前节点才继续执行。";
    if (port.id === "left") return "接收比较左侧的数值。";
    if (port.id === "right") return "接收比较右侧的数值。";
    if (port.id === "rarity") return "接收一个或多个品质常量，用于筛选目标。";
    if (port.id === "tag") return "接收一个或多个标签常量，用于筛选目标。";
  }

  if (isActionNodeType(node.type) && port.id === "complete") return "动作成功触发且全部完成后，运行时调用该流程函数继续执行。";
  if (isActionNodeType(node.type) && port.id === "fail") return "动作未触发时，运行时调用该流程函数继续执行。";
  if (isActionNodeType(node.type) && port.id === "each") return "动作作用于多个目标时，每成功处理一个目标就调用一次该流程函数。";
  if (port.kind === "flow") return "输出流程函数；运行时在触发条件满足后调用，用于推进后续流程。";
  if (port.kind === "target") return "输出目标函数；下游输入求值时调用，返回道具目标数组或原型数组。";
  if (port.kind === "value") return `输出${getValueTypeLabel(port.valueType ?? "integer")}函数；下游输入求值时调用，返回对应数值。`;
  if (port.kind === "condition") return "输出条件函数；下游输入求值时调用，返回布尔值。";
  if (port.kind === "rarity") return "输出品质函数；下游输入求值时调用，返回品质配置值。";
  if (port.kind === "tag") return "输出标签函数；下游输入求值时调用，返回标签配置值。";

  if (node.type === "eventTurnStart" && port.id === "flow") return "回合开始事件满足间隔后，启动后续流程。";
  if (node.type === "eventAcquire" && port.id === "flow") return "持有本道具期间，每轮启动后续流程；道具被移除后停止。";
  if (node.type === "eventUse" && port.id === "flow") return "玩家主动使用本道具时，启动后续流程。";
  if (node.type === "eventEliminated" && port.id === "flow") return "输入目标被消除时，启动后续流程。";
  if (node.type === "targetSelf" && port.id === "target") return "输出当前道具对象，运行时按目标数组传递。";
  if (node.type === "targetBoard" && port.id === "targets") return "输出符合盘面原料范围设置的目标数组。";
  if (node.type === "prototypeLibrary" && port.id === "prototypes") return "按属性面板中的类型输出可用道具原型目标。";
  if (node.type === "prototypeSelect" && port.id === "prototypes") return "输出属性面板中选择的道具原型目标。";
  if (node.type === "targetFilter" && port.id === "targets") return "输出条件成立后保留的目标数组。";
  if (node.type === "filterRarity" && port.id === "targets") return "输出品质匹配后的目标数组。";
  if (node.type === "filterTag" && port.id === "targets") return "输出标签匹配后的目标数组。";
  if (node.type === "filterPrototype" && port.id === "targets") return "输出原型匹配后的目标数组。";
  if (node.type === "filterRandom" && port.id === "targets") return "输出随机筛选后保留的目标数组。";
  if (node.type === "rarityConstant" && port.id === "value") return "输出属性面板中选择的品质。";
  if (node.type === "tagConstant" && port.id === "value") return "输出属性面板中选择的标签。";
  if (node.type === "intQuantity" && port.id === "value") return "输出输入目标数组的数量。";
  if (node.type === "intKindCount" && port.id === "value") return "输出输入目标数组中不同道具种类的数量。";
  if ((node.type === "intRandom" || node.type === "floatRandom") && port.id === "value") return "输出最小值到最大值之间的随机结果。";
  if ((node.type === "intOperation" || node.type === "floatOperation") && port.id === "value") return "输出左右值运算后的结果。";
  if (node.type === "roundNumber" && port.id === "value") return "输出取整后的整数。";
  if (node.type === "logicContains" && port.id === "condition") return "目标数组包含指定目标时输出真，否则输出假。";
  if (node.type === "logicProbability" && port.id === "condition") return "按属性面板中的概率随机输出真或假。";
  if (isActionNodeType(node.type) && port.id === "complete") return "动作成功触发且全部完成后，执行该流程。";
  if (isActionNodeType(node.type) && port.id === "fail") return "动作未触发时，执行该流程。";
  if (isActionNodeType(node.type) && port.id === "each") return "动作作用于多个目标时，每成功触发一次就执行该流程。";
  if (port.kind === "value") return `输出${getValueTypeLabel(port.valueType ?? "integer")}值。`;
  if (port.kind === "condition") return "输出比较结果，成立为真，不成立为假。";
  if (port.kind === "flow") return "输出动作执行后的后续流程。";
  if (port.kind === "rarity") return "输出一个品质值。";
  if (port.kind === "tag") return "输出一个标签值。";
  return `${getPortKindLabel(port.kind)}端口。`;
}

function getPortKindLabel(kind: PropGraphPort["kind"]) {
  switch (kind) {
    case "flow":
      return "流程";
    case "value":
      return "数值";
    case "target":
      return "目标";
    case "condition":
      return "条件";
    case "rarity":
      return "品质";
    case "tag":
      return "标签";
  }
}

function getValueTypeLabel(type: PropGraphValueType) {
  switch (type) {
    case "flow":
      return "流程";
    case "propArray":
      return "道具数组";
    case "integer":
      return "整数";
    case "float":
      return "浮点数";
    case "boolean":
      return "布尔";
    case "probability":
      return "概率";
    case "multiplier":
      return "倍率";
    case "rarity":
      return "品质";
    case "tag":
      return "标签";
  }
}

function getNodePropertyRows(node: PropNode, configOptions?: PropGraphConfigOptions): NodeDisplayRow[] {
  const commonRows: NodeDisplayRow[] = [];

  switch (node.type) {
    case "eventTurnStart":
      return commonRows.concat([
        { label: "间隔", value: formatDisplayValue(node.data.interval) },
        { label: "循环", value: formatDisplayValue(node.data.loop) },
      ]);
    case "eventAcquire":
    case "eventUse":
    case "eventEliminated":
    case "targetSelf":
    case "targetFilter":
    case "filterRarity":
    case "filterTag":
    case "filterPrototype":
    case "filterRandom":
    case "intQuantity":
    case "intKindCount":
    case "intRandom":
    case "floatRandom":
      return commonRows;
    case "prototypeLibrary":
      return commonRows.concat([
        { label: "类型", value: optionLabel(prototypeLibraryTypeOptions, node.data.propType) },
      ]);
    case "intOperation":
      return commonRows.concat([
        { label: "运算", value: optionLabel(integerOperationOptions, node.data.op) },
      ]);
    case "floatOperation":
      return commonRows.concat([
        { label: "运算", value: optionLabel(floatOperationOptions, node.data.op) },
      ]);
    case "roundNumber":
      return commonRows.concat([
        { label: "取整方式", value: optionLabel(roundModeOptions, node.data.mode) },
      ]);
    case "actionGrow":
    case "actionExtraYield":
    case "actionPermanentBoost":
    case "actionPurify":
    case "actionProtect":
    case "actionAccelerate":
    case "actionTransform":
    case "actionSpawn":
    case "actionEliminate":
    case "actionCollect":
    case "actionHarvest":
    case "actionRarityWeight":
    case "actionExtendCommission":
    case "actionSwapPosition":
    case "actionTriggerVisual":
      return commonRows.concat(getActionEffectRows(node.data, configOptions));
    case "targetBoard":
      return commonRows.concat([
        { label: "范围", value: optionLabel(boardMaterialScopeOptions, node.data.scope) },
        ...(node.data.scope !== "all" ? [{ label: "位置", value: boardMaterialPositionLabels(node.data.scope === "absolute" ? absolutePositionOptions : relativePositionOptions, node.data.position) }] : []),
        { label: "包含空位置", value: node.data.includeEmpty ? "是" : "否" },
      ]);
    case "prototypeSelect":
      return commonRows.concat([
        { label: "道具原型", value: getPropOptionLabel(configOptions?.props, node.data.prototypeId) },
      ]);
    case "intConstant":
    case "floatConstant":
      return commonRows.concat([
        { label: "\u503c", value: formatDisplayValue(node.data.value) },
      ]);
    case "rarityConstant":
      return commonRows.concat([
        { label: "\u54c1\u8d28", value: getDictLabel(configOptions?.rarities, node.data.value) },
      ]);
    case "tagConstant":
      return commonRows.concat([
        { label: "\u6807\u7b7e", value: getDictLabel(configOptions?.tags, node.data.value) },
      ]);
    case "intAttribute":
    case "floatAttribute":
      return commonRows.concat([
        { label: "属性", value: optionLabel(targetAttributeOptions, node.data.attribute) },
      ]);
    case "logicCompareInt":
    case "logicCompareFloat":
      return commonRows.concat([
        { label: "比较", value: optionLabel(compareOpOptions, node.data.op) },
      ]);
    case "logicContains":
      return commonRows;
    case "logicProbability":
      return commonRows.concat([
        { label: "概率", value: formatProbabilityDisplay(node.data.probability) },
      ]);
  }
}

function getActionEffectRows(data: ActionEffectNodeData | ActionPermanentBoostNodeData, configOptions?: PropGraphConfigOptions): NodeDisplayRow[] {
  const rows: NodeDisplayRow[] = [
    { label: "负面", value: formatDisplayValue(data.negative ?? false) },
    { label: "自身特效", value: getEffectDisplayLabels(configOptions?.effects, data.selfEffects, data.selfEffectId) },
    { label: "目标特效", value: getEffectDisplayLabels(configOptions?.effects, data.targetEffects, data.targetEffectId) },
  ];
  rows.push(
    { label: "自身动画", value: getAnimationDisplayLabels(configOptions?.animations, data.selfAnimations) },
    { label: "目标动画", value: getAnimationDisplayLabels(configOptions?.animations, data.targetAnimations) },
  );
  if ("mutexTag" in data) rows.push({ label: "互斥标签", value: formatDisplayValue((data as ActionPermanentBoostNodeData).mutexTag) });
  return rows;
}

function formatDisplayValue(value: unknown): string {
  if (value === undefined || value === null || value === "") return "未设置";
  if (Array.isArray(value)) {
    return value.length ? value.map(formatDisplayValue).join("，") : "空";
  }
  if (typeof value === "boolean") return value ? "是" : "否";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function optionLabel(
  options: Array<{ value: string | number; label: ReactNode }>,
  value: unknown,
): string {
  if (value === undefined || value === null || value === "") return "未设置";
  const option = options.find((item) => item.value === value);
  return typeof option?.label === "string" ? option.label : String(value);
}

function getDictLabel(options: DictItem[] | undefined, value: unknown): string {
  if (value === undefined || value === null || value === "") return "未设置";
  const option = options?.find((item) => Number(item.value) === Number(value));
  return option?.label ?? String(value);
}

function renderDescribedOption(option: {
  label?: ReactNode;
  data?: { label?: ReactNode; description?: ReactNode };
}) {
  const data = (option.data ?? option) as { label?: ReactNode; description?: ReactNode };
  const label = data.label ?? option.label;
  if (!data.description) return label;

  return (
    <Tooltip
      title={<DescriptionText text={data.description} compact />}
      placement="right"
      overlayStyle={{ maxWidth: 420 }}
      mouseEnterDelay={0.35}
    >
      <div style={{ padding: "4px 0", lineHeight: 1.35, minWidth: 0 }}>
        <div
          style={{
            color: "#0f172a",
            fontSize: 13,
            lineHeight: "20px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </div>
        <div
          style={{
            color: "#64748b",
            fontSize: 12,
            lineHeight: "18px",
            marginTop: 2,
            whiteSpace: "normal",
            display: "block",
            maxHeight: 36,
            overflow: "hidden",
            wordBreak: "break-word",
          }}
        >
          <DescriptionText text={data.description} compact />
        </div>
      </div>
    </Tooltip>
  );
}

function findPortInfo(nodes: PropNode[], reference: PortReference) {
  const node = nodes.find((item) => item.id === reference.nodeId);
  if (!node) return null;
  const portDefinition = getPropNodePorts(node);
  const ports = reference.direction === "input" ? portDefinition.inputs : portDefinition.outputs;
  const port = ports.find((item) => item.id === reference.portId);
  if (!port) return null;
  return { node, port };
}

function isSamePortReference(
  a: PortReference | null | undefined,
  b: PortReference | null | undefined,
) {
  return Boolean(
    a &&
      b &&
      a.nodeId === b.nodeId &&
      a.portId === b.portId &&
      a.direction === b.direction,
  );
}

function canConnectPortReferences(
  nodes: PropNode[],
  start: PortReference,
  target: PortReference,
) {
  if (isSamePortReference(start, target)) return false;
  if (start.nodeId === target.nodeId) return false;

  const output = start.direction === "output" ? start : target;
  const input = start.direction === "input" ? start : target;
  if (output.direction !== "output" || input.direction !== "input") return false;

  const outputInfo = findPortInfo(nodes, output);
  const inputInfo = findPortInfo(nodes, input);
  if (!outputInfo || !inputInfo) return false;
  return isCompatibleConnection(outputInfo.port, inputInfo.port);
}

function createAutoConnectionEdge(
  selectedNode: PropNode,
  newNode: PropNode,
  nodes: PropNode[],
  existingEdges: PropEdge[],
) {
  const forward = findAutoConnectionCandidate(selectedNode, newNode, nodes);
  const backward = forward ? null : findAutoConnectionCandidate(newNode, selectedNode, nodes);
  const candidate = forward ?? backward;
  if (!candidate) return null;
  const duplicated = existingEdges.some(
    (edge) =>
      edge.fromNodeId === candidate.output.nodeId &&
      edge.fromPortId === candidate.output.portId &&
      edge.toNodeId === candidate.input.nodeId &&
      edge.toPortId === candidate.input.portId,
  );
  if (duplicated) return null;
  return {
    inputPort: candidate.inputPort,
    edge: {
      id: createPropGraphId("edge"),
      kind: candidate.outputPort.kind,
      fromNodeId: candidate.output.nodeId,
      fromPortId: candidate.output.portId,
      toNodeId: candidate.input.nodeId,
      toPortId: candidate.input.portId,
    } as PropEdge,
  };
}

function findAutoConnectionCandidate(
  sourceNode: PropNode,
  targetNode: PropNode,
  nodes: PropNode[],
) {
  const sourcePorts = getPropNodePorts(sourceNode).outputs;
  const targetPorts = getPropNodePorts(targetNode).inputs;
  return sourcePorts
    .flatMap((outputPort) =>
      targetPorts
        .filter((inputPort) => isCompatibleConnection(outputPort, inputPort))
        .map((inputPort) => ({
          output: { nodeId: sourceNode.id, portId: outputPort.id, direction: "output" as const },
          input: { nodeId: targetNode.id, portId: inputPort.id, direction: "input" as const },
          outputPort,
          inputPort,
          score: getAutoConnectionPortScore(outputPort.id, inputPort.id, nodes),
        })),
    )
    .sort((a, b) => a.score - b.score)[0];
}

function getAutoConnectionPortScore(outputPortId: string, inputPortId: string, nodes: PropNode[]) {
  return getAutoConnectionOutputPortRank(outputPortId) * 100 +
    getAutoConnectionInputPortRank(inputPortId) +
    Math.min(nodes.length, 99) * 0.0001;
}

function getAutoConnectionOutputPortRank(portId: string) {
  if (portId === "flow") return 0;
  if (portId === "complete") return 1;
  if (portId === "target" || portId === "targets") return 2;
  if (portId === "value") return 3;
  if (portId === "condition") return 4;
  return 10;
}

function getAutoConnectionInputPortRank(portId: string) {
  if (portId === "flow") return 0;
  if (portId === "targets" || portId === "target") return 1;
  if (portId === "amount") return 2;
  if (portId === "condition") return 3;
  if (portId === "tag" || portId === "rarity") return 4;
  return 10;
}

function getPortAnchor(
  node: PropNode,
  editorState: PropGraphNodeEditorState,
  direction: PortDirection,
  portId: string,
): WorldPoint {
  const portDefinition = getPropNodePorts(node);
  const ports = direction === "input" ? portDefinition.inputs : portDefinition.outputs;
  const index = Math.max(
    0,
    ports.findIndex((port) => port.id === portId),
  );
  return {
    x:
      editorState.position.x +
      (direction === "input" ? PORT_HIT_SIZE / 2 : NODE_WIDTH - PORT_HIT_SIZE / 2),
    y:
      editorState.position.y +
      NODE_HEADER_HEIGHT +
      NODE_BODY_PADDING +
      index * PORT_ROW_HEIGHT +
      PORT_ROW_HEIGHT / 2,
  };
}

function buildEdgePath(from: WorldPoint, to: WorldPoint) {
  const direction = from.x <= to.x ? 1 : -1;
  const offset = Math.max(80, Math.abs(to.x - from.x) / 2);
  return `M ${from.x} ${from.y} C ${from.x + direction * offset} ${from.y}, ${to.x - direction * offset} ${to.y}, ${to.x} ${to.y}`;
}

function isCompatibleConnection(output: PropGraphPort, input: PropGraphPort) {
  if (output.kind !== input.kind) return false;
  if (output.kind === "value" && output.valueType === "integer" && input.valueType === "float") {
    return true;
  }
  if (output.valueType && input.valueType && output.valueType !== input.valueType) {
    return false;
  }
  return true;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function clampNodeCreateMenuPosition(x: number, y: number) {
  const maxX = Math.max(
    NODE_CREATE_MENU_VIEWPORT_GAP,
    window.innerWidth - NODE_CREATE_MENU_WIDTH - NODE_CREATE_MENU_VIEWPORT_GAP,
  );
  const maxY = Math.max(
    NODE_CREATE_MENU_VIEWPORT_GAP,
    window.innerHeight - NODE_CREATE_MENU_MAX_HEIGHT - NODE_CREATE_MENU_VIEWPORT_GAP,
  );
  return {
    x: clamp(x, NODE_CREATE_MENU_VIEWPORT_GAP, maxX),
    y: clamp(y, NODE_CREATE_MENU_VIEWPORT_GAP, maxY),
  };
}

function clampKeyboardPalettePosition(x: number, y: number) {
  const maxX = Math.max(
    NODE_CREATE_MENU_VIEWPORT_GAP,
    window.innerWidth - KEYBOARD_PALETTE_WIDTH - NODE_CREATE_MENU_VIEWPORT_GAP,
  );
  const maxY = Math.max(
    NODE_CREATE_MENU_VIEWPORT_GAP,
    window.innerHeight - KEYBOARD_PALETTE_MAX_HEIGHT - NODE_CREATE_MENU_VIEWPORT_GAP,
  );
  return {
    x: clamp(x, NODE_CREATE_MENU_VIEWPORT_GAP, maxX),
    y: clamp(y, NODE_CREATE_MENU_VIEWPORT_GAP, maxY),
  };
}

function isEditableElement(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return Boolean(target.closest("input, textarea, [contenteditable='true'], .ant-select"));
}

function arrangeEditorNodes(
  nodes: PropNode[],
  edges: PropEdge[],
  editorNodes: PropGraphNodeEditorState[],
  entryNodeIds: string[],
): PropGraphNodeEditorState[] {
  const existingEditorMap = new Map(editorNodes.map((item) => [item.nodeId, item]));
  const nodeIndex = new Map(nodes.map((node, index) => [node.id, index]));
  const entrySet = new Set(entryNodeIds);
  const connectedGroups = createConnectedNodeGroups(nodes, edges).sort((a, b) => {
    const aRank = getAutoLayoutGroupRank(a, entrySet);
    const bRank = getAutoLayoutGroupRank(b, entrySet);
    if (aRank !== bRank) return aRank - bRank;
    const aIndex = Math.min(...a.map((node) => nodeIndex.get(node.id) ?? 0));
    const bIndex = Math.min(...b.map((node) => nodeIndex.get(node.id) ?? 0));
    return aIndex - bIndex;
  });
  const positions = new Map<string, PropGraphPosition>();
  let cursorY = 0;

  connectedGroups.forEach((groupNodes) => {
    const groupNodeIds = new Set(groupNodes.map((node) => node.id));
    const groupEdges = edges.filter(
      (edge) => groupNodeIds.has(edge.fromNodeId) && groupNodeIds.has(edge.toNodeId),
    );
    const layout = arrangeConnectedNodeGroup(
      groupNodes,
      groupEdges,
      entryNodeIds,
    );

    layout.positions.forEach((position, nodeId) => {
      positions.set(nodeId, {
        x: position.x,
        y: position.y + cursorY,
      });
    });
    cursorY += layout.height + AUTO_LAYOUT_GROUP_ROW_GAP;
  });

  return nodes.map((node, index) => {
    const fallback = createDefaultNodeEditorState(node, index);
    const current = existingEditorMap.get(node.id) ?? fallback;
    return {
      ...current,
      nodeId: node.id,
      title: current.title || fallback.title,
      position: positions.get(node.id) ?? fallback.position,
    };
  });
}

function createConnectedNodeGroups(nodes: PropNode[], edges: PropEdge[]) {
  const nodeIds = new Set(nodes.map((node) => node.id));
  const adjacency = new Map(nodes.map((node) => [node.id, new Set<string>()]));
  edges.forEach((edge) => {
    if (!nodeIds.has(edge.fromNodeId) || !nodeIds.has(edge.toNodeId)) return;
    adjacency.get(edge.fromNodeId)?.add(edge.toNodeId);
    adjacency.get(edge.toNodeId)?.add(edge.fromNodeId);
  });

  const visited = new Set<string>();
  const groups: PropNode[][] = [];
  nodes.forEach((node) => {
    if (visited.has(node.id)) return;
    const stack = [node.id];
    const groupIds: string[] = [];
    visited.add(node.id);

    while (stack.length) {
      const currentId = stack.pop()!;
      groupIds.push(currentId);
      adjacency.get(currentId)?.forEach((nextId) => {
        if (visited.has(nextId)) return;
        visited.add(nextId);
        stack.push(nextId);
      });
    }

    const groupIdSet = new Set(groupIds);
    groups.push(nodes.filter((item) => groupIdSet.has(item.id)));
  });
  return groups;
}

function getAutoLayoutGroupRank(nodes: PropNode[], entrySet: Set<string>) {
  if (nodes.some((node) => entrySet.has(node.id))) return 0;
  if (nodes.some((node) => isEventNodeType(node.type))) return 1;
  if (nodes.length > 1) return 2;
  return 3;
}

function arrangeConnectedNodeGroup(
  nodes: PropNode[],
  edges: PropEdge[],
  entryNodeIds: string[],
) {
  const actionCenteredLayout = arrangeActionCenteredNodeGroup(nodes, edges, entryNodeIds);
  if (actionCenteredLayout) return actionCenteredLayout;

  const auxiliaryNodes = nodes.filter((node) => isAutoLayoutAuxiliaryNode(node, edges));
  const auxiliaryNodeIds = new Set(auxiliaryNodes.map((node) => node.id));
  const coreNodes = nodes.filter((node) => !auxiliaryNodeIds.has(node.id));

  if (!coreNodes.length) {
    return arrangeCoreNodeColumns(nodes, edges, entryNodeIds);
  }

  const coreNodeIds = new Set(coreNodes.map((node) => node.id));
  const coreLayout = arrangeCoreNodeColumns(
    coreNodes,
    edges.filter(
      (edge) => coreNodeIds.has(edge.fromNodeId) && coreNodeIds.has(edge.toNodeId),
    ),
    entryNodeIds,
  );
  const positions = new Map(coreLayout.positions);
  expandCoreLayoutForAuxiliaryBridges(auxiliaryNodes, edges, positions, nodes);
  const occupied = coreNodes.map((node) =>
    createLayoutRect(node, positions.get(node.id) ?? { x: 0, y: 0 }),
  );
  const anchorGroups = new Map<
    string,
    { anchor: AuxiliaryAnchor; nodes: PropNode[] }
  >();

  auxiliaryNodes.forEach((node) => {
    const anchor = findAuxiliaryAnchor(node.id, edges, coreNodeIds) ?? {
      nodeId: coreNodes[0].id,
      role: "outputTarget" as const,
    };
    const key = `${anchor.role}:${anchor.nodeId}`;
    const current = anchorGroups.get(key);
    anchorGroups.set(key, {
      anchor,
      nodes: (current?.nodes ?? []).concat(node),
    });
  });

  anchorGroups.forEach(({ anchor, nodes: group }) => {
    const anchorId = anchor.nodeId;
    const anchorNode = coreNodes.find((node) => node.id === anchorId);
    const anchorPosition = positions.get(anchorId);
    if (!anchorNode || !anchorPosition) return;

    group
      .sort((a, b) => {
        return getDisplayNodeTitle(a).localeCompare(getDisplayNodeTitle(b));
      })
      .forEach((node) => {
        const position = findNearestAuxiliaryPosition(
          node,
          anchorNode,
          anchorPosition,
          occupied,
          edges,
          positions,
          nodes,
          anchor.role,
        );
        positions.set(node.id, position);
        occupied.push(createLayoutRect(node, position));
      });
  });

  return normalizeAutoLayoutPositions(positions, nodes);
}

function arrangeActionCenteredNodeGroup(
  nodes: PropNode[],
  edges: PropEdge[],
  entryNodeIds: string[],
) {
  const actionNodes = orderActionNodesForLayout(
    nodes.filter((node) => isActionNodeType(node.type)),
    edges,
    entryNodeIds,
  );
  if (!actionNodes.length) return null;

  const nodeMap = new Map(nodes.map((node) => [node.id, node]));
  const positions = new Map<string, PropGraphPosition>();
  const occupied: Array<{ nodeId: string; rect: LayoutRect }> = [];
  const columnGap = NODE_WIDTH + 64;
  const clusterGap = AUTO_LAYOUT_GROUP_ROW_GAP;
  let cursorY = 0;

  actionNodes.forEach((actionNode) => {
    const beforeClusterNodeIds = new Set(positions.keys());
    const flowInputAction = findPositionedFlowInputAction(
      actionNode.id,
      edges,
      nodeMap,
      positions,
    );
    const preferredActionPosition = flowInputAction
      ? {
          x: flowInputAction.position.x + columnGap,
          y: flowInputAction.position.y,
        }
      : { x: columnGap * 2, y: cursorY };
    const actionPosition = reserveActionLayoutNodePosition(
      actionNode,
      preferredActionPosition,
      positions,
      occupied,
    );

    placeActionInputSourceTree(
      actionNode,
      actionPosition,
      "flow",
      actionPosition.x - columnGap * 2,
      nodeMap,
      edges,
      positions,
      occupied,
      0,
    );
    placeActionInputSourceTree(
      actionNode,
      actionPosition,
      "condition",
      actionPosition.x - columnGap,
      nodeMap,
      edges,
      positions,
      occupied,
      0,
    );
    placeActionInputSourceTree(
      actionNode,
      actionPosition,
      "targets",
      actionPosition.x - columnGap,
      nodeMap,
      edges,
      positions,
      occupied,
      0,
    );
    placeActionInputSourceTree(
      actionNode,
      actionPosition,
      "amount",
      actionPosition.x - columnGap,
      nodeMap,
      edges,
      positions,
      occupied,
      0,
    );
    placeActionInputSourceTree(
      actionNode,
      actionPosition,
      "multiplier",
      actionPosition.x - columnGap,
      nodeMap,
      edges,
      positions,
      occupied,
      0,
    );
    placeActionInputSourceTree(
      actionNode,
      actionPosition,
      "prototypes",
      actionPosition.x - columnGap,
      nodeMap,
      edges,
      positions,
      occupied,
      0,
    );

    cursorY = Math.max(
      cursorY + estimateNodeHeight(actionNode) + clusterGap,
      getActionLayoutClusterBottom(nodes, positions, beforeClusterNodeIds) + clusterGap,
    );
  });

  const remainingNodes = nodes.filter((node) => !positions.has(node.id));
  if (remainingNodes.length) {
    const remainingNodeIds = new Set(remainingNodes.map((node) => node.id));
    const remainingLayout = arrangeCoreNodeColumns(
      remainingNodes,
      edges.filter(
        (edge) => remainingNodeIds.has(edge.fromNodeId) && remainingNodeIds.has(edge.toNodeId),
      ),
      entryNodeIds,
    );
    remainingLayout.positions.forEach((position, nodeId) => {
      const node = nodeMap.get(nodeId);
      if (!node) return;
      reserveActionLayoutNodePosition(
        node,
        { x: position.x, y: position.y + cursorY },
        positions,
        occupied,
      );
    });
  }

  return normalizeAutoLayoutPositions(positions, nodes);
}

function orderActionNodesForLayout(
  actionNodes: PropNode[],
  edges: PropEdge[],
  entryNodeIds: string[],
) {
  const originalOrder = new Map(actionNodes.map((node, index) => [node.id, index]));
  const flowDepths = getFlowLayoutDepths(edges, entryNodeIds);
  return actionNodes.slice().sort((a, b) => {
    const depthDiff = (flowDepths.get(a.id) ?? 9999) - (flowDepths.get(b.id) ?? 9999);
    if (depthDiff !== 0) return depthDiff;
    return (originalOrder.get(a.id) ?? 0) - (originalOrder.get(b.id) ?? 0);
  });
}

function getFlowLayoutDepths(edges: PropEdge[], entryNodeIds: string[]) {
  const outgoing = new Map<string, string[]>();
  edges
    .filter((edge) => edge.kind === "flow")
    .forEach((edge) => {
      outgoing.set(edge.fromNodeId, (outgoing.get(edge.fromNodeId) ?? []).concat(edge.toNodeId));
    });

  const depth = new Map<string, number>();
  const queue = entryNodeIds.map((nodeId) => ({ nodeId, depth: 0 }));
  queue.forEach((item) => depth.set(item.nodeId, item.depth));

  for (let cursor = 0; cursor < queue.length; cursor += 1) {
    const current = queue[cursor];
    (outgoing.get(current.nodeId) ?? []).forEach((nextNodeId) => {
      const nextDepth = current.depth + 1;
      const knownDepth = depth.get(nextNodeId);
      if (knownDepth !== undefined && knownDepth <= nextDepth) return;
      depth.set(nextNodeId, nextDepth);
      queue.push({ nodeId: nextNodeId, depth: nextDepth });
    });
  }

  return depth;
}

function findPositionedFlowInputAction(
  nodeId: string,
  edges: PropEdge[],
  nodeMap: Map<string, PropNode>,
  positions: Map<string, PropGraphPosition>,
) {
  const incomingActionEdges = getIncomingPortEdges(edges, nodeId, "flow").filter((edge) => {
    const sourceNode = nodeMap.get(edge.fromNodeId);
    return sourceNode && isActionNodeType(sourceNode.type) && positions.has(sourceNode.id);
  });
  const edge = incomingActionEdges[0];
  if (!edge) return null;

  const node = nodeMap.get(edge.fromNodeId);
  const position = positions.get(edge.fromNodeId);
  if (!node || !position) return null;
  return { node, position };
}

function placeActionInputSourceTree(
  targetNode: PropNode,
  targetPosition: PropGraphPosition,
  inputPortId: string,
  sourceX: number,
  nodeMap: Map<string, PropNode>,
  edges: PropEdge[],
  positions: Map<string, PropGraphPosition>,
  occupied: Array<{ nodeId: string; rect: LayoutRect }>,
  depth: number,
) {
  if (depth > 8) return;

  getIncomingPortEdges(edges, targetNode.id, inputPortId).forEach((edge, index) => {
    const sourceNode = nodeMap.get(edge.fromNodeId);
    if (!sourceNode) return;

    const targetAnchor = getAutoLayoutPortAnchor(
      targetNode,
      targetPosition,
      "input",
      inputPortId,
    );
    const sourceAnchorOffset = getAutoLayoutPortAnchor(
      sourceNode,
      { x: 0, y: 0 },
      "output",
      edge.fromPortId,
    );
    const preferredPosition = {
      x: sourceX,
      y: targetAnchor.y - sourceAnchorOffset.y + index * (estimateNodeHeight(sourceNode) + 18),
    };
    const sourcePosition = reserveActionLayoutNodePosition(
      sourceNode,
      preferredPosition,
      positions,
      occupied,
    );

    getActionLayoutInputPorts(sourceNode).forEach((port) => {
      placeActionInputSourceTree(
        sourceNode,
        sourcePosition,
        port.id,
        sourcePosition.x - (NODE_WIDTH + 64),
        nodeMap,
        edges,
        positions,
        occupied,
        depth + 1,
      );
    });
  });
}

function getActionLayoutInputPorts(node: PropNode) {
  return getPropNodePorts(node).inputs.filter((port) => port.kind !== "flow");
}

function getIncomingPortEdges(edges: PropEdge[], nodeId: string, portId: string) {
  return edges
    .filter((edge) => edge.toNodeId === nodeId && edge.toPortId === portId)
    .sort((a, b) => {
      const kindDiff = getActionLayoutEdgeKindPriority(a.kind) - getActionLayoutEdgeKindPriority(b.kind);
      if (kindDiff !== 0) return kindDiff;
      return (a.order ?? 0) - (b.order ?? 0);
    });
}

function getActionLayoutEdgeKindPriority(kind: PropEdge["kind"]) {
  switch (kind) {
    case "flow":
      return 0;
    case "target":
      return 1;
    case "value":
      return 2;
    case "rarity":
    case "tag":
      return 2;
    case "condition":
      return 3;
  }
  return 99;
}

function reserveActionLayoutNodePosition(
  node: PropNode,
  preferredPosition: PropGraphPosition,
  positions: Map<string, PropGraphPosition>,
  occupied: Array<{ nodeId: string; rect: LayoutRect }>,
) {
  const currentPosition = positions.get(node.id);
  if (currentPosition) return currentPosition;

  const position = findAvailableActionLayoutPosition(
    node,
    preferredPosition,
    occupied.map((item) => item.rect),
  );
  positions.set(node.id, position);
  occupied.push({ nodeId: node.id, rect: createLayoutRect(node, position) });
  return position;
}

function findAvailableActionLayoutPosition(
  node: PropNode,
  preferredPosition: PropGraphPosition,
  occupied: LayoutRect[],
) {
  const step = estimateNodeHeight(node) + 16;
  const candidates: PropGraphPosition[] = [];

  for (let layer = 0; layer <= 24; layer += 1) {
    const offset = layer * step;
    candidates.push({ x: preferredPosition.x, y: preferredPosition.y + offset });
  }

  return (
    candidates.find((position) => !doesActionLayoutRectOverlap(createLayoutRect(node, position), occupied)) ??
    preferredPosition
  );
}

function doesActionLayoutRectOverlap(rect: LayoutRect, occupied: LayoutRect[]) {
  const padding = 12;
  return occupied.some(
    (item) =>
      rect.x < item.x + item.width + padding &&
      rect.x + rect.width + padding > item.x &&
      rect.y < item.y + item.height + padding &&
      rect.y + rect.height + padding > item.y,
  );
}

function getActionLayoutClusterBottom(
  nodes: PropNode[],
  positions: Map<string, PropGraphPosition>,
  beforeClusterNodeIds: Set<string>,
) {
  return nodes.reduce((bottom, node) => {
    if (beforeClusterNodeIds.has(node.id)) return bottom;
    const position = positions.get(node.id);
    if (!position) return bottom;
    return Math.max(bottom, position.y + estimateNodeHeight(node));
  }, 0);
}

function arrangeCoreNodeColumns(
  nodes: PropNode[],
  edges: PropEdge[],
  entryNodeIds: string[],
) {
  const nodeIds = new Set(nodes.map((node) => node.id));
  const incomingCount = new Map(nodes.map((node) => [node.id, 0]));
  const outgoing = new Map<string, string[]>();

  edges.forEach((edge) => {
    if (!nodeIds.has(edge.fromNodeId) || !nodeIds.has(edge.toNodeId)) return;
    incomingCount.set(edge.toNodeId, (incomingCount.get(edge.toNodeId) ?? 0) + 1);
    outgoing.set(edge.fromNodeId, (outgoing.get(edge.fromNodeId) ?? []).concat(edge.toNodeId));
  });

  const entrySet = new Set(entryNodeIds);
  const roots = nodes.filter(
    (node) =>
      entrySet.has(node.id) ||
      isEventNodeType(node.type) ||
      (incomingCount.get(node.id) ?? 0) === 0,
  );
  const depth = new Map<string, number>();
  const queue = roots.length ? roots.map((node) => node.id) : nodes.slice(0, 1).map((node) => node.id);
  queue.forEach((id) => depth.set(id, 0));

  const maxDepth = Math.max(nodes.length, 1);
  for (let cursor = 0; cursor < queue.length; cursor += 1) {
    const currentId = queue[cursor];
    const currentDepth = depth.get(currentId) ?? 0;
    const nextDepth = Math.min(currentDepth + 1, maxDepth);
    (outgoing.get(currentId) ?? []).forEach((nextId) => {
      const knownDepth = depth.get(nextId);
      if (knownDepth !== undefined && knownDepth >= nextDepth) return;
      depth.set(nextId, nextDepth);
      if (nextDepth < maxDepth) queue.push(nextId);
    });
  }

  const fallbackDepth = Math.max(0, ...Array.from(depth.values())) + 1;
  nodes.forEach((node, index) => {
    if (!depth.has(node.id)) depth.set(node.id, fallbackDepth + Math.floor(index / 5));
  });

  const typeOrder: Record<PropNodeType, number> = {
    eventTurnStart: 0,
    eventAcquire: 0,
    eventUse: 0,
    eventEliminated: 0,
    targetSelf: 1,
    targetBoard: 1,
    prototypeLibrary: 1,
    prototypeSelect: 1,
    targetFilter: 2,
    filterRarity: 2,
    filterTag: 2,
    filterPrototype: 2,
    filterRandom: 2,
    intConstant: 2,
    intAttribute: 2,
    intQuantity: 2,
    intKindCount: 2,
    intRandom: 2,
    intOperation: 2,
    floatConstant: 2,
    floatAttribute: 2,
    floatRandom: 2,
    floatOperation: 2,
    roundNumber: 2,
    rarityConstant: 2,
    tagConstant: 2,
    logicCompareInt: 3,
    logicCompareFloat: 3,
    logicContains: 3,
    logicProbability: 3,
    actionGrow: 5,
    actionExtraYield: 5,
    actionPermanentBoost: 5,
    actionPurify: 5,
    actionProtect: 5,
    actionAccelerate: 5,
    actionTransform: 5,
    actionSpawn: 5,
    actionEliminate: 5,
    actionCollect: 5,
    actionHarvest: 5,
    actionRarityWeight: 5,
    actionExtendCommission: 5,
    actionSwapPosition: 5,
    actionTriggerVisual: 5,
  };
  const groups = new Map<number, PropNode[]>();
  nodes.forEach((node) => {
    const column = depth.get(node.id) ?? 0;
    groups.set(column, (groups.get(column) ?? []).concat(node));
  });
  groups.forEach((group) => {
    group.sort((a, b) => {
      const typeDiff = typeOrder[a.type] - typeOrder[b.type];
      if (typeDiff !== 0) return typeDiff;
      return getDisplayNodeTitle(a).localeCompare(getDisplayNodeTitle(b));
    });
  });
  optimizeAutoLayoutColumnOrder(groups, edges, typeOrder);

  const columnGap = NODE_WIDTH + AUTO_LAYOUT_COLUMN_GAP;
  const positions = new Map<string, PropGraphPosition>();
  let width = NODE_WIDTH;
  let height = NODE_HEIGHT;
  Array.from(groups.keys())
    .sort((a, b) => a - b)
    .forEach((column) => {
      const group = groups.get(column) ?? [];
      let y = 0;
      group.forEach((node) => {
        positions.set(node.id, {
          x: column * columnGap,
          y,
        });
        y += estimateNodeHeight(node) + AUTO_LAYOUT_ROW_GAP;
      });
      width = Math.max(width, column * columnGap + NODE_WIDTH);
      height = Math.max(height, y > 0 ? y - AUTO_LAYOUT_ROW_GAP : NODE_HEIGHT);
    });
  return { positions, width, height };
}

function optimizeAutoLayoutColumnOrder(
  groups: Map<number, PropNode[]>,
  edges: PropEdge[],
  typeOrder: Record<PropNodeType, number>,
) {
  const columns = Array.from(groups.keys()).sort((a, b) => a - b);
  const fallbackCompare = (a: PropNode, b: PropNode) => {
    const typeDiff = typeOrder[a.type] - typeOrder[b.type];
    if (typeDiff !== 0) return typeDiff;
    return getDisplayNodeTitle(a).localeCompare(getDisplayNodeTitle(b));
  };

  for (let pass = 0; pass < 4; pass += 1) {
    for (let index = 1; index < columns.length; index += 1) {
      sortColumnByNeighbor(groups, edges, columns[index], columns[index - 1], fallbackCompare);
    }
    for (let index = columns.length - 2; index >= 0; index -= 1) {
      sortColumnByNeighbor(groups, edges, columns[index], columns[index + 1], fallbackCompare);
    }
  }
}

function sortColumnByNeighbor(
  groups: Map<number, PropNode[]>,
  edges: PropEdge[],
  column: number,
  neighborColumn: number,
  fallbackCompare: (a: PropNode, b: PropNode) => number,
) {
  const group = groups.get(column);
  const neighborGroup = groups.get(neighborColumn);
  if (!group?.length || !neighborGroup?.length) return;
  const neighborOrder = new Map(neighborGroup.map((node, index) => [node.id, index]));

  group.sort((a, b) => {
    const aScore = getNeighborOrderScore(a.id, edges, neighborOrder);
    const bScore = getNeighborOrderScore(b.id, edges, neighborOrder);
    if (aScore !== null && bScore !== null && aScore !== bScore) return aScore - bScore;
    if (aScore !== null && bScore === null) return -1;
    if (aScore === null && bScore !== null) return 1;
    return fallbackCompare(a, b);
  });
}

function getNeighborOrderScore(
  nodeId: string,
  edges: PropEdge[],
  neighborOrder: Map<string, number>,
) {
  const scores = edges
    .flatMap((edge) => {
      if (edge.fromNodeId === nodeId) return [neighborOrder.get(edge.toNodeId)];
      if (edge.toNodeId === nodeId) return [neighborOrder.get(edge.fromNodeId)];
      return [];
    })
    .filter((score): score is number => score !== undefined);
  if (!scores.length) return null;
  return scores.reduce((sum, score) => sum + score, 0) / scores.length;
}

function isAutoLayoutAuxiliaryNode(node: PropNode, edges: PropEdge[]) {
  const ports = getPropNodePorts(node);
  const hasFlowPort = ports.inputs.concat(ports.outputs).some((port) => port.kind === "flow");
  const hasRelation = edges.some(
    (edge) => edge.fromNodeId === node.id || edge.toNodeId === node.id,
  );
  return !hasFlowPort && hasRelation;
}

type AuxiliaryAnchor = {
  nodeId: string;
  role: "inputSource" | "outputTarget";
};

function findAuxiliaryAnchor(
  nodeId: string,
  edges: PropEdge[],
  coreNodeIds: Set<string>,
) {
  const inputSourceId = findAuxiliaryAnchorInDirection(
    nodeId,
    edges,
    coreNodeIds,
    "inputSource",
  );
  if (inputSourceId) return { nodeId: inputSourceId, role: "inputSource" as const };

  const outputTargetId = findAuxiliaryAnchorInDirection(
    nodeId,
    edges,
    coreNodeIds,
    "outputTarget",
  );
  if (outputTargetId) return { nodeId: outputTargetId, role: "outputTarget" as const };

  return null;
}

function findAuxiliaryAnchorInDirection(
  nodeId: string,
  edges: PropEdge[],
  coreNodeIds: Set<string>,
  role: AuxiliaryAnchor["role"],
) {
  const visited = new Set([nodeId]);
  const queue = [nodeId];

  while (queue.length) {
    const currentId = queue.shift()!;
    const nextIds =
      role === "inputSource"
        ? edges
            .filter((edge) => edge.toNodeId === currentId)
            .map((edge) => edge.fromNodeId)
        : edges
            .filter((edge) => edge.fromNodeId === currentId)
            .map((edge) => edge.toNodeId);

    for (const nextId of nextIds) {
      if (coreNodeIds.has(nextId)) return nextId;
      if (visited.has(nextId)) continue;
      visited.add(nextId);
      queue.push(nextId);
    }
  }

  return null;
}

function expandCoreLayoutForAuxiliaryBridges(
  auxiliaryNodes: PropNode[],
  edges: PropEdge[],
  positions: Map<string, PropGraphPosition>,
  nodes: PropNode[],
) {
  const requiredBridgeGap = NODE_WIDTH + AUTO_LAYOUT_COLUMN_GAP;

  auxiliaryNodes.forEach((node) => {
    const inputSource = findPositionedInputSource(node.id, edges, positions, nodes);
    const outputTarget = findPositionedOutputTarget(node.id, edges, positions, nodes);
    if (!inputSource || !outputTarget) return;

    const sourceRight = inputSource.position.x + NODE_WIDTH;
    const targetLeft = outputTarget.position.x;
    if (targetLeft <= sourceRight) return;

    const currentGap = targetLeft - sourceRight;
    const missingGap = requiredBridgeGap - currentGap;
    if (missingGap <= 0) return;

    shiftAutoLayoutPositionsAtOrAfter(positions, targetLeft, missingGap);
  });
}

function shiftAutoLayoutPositionsAtOrAfter(
  positions: Map<string, PropGraphPosition>,
  minX: number,
  delta: number,
) {
  positions.forEach((position, nodeId) => {
    if (position.x < minX) return;
    positions.set(nodeId, {
      ...position,
      x: position.x + delta,
    });
  });
}

function findNearestAuxiliaryPosition(
  node: PropNode,
  anchorNode: PropNode,
  anchorPosition: PropGraphPosition,
  occupied: LayoutRect[],
  edges: PropEdge[],
  positions: Map<string, PropGraphPosition>,
  nodes: PropNode[],
  anchorRole: AuxiliaryAnchor["role"],
): PropGraphPosition {
  const nodeHeight = estimateNodeHeight(node);
  const anchorHeight = estimateNodeHeight(anchorNode);
  const horizontalGap = Math.round(AUTO_LAYOUT_COLUMN_GAP * 0.72);
  const verticalGap = AUTO_LAYOUT_ROW_GAP;
  const xCandidates =
    anchorRole === "inputSource"
      ? [
          anchorPosition.x + NODE_WIDTH + horizontalGap,
          anchorPosition.x,
          anchorPosition.x - NODE_WIDTH - horizontalGap,
        ]
      : [
          anchorPosition.x - NODE_WIDTH - horizontalGap,
          anchorPosition.x,
          anchorPosition.x + NODE_WIDTH + horizontalGap,
        ];
  const ySeeds = [
    anchorPosition.y + anchorHeight + verticalGap,
    anchorPosition.y,
    anchorPosition.y - nodeHeight - verticalGap,
  ];
  const candidates: PropGraphPosition[] = [];
  const inputSource = findPositionedInputSource(node.id, edges, positions, nodes);
  const outputTarget = findPositionedOutputTarget(node.id, edges, positions, nodes);
  const companionInputSource =
    !inputSource && outputTarget
      ? findPositionedCompanionInputSource(node.id, edges, positions, nodes)
      : null;
  const inputOnlyRightCore =
    anchorRole === "inputSource" && inputSource && !outputTarget
      ? findNearestRightCoreNode(inputSource.node, inputSource.position, edges, positions, nodes)
      : null;

  if (anchorRole === "inputSource" && inputSource && inputOnlyRightCore) {
    const sourceAnchor = getAutoLayoutPortAnchor(
      inputSource.node,
      inputSource.position,
      "output",
      inputSource.edge.fromPortId,
    );
    const inputOffset = getAutoLayoutPortAnchor(
      node,
      { x: 0, y: 0 },
      "input",
      inputSource.edge.toPortId,
    ).y;
    const preferredY = Math.round(
      Math.max(
        inputSource.position.y + estimateNodeHeight(inputSource.node) + verticalGap,
        inputOnlyRightCore.position.y + estimateNodeHeight(inputOnlyRightCore.node) + verticalGap,
        sourceAnchor.y + verticalGap - inputOffset,
      ),
    );
    candidates.push({ x: inputOnlyRightCore.position.x, y: preferredY });
    candidates.push({ x: inputOnlyRightCore.position.x, y: preferredY + verticalGap });
    candidates.push({ x: inputOnlyRightCore.position.x, y: preferredY - verticalGap });
  }

  if (anchorRole === "inputSource" && inputSource && outputTarget) {
    const sourceAnchor = getAutoLayoutPortAnchor(
      inputSource.node,
      inputSource.position,
      "output",
      inputSource.edge.fromPortId,
    );
    const targetAnchor = getAutoLayoutPortAnchor(
      outputTarget.node,
      outputTarget.position,
      "input",
      outputTarget.edge.toPortId,
    );
    const inputOffset = getAutoLayoutPortAnchor(
      node,
      { x: 0, y: 0 },
      "input",
      inputSource.edge.toPortId,
    ).y;
    const sourceRight = inputSource.position.x + NODE_WIDTH;
    const targetLeft = outputTarget.position.x;
    const bridgeX = Math.round(
      sourceRight < targetLeft
        ? getCenteredBridgeNodeX(inputSource.position, outputTarget.position)
        : (inputSource.position.x + outputTarget.position.x) / 2,
    );
    const bridgeY = Math.round(
      Math.max(sourceAnchor.y, targetAnchor.y) + verticalGap - inputOffset,
    );
    candidates.push({ x: bridgeX, y: bridgeY });
    candidates.push({ x: bridgeX, y: bridgeY + verticalGap });
    candidates.push({ x: bridgeX, y: bridgeY - verticalGap });
  }

  if (anchorRole === "outputTarget" && companionInputSource && outputTarget) {
    const sourceAnchor = getAutoLayoutPortAnchor(
      companionInputSource.node,
      companionInputSource.position,
      "output",
      companionInputSource.edge.fromPortId,
    );
    const targetAnchor = getAutoLayoutPortAnchor(
      outputTarget.node,
      outputTarget.position,
      "input",
      outputTarget.edge.toPortId,
    );
    const inputOffset = getAutoLayoutPortAnchor(
      node,
      { x: 0, y: 0 },
      "output",
      outputTarget.edge.fromPortId,
    ).y;
    const bridgeX = companionInputSource.position.x;
    const bridgeY = Math.round(
      Math.max(
        companionInputSource.position.y + estimateNodeHeight(companionInputSource.node) + verticalGap,
        Math.max(sourceAnchor.y, targetAnchor.y) + verticalGap - inputOffset,
      ),
    );
    candidates.push({ x: bridgeX, y: bridgeY });
    candidates.push({ x: bridgeX, y: bridgeY + verticalGap });
    candidates.push({ x: bridgeX, y: bridgeY - verticalGap });
  }

  for (let layer = 0; layer < 8; layer += 1) {
    for (const x of xCandidates) {
      for (const seedY of ySeeds) {
        const offset = layer * (nodeHeight + verticalGap);
        candidates.push({ x, y: seedY + offset });
        if (layer > 0) candidates.push({ x, y: seedY - offset });
      }
    }
  }

  const scoredCandidates = candidates
    .filter((position) => !doesLayoutRectOverlap(createLayoutRect(node, position), occupied))
    .map((position) => ({
      position,
      score: scoreAuxiliaryPosition(
        node,
        anchorNode,
        anchorPosition,
        position,
        edges,
        positions,
        nodes,
        anchorRole,
      ),
    }))
    .sort((a, b) => a.score - b.score);

  return scoredCandidates[0]?.position ?? {
    x: anchorPosition.x - NODE_WIDTH - horizontalGap,
    y: anchorPosition.y + occupied.length * (nodeHeight + verticalGap),
  };
}

function scoreAuxiliaryPosition(
  node: PropNode,
  anchorNode: PropNode,
  anchorPosition: PropGraphPosition,
  candidate: PropGraphPosition,
  edges: PropEdge[],
  positions: Map<string, PropGraphPosition>,
  nodes: PropNode[],
  anchorRole: AuxiliaryAnchor["role"],
) {
  const candidatePositions = new Map(positions);
  candidatePositions.set(node.id, candidate);
  const nodeMap = new Map(nodes.map((item) => [item.id, item]));
  const nodeEdges = edges.filter(
    (edge) => edge.fromNodeId === node.id || edge.toNodeId === node.id,
  );
  const otherEdges = edges.filter(
    (edge) =>
      edge.fromNodeId !== node.id &&
      edge.toNodeId !== node.id &&
      candidatePositions.has(edge.fromNodeId) &&
      candidatePositions.has(edge.toNodeId),
  );
  const nodeSegments = nodeEdges
    .map((edge) => createAutoLayoutEdgeSegment(edge, nodeMap, candidatePositions))
    .filter((segment): segment is LayoutSegment => Boolean(segment));
  const otherSegments = otherEdges
    .map((edge) => createAutoLayoutEdgeSegment(edge, nodeMap, candidatePositions))
    .filter((segment): segment is LayoutSegment => Boolean(segment));
  const crossingCount = nodeSegments.reduce(
    (count, segment) =>
      count +
      otherSegments.filter(
        (other) =>
          !doSegmentsShareEndpoint(segment, other) &&
          doSegmentsIntersect(segment, other),
      ).length,
    0,
  );
  const blockedCount = countEdgeNodeObstructions(
    edges,
    nodeMap,
    candidatePositions,
    nodes,
  );
  const directionViolationCount = countEdgeDirectionViolations(
    edges,
    nodeMap,
    candidatePositions,
  );
  const outputTarget = findPositionedOutputTarget(node.id, edges, candidatePositions, nodes);
  const inputSource = findPositionedInputSource(node.id, edges, candidatePositions, nodes);
  const companionInputSource =
    !inputSource && outputTarget
      ? findPositionedCompanionInputSource(node.id, edges, candidatePositions, nodes)
      : null;
  const anchorCenter = getLayoutRectCenter(createLayoutRect(anchorNode, anchorPosition));
  const candidateCenter = getLayoutRectCenter(createLayoutRect(node, candidate));
  const outputTargetCenter = outputTarget
    ? getLayoutRectCenter(createLayoutRect(outputTarget.node, outputTarget.position))
    : null;
  const dx = candidateCenter.x - anchorCenter.x;
  const dy = candidateCenter.y - anchorCenter.y;
  const distanceScore = Math.hypot(dx, dy);
  const aboveAnchorPenalty = candidateCenter.y < anchorCenter.y ? 220 : 0;
  const sourceRight = anchorPosition.x + NODE_WIDTH;
  const outputTargetLeft = outputTarget?.position.x;
  const inputOnlyRightCore =
    anchorRole === "inputSource" && !outputTarget
      ? findNearestRightCoreNode(anchorNode, anchorPosition, edges, candidatePositions, nodes)
      : null;
  const centeredBridgePenalty =
    anchorRole === "inputSource" &&
    outputTarget &&
    outputTargetLeft !== undefined &&
    outputTargetLeft > sourceRight
      ? Math.abs(candidate.x - getCenteredBridgeNodeX(anchorPosition, outputTarget.position)) * 1600
      : 0;
  const notBetweenInputAndOutputPenalty =
    anchorRole === "inputSource" &&
    outputTargetCenter &&
    outputTargetLeft !== undefined &&
    outputTargetLeft > sourceRight &&
    (candidate.x < sourceRight - 8 || candidate.x + NODE_WIDTH > outputTargetLeft + 8)
      ? 500000
      : 0;
  const inputOnlyRightLanePenalty =
    inputOnlyRightCore && candidate.x < inputOnlyRightCore.position.x - 8
      ? 500000 + Math.abs(candidate.x - inputOnlyRightCore.position.x) * 1600
      : inputOnlyRightCore
        ? Math.abs(candidate.x - inputOnlyRightCore.position.x) * 1600
        : 0;
  const rightOfOutputTargetPenalty =
    anchorRole === "outputTarget" && candidateCenter.x > anchorCenter.x ? 260 : 0;
  const outputTargetSameColumnPenalty =
    anchorRole === "outputTarget" && candidateCenter.x >= anchorCenter.x - NODE_WIDTH * 0.45
      ? 40000
      : 0;
  const companionPreferredY =
    companionInputSource && outputTarget
      ? companionInputSource.position.y + estimateNodeHeight(companionInputSource.node) + AUTO_LAYOUT_ROW_GAP
      : null;
  const companionBelowSourcePenalty =
    companionInputSource && companionPreferredY !== null && candidate.y < companionPreferredY - 8
      ? 900000 + Math.abs(candidate.y - companionPreferredY) * 2000
      : companionPreferredY !== null
        ? Math.abs(candidate.y - companionPreferredY) * 35
        : 0;
  const companionCenteredBridgePenalty =
    companionInputSource && outputTarget
      ? Math.abs(candidate.x - companionInputSource.position.x) * 1800
      : 0;
  const companionBetweenSourceAndTargetPenalty =
    companionInputSource &&
    outputTarget &&
    candidate.x >= outputTarget.position.x - 8
      ? 600000
      : 0;

  return (
    crossingCount * 1200000 +
    blockedCount * 300000 +
    directionViolationCount * 80000 +
    distanceScore +
    aboveAnchorPenalty +
    centeredBridgePenalty +
    notBetweenInputAndOutputPenalty +
    inputOnlyRightLanePenalty +
    rightOfOutputTargetPenalty +
    outputTargetSameColumnPenalty +
    companionBelowSourcePenalty +
    companionCenteredBridgePenalty +
    companionBetweenSourceAndTargetPenalty
  );
}

function getCenteredBridgeNodeX(
  inputSourcePosition: PropGraphPosition,
  outputTargetPosition: PropGraphPosition,
) {
  return Math.round((inputSourcePosition.x + outputTargetPosition.x) / 2);
}

function findNearestRightCoreNode(
  anchorNode: PropNode,
  anchorPosition: PropGraphPosition,
  edges: PropEdge[],
  positions: Map<string, PropGraphPosition>,
  nodes: PropNode[],
) {
  const anchorCenter = getLayoutRectCenter(createLayoutRect(anchorNode, anchorPosition));
  return nodes
    .map((node) => {
      const position = positions.get(node.id);
      return position ? { node, position } : null;
    })
    .filter(
      (item): item is { node: PropNode; position: PropGraphPosition } =>
        item !== null &&
        item.node.id !== anchorNode.id &&
        item.position.x > anchorPosition.x + NODE_WIDTH * 0.5 &&
        !isAutoLayoutAuxiliaryNode(item.node, edges),
    )
    .sort((a, b) => {
      const aCenter = getLayoutRectCenter(createLayoutRect(a.node, a.position));
      const bCenter = getLayoutRectCenter(createLayoutRect(b.node, b.position));
      const yDiff = Math.abs(aCenter.y - anchorCenter.y) - Math.abs(bCenter.y - anchorCenter.y);
      if (yDiff !== 0) return yDiff;
      return a.position.x - b.position.x;
    })[0] ?? null;
}

function findPositionedOutputTarget(
  nodeId: string,
  edges: PropEdge[],
  positions: Map<string, PropGraphPosition>,
  nodes: PropNode[],
) {
  const nodeMap = new Map(nodes.map((node) => [node.id, node]));
  const edge = edges.find(
    (item) => item.fromNodeId === nodeId && positions.has(item.toNodeId),
  );
  if (!edge) return null;
  const node = nodeMap.get(edge.toNodeId);
  const position = positions.get(edge.toNodeId);
  if (!node || !position) return null;
  return { node, position, edge };
}

function findPositionedInputSource(
  nodeId: string,
  edges: PropEdge[],
  positions: Map<string, PropGraphPosition>,
  nodes: PropNode[],
) {
  const nodeMap = new Map(nodes.map((node) => [node.id, node]));
  const edge = edges.find(
    (item) => item.toNodeId === nodeId && positions.has(item.fromNodeId),
  );
  if (!edge) return null;
  const node = nodeMap.get(edge.fromNodeId);
  const position = positions.get(edge.fromNodeId);
  if (!node || !position) return null;
  return { node, position, edge };
}

function findPositionedCompanionInputSource(
  nodeId: string,
  edges: PropEdge[],
  positions: Map<string, PropGraphPosition>,
  nodes: PropNode[],
) {
  const nodeMap = new Map(nodes.map((node) => [node.id, node]));
  const outputEdge = edges.find(
    (item) => item.fromNodeId === nodeId && positions.has(item.toNodeId),
  );
  if (!outputEdge) return null;

  const companionEdges = edges
    .filter(
      (item) =>
        item.toNodeId === outputEdge.toNodeId &&
        item.fromNodeId !== nodeId &&
        positions.has(item.fromNodeId),
    )
    .sort((a, b) => {
      const priorityDiff = getCompanionInputSourcePriority(a) - getCompanionInputSourcePriority(b);
      if (priorityDiff !== 0) return priorityDiff;
      const aPosition = positions.get(a.fromNodeId);
      const bPosition = positions.get(b.fromNodeId);
      return (aPosition?.y ?? 0) - (bPosition?.y ?? 0);
    });
  const edge = companionEdges[0];
  if (!edge) return null;

  const node = nodeMap.get(edge.fromNodeId);
  const position = positions.get(edge.fromNodeId);
  if (!node || !position) return null;
  return { node, position, edge };
}

function getCompanionInputSourcePriority(edge: PropEdge) {
  if (edge.kind === "target") return 0;
  if (edge.kind === "condition") return 1;
  if (edge.kind === "flow") return 2;
  if (edge.kind === "value") return 3;
  return 4;
}

type LayoutSegment = {
  from: PropGraphPosition;
  to: PropGraphPosition;
  fromNodeId: string;
  toNodeId: string;
};

function createAutoLayoutEdgeSegment(
  edge: PropEdge,
  nodeMap: Map<string, PropNode>,
  positions: Map<string, PropGraphPosition>,
): LayoutSegment | null {
  const fromNode = nodeMap.get(edge.fromNodeId);
  const toNode = nodeMap.get(edge.toNodeId);
  const fromPosition = positions.get(edge.fromNodeId);
  const toPosition = positions.get(edge.toNodeId);
  if (!fromNode || !toNode || !fromPosition || !toPosition) return null;
  return {
    from: getAutoLayoutPortAnchor(fromNode, fromPosition, "output", edge.fromPortId),
    to: getAutoLayoutPortAnchor(toNode, toPosition, "input", edge.toPortId),
    fromNodeId: edge.fromNodeId,
    toNodeId: edge.toNodeId,
  };
}

function countEdgeNodeObstructions(
  edges: PropEdge[],
  nodeMap: Map<string, PropNode>,
  positions: Map<string, PropGraphPosition>,
  nodes: PropNode[],
) {
  const nodeRects = nodes
    .map((node) => {
      const position = positions.get(node.id);
      return position ? { nodeId: node.id, rect: createLayoutRect(node, position) } : null;
    })
    .filter((item): item is { nodeId: string; rect: LayoutRect } => Boolean(item));

  return edges.reduce((count, edge) => {
    const segment = createAutoLayoutEdgeSegment(edge, nodeMap, positions);
    if (!segment) return count;
    return (
      count +
      nodeRects.filter(
        ({ nodeId, rect }) =>
          nodeId !== segment.fromNodeId &&
          nodeId !== segment.toNodeId &&
          doesSegmentIntersectRect(segment, rect),
      ).length
    );
  }, 0);
}

function countEdgeDirectionViolations(
  edges: PropEdge[],
  nodeMap: Map<string, PropNode>,
  positions: Map<string, PropGraphPosition>,
) {
  return edges.reduce((count, edge) => {
    const fromNode = nodeMap.get(edge.fromNodeId);
    const toNode = nodeMap.get(edge.toNodeId);
    const fromPosition = positions.get(edge.fromNodeId);
    const toPosition = positions.get(edge.toNodeId);
    if (!fromNode || !toNode || !fromPosition || !toPosition) return count;
    const fromCenter = getLayoutRectCenter(createLayoutRect(fromNode, fromPosition));
    const toCenter = getLayoutRectCenter(createLayoutRect(toNode, toPosition));
    return count + (fromCenter.x > toCenter.x + NODE_WIDTH * 0.25 ? 1 : 0);
  }, 0);
}

function getAutoLayoutPortAnchor(
  node: PropNode,
  position: PropGraphPosition,
  direction: PortDirection,
  portId: string,
): PropGraphPosition {
  const portDefinition = getPropNodePorts(node);
  const ports = direction === "input" ? portDefinition.inputs : portDefinition.outputs;
  const index = Math.max(
    0,
    ports.findIndex((port) => port.id === portId),
  );
  return {
    x: position.x + (direction === "input" ? PORT_HIT_SIZE / 2 : NODE_WIDTH - PORT_HIT_SIZE / 2),
    y:
      position.y +
      NODE_HEADER_HEIGHT +
      NODE_BODY_PADDING +
      index * PORT_ROW_HEIGHT +
      PORT_ROW_HEIGHT / 2,
  };
}

function getLayoutRectCenter(rect: LayoutRect): PropGraphPosition {
  return {
    x: rect.x + rect.width / 2,
    y: rect.y + rect.height / 2,
  };
}

function doSegmentsIntersect(a: LayoutSegment, b: LayoutSegment) {
  const epsilon = 0.001;
  const d1 = getPointDirection(a.from, a.to, b.from);
  const d2 = getPointDirection(a.from, a.to, b.to);
  const d3 = getPointDirection(b.from, b.to, a.from);
  const d4 = getPointDirection(b.from, b.to, a.to);

  if (
    ((d1 > epsilon && d2 < -epsilon) || (d1 < -epsilon && d2 > epsilon)) &&
    ((d3 > epsilon && d4 < -epsilon) || (d3 < -epsilon && d4 > epsilon))
  ) {
    return true;
  }

  return false;
}

function doSegmentsShareEndpoint(a: LayoutSegment, b: LayoutSegment) {
  return (
    a.fromNodeId === b.fromNodeId ||
    a.fromNodeId === b.toNodeId ||
    a.toNodeId === b.fromNodeId ||
    a.toNodeId === b.toNodeId
  );
}

function doesSegmentIntersectRect(segment: LayoutSegment, rect: LayoutRect) {
  const padding = 8;
  const expandedRect = {
    x: rect.x - padding,
    y: rect.y - padding,
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
  };
  if (
    isPointInsideRect(segment.from, expandedRect) ||
    isPointInsideRect(segment.to, expandedRect)
  ) {
    return true;
  }

  const left = expandedRect.x;
  const right = expandedRect.x + expandedRect.width;
  const top = expandedRect.y;
  const bottom = expandedRect.y + expandedRect.height;
  const rectEdges: LayoutSegment[] = [
    {
      from: { x: left, y: top },
      to: { x: right, y: top },
      fromNodeId: "",
      toNodeId: "",
    },
    {
      from: { x: right, y: top },
      to: { x: right, y: bottom },
      fromNodeId: "",
      toNodeId: "",
    },
    {
      from: { x: right, y: bottom },
      to: { x: left, y: bottom },
      fromNodeId: "",
      toNodeId: "",
    },
    {
      from: { x: left, y: bottom },
      to: { x: left, y: top },
      fromNodeId: "",
      toNodeId: "",
    },
  ];
  return rectEdges.some((edge) => doSegmentsIntersect(segment, edge));
}

function isPointInsideRect(point: PropGraphPosition, rect: LayoutRect) {
  return (
    point.x >= rect.x &&
    point.x <= rect.x + rect.width &&
    point.y >= rect.y &&
    point.y <= rect.y + rect.height
  );
}

function getPointDirection(
  a: PropGraphPosition,
  b: PropGraphPosition,
  c: PropGraphPosition,
) {
  return (c.x - a.x) * (b.y - a.y) - (c.y - a.y) * (b.x - a.x);
}

type LayoutRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

function createLayoutRect(node: PropNode, position: PropGraphPosition): LayoutRect {
  return {
    x: position.x,
    y: position.y,
    width: NODE_WIDTH,
    height: estimateNodeHeight(node),
  };
}

function doesLayoutRectOverlap(rect: LayoutRect, occupied: LayoutRect[]) {
  const padding = 28;
  return occupied.some(
    (item) =>
      rect.x < item.x + item.width + padding &&
      rect.x + rect.width + padding > item.x &&
      rect.y < item.y + item.height + padding &&
      rect.y + rect.height + padding > item.y,
  );
}

function normalizeAutoLayoutPositions(
  positions: Map<string, PropGraphPosition>,
  nodes: PropNode[],
) {
  const rects = nodes
    .map((node) => {
      const position = positions.get(node.id);
      return position ? createLayoutRect(node, position) : null;
    })
    .filter((rect): rect is LayoutRect => Boolean(rect));
  if (!rects.length) return { positions, width: NODE_WIDTH, height: NODE_HEIGHT };

  const minX = Math.min(...rects.map((rect) => rect.x));
  const minY = Math.min(...rects.map((rect) => rect.y));
  const normalizedPositions = new Map<string, PropGraphPosition>();
  positions.forEach((position, nodeId) => {
    normalizedPositions.set(nodeId, {
      x: position.x - minX,
      y: position.y - minY,
    });
  });

  const normalizedRects = nodes
    .map((node) => {
      const position = normalizedPositions.get(node.id);
      return position ? createLayoutRect(node, position) : null;
    })
    .filter((rect): rect is LayoutRect => Boolean(rect));
  return {
    positions: normalizedPositions,
    width: Math.max(...normalizedRects.map((rect) => rect.x + rect.width)),
    height: Math.max(...normalizedRects.map((rect) => rect.y + rect.height)),
  };
}

function estimateNodeHeight(node: PropNode) {
  const ports = getPropNodePorts(node);
  const portRows = Math.max(ports.inputs.length, ports.outputs.length);
  const propertyRows = getNodePropertyRows(node);
  const portAreaHeight = portRows > 0 ? portRows * PORT_ROW_HEIGHT + 8 : 0;
  const propertyAreaHeight = propertyRows.length > 0 ? 7 + propertyRows.length * 20 : 0;
  return Math.max(
    NODE_HEADER_HEIGHT + NODE_BODY_PADDING * 2,
    NODE_HEADER_HEIGHT + NODE_BODY_PADDING + portAreaHeight + propertyAreaHeight + NODE_BODY_PADDING,
  );
}

type NormalizedPropGraphDocument = {
  nodes: PropNode[];
  edges: PropEdge[];
  entryNodeIds: string[];
  editor: PropGraphEditorState;
};

function getPropGraphValidationIssues(graph: PropGraph): PropGraphValidationIssue[] {
  const issues: PropGraphValidationIssue[] = [];
  const nodes = getGraphNodeList(graph.graph);
  const edges = getValidCanvasEdges(getGraphEdgeList(graph.graph), nodes);
  const connectedInputPorts = new Set(edges.map((edge) => `${edge.toNodeId}:${edge.toPortId}`));
  const editorNodeMap = new Map((graph.editor?.nodes ?? []).map((item) => [item.nodeId, item]));

  addGraphFieldIssue(issues, graph, "name", "名称");
  addGraphFieldIssue(issues, graph, "assetId", "资源ID");
  addGraphFieldIssue(issues, graph, "type", "类型");
  addGraphFieldIssue(issues, graph, "rarity", "品质");

  nodes.forEach((node) => {
    if (editorNodeMap.get(node.id)?.disabled) return;
    addNodeFieldIssues(issues, node);
    getRequiredInputPorts(node).forEach((port) => {
      if (connectedInputPorts.has(`${node.id}:${port.id}`)) return;
      issues.push({
        kind: "port",
        nodeId: node.id,
        portId: port.id,
        message: `${getDisplayNodeTitle(node)}：请连接${getDisplayPortName(port)}输入`,
      });
    });
  });

  return issues;
}

function addGraphFieldIssue(
  issues: PropGraphValidationIssue[],
  graph: PropGraph,
  field: keyof Pick<PropGraph, "name" | "assetId" | "type" | "rarity">,
  label: string,
) {
  if (!isEmptyRequiredValue(graph[field])) return;
  issues.push({
    kind: "graphField",
    field,
    message: `请填写${label}`,
  });
}

function addNodeFieldIssues(issues: PropGraphValidationIssue[], node: PropNode) {
  switch (node.type) {
    case "eventTurnStart":
      addRequiredNodeFieldIssue(issues, node, "interval", node.data.interval, "间隔");
      return;
    case "eventAcquire":
    case "eventUse":
    case "eventEliminated":
    case "targetSelf":
    case "targetFilter":
    case "filterRarity":
    case "filterTag":
    case "filterPrototype":
    case "filterRandom":
    case "intQuantity":
    case "intKindCount":
    case "intRandom":
    case "floatRandom":
    case "actionGrow":
    case "actionExtraYield":
    case "actionPermanentBoost":
    case "actionPurify":
    case "actionProtect":
    case "actionAccelerate":
    case "actionTransform":
    case "actionSpawn":
    case "actionEliminate":
    case "actionCollect":
    case "actionHarvest":
    case "actionRarityWeight":
    case "actionExtendCommission":
    case "actionSwapPosition":
    case "actionTriggerVisual":
      return;
    case "prototypeLibrary":
      addRequiredNodeFieldIssue(issues, node, "propType", node.data.propType, "类型");
      return;
    case "targetBoard":
      addRequiredNodeFieldIssue(issues, node, "scope", node.data.scope, "范围");
      if (node.data.scope !== "all") {
        addRequiredNodeFieldIssue(issues, node, "position", node.data.position, "位置");
      }
      return;
    case "prototypeSelect":
      addRequiredNodeFieldIssue(issues, node, "prototypeId", node.data.prototypeId, "道具原型");
      return;
    case "intConstant":
    case "floatConstant":
    case "rarityConstant":
    case "tagConstant":
      addRequiredNodeFieldIssue(issues, node, "value", node.data.value, "值");
      return;
    case "intAttribute":
    case "floatAttribute":
      addRequiredNodeFieldIssue(issues, node, "attribute", node.data.attribute, "属性");
      return;
    case "intOperation":
    case "floatOperation":
      addRequiredNodeFieldIssue(issues, node, "op", node.data.op, "运算");
      return;
    case "roundNumber":
      addRequiredNodeFieldIssue(issues, node, "mode", node.data.mode, "取整方式");
      return;
    case "logicCompareInt":
    case "logicCompareFloat":
      addRequiredNodeFieldIssue(issues, node, "op", node.data.op, "比较");
      return;
    case "logicContains":
      return;
    case "logicProbability":
      addRequiredNodeFieldIssue(issues, node, "probability", node.data.probability, "概率");
      return;
  }
}

function addRequiredNodeFieldIssue(
  issues: PropGraphValidationIssue[],
  node: PropNode,
  field: string,
  value: unknown,
  label: string,
) {
  if (!isEmptyRequiredValue(value)) return;
  issues.push({
    kind: "field",
    nodeId: node.id,
    field,
    message: `${getDisplayNodeTitle(node)}：请填写${label}`,
  });
}

function getRequiredInputPorts(node: PropNode) {
  return getPropNodePorts(node).inputs.filter((port) => port.required !== false);
}

function isEmptyRequiredValue(value: unknown) {
  if (value === undefined || value === null) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

function clampNumber(value: number, min: number, max: number) {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

function sanitizePropGraph(value: Partial<PropGraph> & Record<string, any>): PropGraph {
  const graph = normalizeDocument(value.graph, value.editor);
  const validEdges = getValidCanvasEdges(graph.edges, graph.nodes);
  const orderedGraph = orderGraphDocumentByEventPosition(graph, validEdges);
  const connections = buildGraphConnections(validEdges);

  return {
    _id: value._id ?? "",
    name: value.name?.trim() || "未命名道具",
    assetId: value.assetId?.trim() || "Empty_001",
    type: Number(value.type ?? 1),
    rarity: Number(value.rarity ?? 1),
    tags: toNumberArray(value.tags),
    derived: Boolean(value.derived),
    yield: Number(value.yield ?? 0),
    updatedAt: Number.isFinite(Number(value.updatedAt)) ? Number(value.updatedAt) : undefined,
    updatedBy: typeof value.updatedBy === "string" ? value.updatedBy : undefined,
    schemaVersion: 3,
    graph: {
      entryNodeIds: orderedGraph.entryNodeIds,
      nodes: createPropNodeMap(orderedGraph.nodes),
      flows: connections.flows,
      bindings: connections.bindings,
    },
    editor: orderedGraph.editor,
  };
}

function normalizePropGraph(value: PropGraph): PropGraph {
  return sanitizePropGraph(value);
}

function normalizePropGraphNameIndexItem(value: Partial<PropGraph> & Record<string, any>): PropGraph {
  return {
    _id: String(value._id ?? ""),
    name: String(value.name ?? "").trim() || "未命名道具",
    assetId: String(value.assetId ?? "").trim(),
    type: Number(value.type ?? 1),
    rarity: Number(value.rarity ?? 1),
    tags: toNumberArray(value.tags),
    derived: Boolean(value.derived),
    yield: Number(value.yield ?? 0),
    updatedAt: Number.isFinite(Number(value.updatedAt)) ? Number(value.updatedAt) : undefined,
    updatedBy: typeof value.updatedBy === "string" ? value.updatedBy : undefined,
    schemaVersion: 3,
    graph: {
      entryNodeIds: [],
      nodes: {},
      flows: [],
      bindings: [],
    },
    editor: {
      nodes: [],
      comments: [],
    },
  };
}

function mergePropGraphNameIndex(...sources: PropGraph[][]) {
  const map = new Map<string, PropGraph>();
  sources.flat().forEach((graph) => {
    const id = String(graph._id ?? "");
    if (!id) return;
    map.set(id, cloneGraph(graph));
  });
  return Array.from(map.values());
}

function isFullPropGraphDocument(value: Partial<PropGraph> | undefined): value is PropGraph {
  return Boolean(value?.graph?.nodes && value?.editor?.nodes);
}

function hasPropGraphLogicDocument(value: Partial<PropGraph> | undefined): value is PropGraph {
  return Boolean(value?.graph?.nodes);
}

function normalizeDocument(
  graph: (Partial<PropGraph["graph"]> & Record<string, any>) | undefined,
  rootEditor?: Partial<PropGraphEditorState> & Record<string, any>,
): NormalizedPropGraphDocument {
  const rawNodes = readRawNodes(graph?.nodes);
  const nodes = rawNodes
    .map((node) => sanitizeNode(node))
    .filter((node): node is PropNode => Boolean(node));
  const editor = rootEditor ?? graph?.editor ?? {};
  const editorNodes = normalizeEditorNodeStates(nodes, rawNodes, editor.nodes);
  const migrated = migrateRandomFilterCountInputs(
    nodes,
    getValidCanvasEdges(readRawEdges(graph), nodes),
    editorNodes,
    rawNodes,
  );

  return {
    nodes: migrated.nodes,
    edges: getValidCanvasEdges(migrated.edges, migrated.nodes),
    entryNodeIds: Array.isArray(graph?.entryNodeIds)
      ? (graph?.entryNodeIds ?? []).map(String)
      : [],
    editor: {
      nodes: migrated.editorNodes,
      comments: Array.isArray(editor.comments) ? editor.comments : [],
    },
  };
}

function migrateRandomFilterCountInputs(
  nodes: PropNode[],
  edges: PropEdge[],
  editorNodes: PropGraphNodeEditorState[],
  rawNodes: Array<Partial<PropNode> & Record<string, any>>,
) {
  let nextNodes = nodes;
  let nextEdges = edges;
  let nextEditorNodes = editorNodes;
  const nodeIds = new Set(nodes.map((node) => node.id));
  const rawNodeMap = new Map(rawNodes.map((node) => [String(node.id ?? ""), node]));

  nodes.forEach((node) => {
    if (node.type !== "filterRandom") return;
    if (nextEdges.some((edge) => edge.toNodeId === node.id && edge.toPortId === "count")) return;
    const rawData = rawNodeMap.get(node.id)?.data as Record<string, unknown> | undefined;
    const rawCount = rawData?.count;
    if (isEmptyRequiredValue(rawCount)) return;

    const countNodeId = getDeterministicMigratedNodeId(`${node.id}_count`, nodeIds);
    nodeIds.add(countNodeId);
    const countNode: PropNode = {
      id: countNodeId,
      type: "intConstant",
      data: { value: Math.max(0, Math.floor(Number(rawCount ?? 1))) },
    };
    const targetEditorState = nextEditorNodes.find((item) => item.nodeId === node.id);
    const countEditorState = createDefaultNodeEditorState(countNode, nextNodes.length);
    if (targetEditorState) {
      countEditorState.position = {
        x: targetEditorState.position.x - NODE_WIDTH - 40,
        y: targetEditorState.position.y,
      };
    }

    nextNodes = nextNodes.concat(countNode);
    nextEditorNodes = nextEditorNodes.concat(countEditorState);
    nextEdges = nextEdges.concat({
      id: `${node.id}_count_binding`,
      kind: "value",
      fromNodeId: countNode.id,
      fromPortId: "value",
      toNodeId: node.id,
      toPortId: "count",
    });
  });

  return {
    nodes: nextNodes,
    edges: nextEdges,
    editorNodes: nextEditorNodes,
  };
}

function getDeterministicMigratedNodeId(baseId: string, usedIds: Set<string>) {
  if (!usedIds.has(baseId)) return baseId;
  let index = 1;
  let candidate = `${baseId}_${index}`;
  while (usedIds.has(candidate)) {
    index += 1;
    candidate = `${baseId}_${index}`;
  }
  return candidate;
}

function orderGraphDocumentByEventPosition(
  graph: NormalizedPropGraphDocument,
  edges: PropEdge[],
): NormalizedPropGraphDocument {
  const nodeIndex = new Map(graph.nodes.map((node, index) => [node.id, index]));
  const editorNodeMap = new Map(graph.editor.nodes.map((item) => [item.nodeId, item]));
  const entryNodeIds = sortEntryNodeIdsByEventPosition(
    normalizeEntryNodeIds(graph.entryNodeIds, graph.nodes),
    editorNodeMap,
    nodeIndex,
  );
  const entryRank = new Map(entryNodeIds.map((id, index) => [id, index]));
  const nodeGroupRank = getEventPositionNodeGroupRanks(graph.nodes, edges, entryRank);
  const fallbackRank = entryNodeIds.length + 1;
  const sortedNodes = graph.nodes.slice().sort((a, b) => {
    const rankDiff = (nodeGroupRank.get(a.id) ?? fallbackRank) - (nodeGroupRank.get(b.id) ?? fallbackRank);
    if (rankDiff !== 0) return rankDiff;
    const aPosition = editorNodeMap.get(a.id)?.position;
    const bPosition = editorNodeMap.get(b.id)?.position;
    if ((nodeGroupRank.get(a.id) ?? fallbackRank) === fallbackRank) {
      const yDiff = Number(aPosition?.y ?? 0) - Number(bPosition?.y ?? 0);
      if (yDiff !== 0) return yDiff;
      const xDiff = Number(aPosition?.x ?? 0) - Number(bPosition?.x ?? 0);
      if (xDiff !== 0) return xDiff;
    }
    return (nodeIndex.get(a.id) ?? 0) - (nodeIndex.get(b.id) ?? 0);
  });

  return {
    ...graph,
    nodes: sortedNodes,
    entryNodeIds,
    editor: {
      ...graph.editor,
      nodes: sortedNodes
        .map((node) => editorNodeMap.get(node.id))
        .filter((item): item is PropGraphNodeEditorState => Boolean(item)),
    },
  };
}

function sortEntryNodeIdsByEventPosition(
  entryNodeIds: string[],
  editorNodeMap: Map<string, PropGraphNodeEditorState>,
  nodeIndex: Map<string, number>,
) {
  return entryNodeIds.slice().sort((a, b) => {
    const aPosition = editorNodeMap.get(a)?.position;
    const bPosition = editorNodeMap.get(b)?.position;
    const yDiff = Number(aPosition?.y ?? 0) - Number(bPosition?.y ?? 0);
    if (yDiff !== 0) return yDiff;
    const xDiff = Number(aPosition?.x ?? 0) - Number(bPosition?.x ?? 0);
    if (xDiff !== 0) return xDiff;
    return (nodeIndex.get(a) ?? 0) - (nodeIndex.get(b) ?? 0);
  });
}

function getEventPositionNodeGroupRanks(
  nodes: PropNode[],
  edges: PropEdge[],
  entryRank: Map<string, number>,
) {
  const ranks = new Map<string, number>();
  createConnectedNodeGroups(nodes, edges).forEach((group) => {
    const groupRank = Math.min(
      ...group
        .map((node) => entryRank.get(node.id))
        .filter((rank): rank is number => rank !== undefined),
    );
    if (!Number.isFinite(groupRank)) return;
    group.forEach((node) => ranks.set(node.id, groupRank));
  });
  return ranks;
}

function getGraphNodeList(graph: PropGraph["graph"]): PropNode[] {
  return Object.values(graph.nodes ?? {});
}

function getGraphEdgeList(graph: PropGraph["graph"]): PropEdge[] {
  return [
    ...(graph.flows ?? []).map((flow, index) => ({
      id: flow.id || createPropGraphId("flow"),
      kind: "flow" as const,
      fromNodeId: flow.from.nodeId,
      fromPortId: flow.from.portId,
      toNodeId: flow.to.nodeId,
      toPortId: flow.to.portId,
      order: flow.order ?? index,
    })),
    ...(graph.bindings ?? []).map((binding) => ({
      id: binding.id || createPropGraphId("binding"),
      kind: binding.kind,
      fromNodeId: binding.source.nodeId,
      fromPortId: binding.source.portId,
      toNodeId: binding.target.nodeId,
      toPortId: binding.target.portId,
    })),
  ];
}

function buildGraphConnections(
  edges: PropEdge[],
): Pick<PropGraph["graph"], "flows" | "bindings"> {
  const flows = edges
    .filter((edge) => edge.kind === "flow")
    .map((edge, index) => ({
      id: edge.id || createPropGraphId("flow"),
      from: { nodeId: edge.fromNodeId, portId: edge.fromPortId },
      to: { nodeId: edge.toNodeId, portId: edge.toPortId },
      order: edge.order ?? index,
    }));
  const bindings = edges
    .filter((edge) => edge.kind !== "flow")
    .map((edge) => ({
      id: edge.id || createPropGraphId("binding"),
      kind: edge.kind as PropGraphBindingKind,
      source: { nodeId: edge.fromNodeId, portId: edge.fromPortId },
      target: { nodeId: edge.toNodeId, portId: edge.toPortId },
    }));
  return { flows, bindings };
}

function getValidCanvasEdges(edges: Array<Partial<PropEdge>>, nodes: PropNode[]): PropEdge[] {
  const nodeIds = new Set(nodes.map((node) => node.id));
  const validEdges = edges
    .map((edge) => normalizeCanvasEdge(edge as Partial<PropEdge> & Record<string, any>, nodes))
    .filter(
      (edge) =>
        nodeIds.has(edge.fromNodeId) &&
        nodeIds.has(edge.toNodeId) &&
        Boolean(edge.fromPortId) &&
        Boolean(edge.toPortId) &&
        inferEdgeKind(edge, nodes) !== null,
    );
  const usedSingleInputPorts = new Set<string>();
  return validEdges.filter((edge) => {
    const inputPort = getEdgeInputPort(edge, nodes);
    if (!inputPort || inputPort.multiple) return true;
    const key = `${edge.toNodeId}:${edge.toPortId}`;
    if (usedSingleInputPorts.has(key)) return false;
    usedSingleInputPorts.add(key);
    return true;
  });
}

function getEdgeInputPort(edge: Pick<PropEdge, "toNodeId" | "toPortId">, nodes: PropNode[]) {
  const toNode = nodes.find((node) => node.id === edge.toNodeId);
  return toNode
    ? getPropNodePorts(toNode).inputs.find((port) => port.id === edge.toPortId)
    : undefined;
}

function normalizeCanvasEdge(edge: Partial<PropEdge> & Record<string, any>, nodes: PropNode[]): PropEdge {
  const candidate = {
    id: edge.id || createPropGraphId("edge"),
    kind: isPropGraphPortKind(edge.kind) ? edge.kind : "flow",
    fromNodeId: String(edge.fromNodeId ?? ""),
    fromPortId: String(edge.fromPortId ?? ""),
    toNodeId: String(edge.toNodeId ?? ""),
    toPortId: String(edge.toPortId ?? ""),
    order: Number.isFinite(Number(edge.order)) ? Number(edge.order) : undefined,
  };
  return {
    ...candidate,
    kind: inferEdgeKind(candidate, nodes) ?? candidate.kind,
  };
}

function inferEdgeKind(edge: Pick<PropEdge, "fromNodeId" | "fromPortId" | "toNodeId" | "toPortId">, nodes: PropNode[]) {
  const fromNode = nodes.find((node) => node.id === edge.fromNodeId);
  const toNode = nodes.find((node) => node.id === edge.toNodeId);
  if (!fromNode || !toNode) return null;
  const output = getPropNodePorts(fromNode).outputs.find((port) => port.id === edge.fromPortId);
  const input = getPropNodePorts(toNode).inputs.find((port) => port.id === edge.toPortId);
  if (!output || !input) return null;
  return isCompatibleConnection(output, input) ? output.kind : null;
}

function normalizeEntryNodeIds(entryNodeIds: string[], nodes: PropNode[]) {
  const nodeMap = new Map(nodes.map((node) => [node.id, node]));
  const explicitEntries = entryNodeIds.filter((id) => {
    const node = nodeMap.get(id);
    return node ? isEventNodeType(node.type) : false;
  });
  const eventEntries = nodes.filter((node) => isEventNodeType(node.type)).map((node) => node.id);
  return Array.from(new Set(explicitEntries.concat(eventEntries)));
}

function readRawNodes(value: unknown): Array<Partial<PropNode> & Record<string, any>> {
  if (Array.isArray(value)) return value as Array<Partial<PropNode> & Record<string, any>>;
  if (!value || typeof value !== "object") return [];
  return Object.entries(value as Record<string, unknown>)
    .filter(([, node]) => Boolean(node) && typeof node === "object")
    .map(([id, node]) => ({ id, ...(node as Record<string, any>) }));
}

function readRawEdges(graph: Record<string, any> | undefined): Array<Partial<PropEdge> & Record<string, any>> {
  if (!graph) return [];
  const edges = Array.isArray(graph.edges) ? graph.edges : [];
  const flows = Array.isArray(graph.flows)
    ? graph.flows.map((flow: Record<string, any>, index: number) => ({
        id: flow.id,
        kind: "flow",
        fromNodeId: flow.from?.nodeId,
        fromPortId: flow.from?.portId,
        toNodeId: flow.to?.nodeId,
        toPortId: flow.to?.portId,
        order: flow.order ?? index,
      }))
    : [];
  const bindings = Array.isArray(graph.bindings)
    ? graph.bindings.map((binding: Record<string, any>) => ({
        id: binding.id,
        kind: binding.kind,
        fromNodeId: binding.source?.nodeId,
        fromPortId: binding.source?.portId,
        toNodeId: binding.target?.nodeId,
        toPortId: binding.target?.portId,
      }))
    : [];
  return edges.concat(flows, bindings);
}

function isPropGraphPortKind(kind: unknown): kind is PropGraphPortKind {
  return kind === "flow" || kind === "value" || kind === "target" || kind === "condition" || kind === "rarity" || kind === "tag";
}

function normalizeEditorNodeStates(
  nodes: PropNode[],
  rawNodes: Array<Partial<PropNode> & Record<string, any>>,
  editorNodes: PropGraphNodeEditorState[] | undefined,
) {
  return nodes.map((node, index) => {
    const fallback = createDefaultNodeEditorState(node, index);
    const rawNode = rawNodes.find((item) => item.id === node.id) as Record<string, any> | undefined;
    const current = editorNodes?.find((item) => item.nodeId === node.id);
    const position = current?.position ?? rawNode?.position ?? fallback.position;
    const title = current?.title ?? rawNode?.title ?? fallback.title;
    const disabled =
      current?.disabled ??
      (typeof rawNode?.enabled === "boolean" ? rawNode.enabled === false : undefined);

    return {
      nodeId: node.id,
      title: translateDefaultNodeTitle(title, node.type),
      position: {
        x: Number(position?.x ?? fallback.position.x),
        y: Number(position?.y ?? fallback.position.y),
      },
      disabled,
    };
  });
}

function sanitizeNode(rawNode: Partial<PropNode> & Record<string, any>): PropNode | null {
  const type = getNormalizedPropNodeType(rawNode.type);
  if (!type) return null;
  const fallback = createDefaultPropNode(type);
  const data = { ...fallback.data, ...getMigratedNodeData(rawNode.type, rawNode.data) } as Record<string, any>;

  if (type === "eventTurnStart") {
    data.interval = Math.max(0, Math.floor(Number(data.interval ?? 0)));
    if (isEmptyRequiredValue(data.loop)) {
      delete data.loop;
    } else {
      data.loop = Math.max(0, Math.floor(Number(data.loop ?? 0)));
    }
  }

  if (
    type === "eventAcquire" ||
    type === "eventUse" ||
    type === "eventEliminated" ||
    type === "targetSelf" ||
    type === "targetFilter" ||
    type === "filterRarity" ||
    type === "filterTag" ||
    type === "filterPrototype" ||
    type === "filterRandom" ||
    type === "intQuantity" ||
    type === "intKindCount" ||
    type === "intRandom" ||
    type === "floatRandom" ||
    type === "logicContains"
  ) {
    Object.keys(data).forEach((key) => delete data[key]);
  }

  if (type === "prototypeLibrary") {
    data.propType = normalizePrototypeLibraryPropType(data.propType ?? data.type);
    Object.keys(data).forEach((key) => {
      if (key !== "propType") delete data[key];
    });
  }

  if (isActionNodeType(type)) {
    data.negative = data.negative === true || data.negative === "true" || data.negative === 1;
    if (type === "actionPermanentBoost") data.mutexTag = String(data.mutexTag ?? "default").trim() || "default";
    const selfEffects = normalizeEffectIds(data.selfEffects, data.selfEffectId);
    const targetEffects = normalizeEffectIds(data.targetEffects, data.targetEffectId);
    const selfAnimations = normalizeAnimationIds(data.selfAnimations);
    const targetAnimations = normalizeAnimationIds(data.targetAnimations);
    if (selfEffects.length) data.selfEffects = selfEffects;
    else delete data.selfEffects;
    if (targetEffects.length) data.targetEffects = targetEffects;
    else delete data.targetEffects;
    if (selfAnimations.length) data.selfAnimations = selfAnimations;
    else delete data.selfAnimations;
    if (targetAnimations.length) data.targetAnimations = targetAnimations;
    else delete data.targetAnimations;
    delete data.selfEffectId;
    delete data.targetEffectId;
    Object.keys(data).forEach((key) => {
      if (
        key !== "negative" &&
        key !== "selfEffects" &&
        key !== "targetEffects" &&
        key !== "selfAnimations" &&
        key !== "targetAnimations" &&
        (type !== "actionPermanentBoost" || key !== "mutexTag")
      ) delete data[key];
    });
  }

  if (type === "targetBoard") {
    if (!isBoardMaterialScope(data.scope)) data.scope = "all";
    if (data.includeEmpty !== true) delete data.includeEmpty;
    if (data.scope === "all") {
      delete data.position;
    } else if (data.scope === "relative") {
      data.position = normalizeBoardMaterialPositions(data.position, isRelativePositionKind, "adjacent");
    } else if (data.scope === "absolute") {
      data.position = normalizeBoardMaterialPositions(data.position, isAbsolutePositionKind, "corner");
    }
  }

  if (type === "prototypeSelect") {
    if (isEmptyRequiredValue(data.prototypeId)) delete data.prototypeId;
  }

  if (type === "intConstant") {
    data.value = Math.floor(Number(data.value ?? 0));
  }

  if (type === "floatConstant") {
    data.value = Number(data.value ?? 0);
  }

  if (type === "rarityConstant" || type === "tagConstant") {
    data.value = Math.floor(Number(data.value ?? 1));
  }

  if (type === "intAttribute" || type === "floatAttribute") {
    if (!isTargetAttributeField(data.attribute)) data.attribute = "rarity";
  }

  if (type === "intOperation") {
    if (!isIntegerOperationOperator(data.op)) data.op = "add";
  }

  if (type === "floatOperation") {
    if (!isFloatOperationOperator(data.op)) data.op = "add";
  }

  if (type === "roundNumber") {
    if (!isRoundMode(data.mode)) data.mode = "floor";
  }

  if (type === "logicCompareInt" || type === "logicCompareFloat") {
    if (!isCompareOperator(data.op)) data.op = "gte";
  }

  if (type === "logicProbability") {
    data.probability = clampNumber(Number(data.probability ?? 0.5), 0, 1);
  }

  return {
    ...fallback,
    id: rawNode.id || fallback.id,
    data,
  } as PropNode;
}

function isPropNodeType(type: unknown): type is PropNodeType {
  return nodeTypeOptions.some((option) => option.value === type);
}

function getNormalizedPropNodeType(type: unknown): PropNodeType | undefined {
  if (type === "filterRelativePosition" || type === "filterAbsolutePosition") return "targetBoard";
  return isPropNodeType(type) ? type : undefined;
}

function getMigratedNodeData(rawType: unknown, rawData: unknown) {
  const data = (rawData && typeof rawData === "object" ? rawData : {}) as Record<string, any>;
  if (rawType === "filterRelativePosition") {
    return { scope: "relative", position: data.position };
  }
  if (rawType === "filterAbsolutePosition") {
    return { scope: "absolute", position: data.position };
  }
  return data;
}

function isEventNodeType(type: PropNodeType) {
  return type === "eventTurnStart" || type === "eventAcquire" || type === "eventUse" || type === "eventEliminated";
}

function isActionNodeType(type: PropNodeType) {
  return actionNodeTypes.includes(type);
}

function isTargetAttributeField(field: unknown): field is AttributeValueNodeData["attribute"] {
  return field === "rarity" || field === "tag" || field === "yield";
}

function isCompareOperator(op: unknown): op is CompareNodeData["op"] {
  return ["eq", "ne", "gt", "gte", "lt", "lte"].includes(String(op));
}

function isIntegerOperationOperator(op: unknown): op is IntegerOperationNodeData["op"] {
  return ["add", "subtract", "multiply", "divide", "mod", "min", "max"].includes(String(op));
}

function isFloatOperationOperator(op: unknown): op is FloatOperationNodeData["op"] {
  return ["add", "subtract", "multiply", "divide", "mod", "min", "max"].includes(String(op));
}

function isRoundMode(mode: unknown): mode is RoundNumberNodeData["mode"] {
  return ["round", "ceil", "floor"].includes(String(mode));
}

function isBoardMaterialScope(scope: unknown): scope is TargetBoardNodeData["scope"] {
  return ["all", "relative", "absolute"].includes(String(scope));
}

function isPrototypeLibraryPropType(type: unknown): type is PrototypeLibraryNodeData["propType"] {
  return [1, 2, 3].includes(Number(type));
}

function normalizePrototypeLibraryPropType(type: unknown): PrototypeLibraryNodeData["propType"] {
  const value = Number(type);
  return isPrototypeLibraryPropType(value) ? value : 1;
}

function isRelativePositionKind(position: unknown): position is BoardMaterialPosition {
  return ["adjacent", "sameRow", "sameColumn", "diagonal"].includes(String(position));
}

function isAbsolutePositionKind(position: unknown): position is BoardMaterialPosition {
  return [
    "corner",
    "edge",
    "firstRow",
    "lastRow",
    "firstColumn",
    "lastColumn",
    "middleColumn",
  ].includes(String(position));
}

function normalizeBoardMaterialPositions(
  value: unknown,
  isValid: (position: unknown) => position is BoardMaterialPosition,
  fallback: BoardMaterialPosition,
): BoardMaterialPosition[] {
  const values = Array.isArray(value) ? value : value === undefined || value === null ? [] : [value];
  const normalized = Array.from(new Set(values.filter(isValid)));
  return normalized.length ? normalized : [fallback];
}

function cloneGraph(graph: PropGraph): PropGraph {
  return JSON.parse(JSON.stringify(graph));
}

async function copyTextToClipboard(text: string) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) {
    // Fall through to the textarea fallback for non-secure contexts.
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);
  return copied;
}

function isEditTabDirty(tab: EditTab) {
  return getComparableGraphSnapshot(tab.graph) !== getComparableGraphSnapshot(tab.savedGraph);
}

function getComparableGraphSnapshot(graph: PropGraph) {
  return stableStringify(sanitizePropGraph(graph));
}

function stableStringify(value: unknown): string {
  return JSON.stringify(sortStableValue(value));
}

function sortStableValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortStableValue);
  if (!value || typeof value !== "object") return value;
  return Object.keys(value as Record<string, unknown>)
    .sort()
    .reduce<Record<string, unknown>>((result, key) => {
      result[key] = sortStableValue((value as Record<string, unknown>)[key]);
      return result;
    }, {});
}

function getSelectionIds(selection: Selection): SelectionIds {
  if (!selection) return { nodeIds: new Set(), edgeIds: new Set() };
  if (selection.type === "node") return { nodeIds: new Set([selection.id]), edgeIds: new Set() };
  if (selection.type === "edge") return { nodeIds: new Set(), edgeIds: new Set([selection.id]) };
  return {
    nodeIds: new Set(selection.nodeIds),
    edgeIds: new Set(selection.edgeIds),
  };
}

function toSelection(nodeIds: string[], edgeIds: string[]): Selection {
  const nextNodeIds = Array.from(new Set(nodeIds));
  const nextEdgeIds = Array.from(new Set(edgeIds));
  if (nextNodeIds.length === 1 && nextEdgeIds.length === 0) {
    return { type: "node", id: nextNodeIds[0] };
  }
  if (nextEdgeIds.length === 1 && nextNodeIds.length === 0) {
    return { type: "edge", id: nextEdgeIds[0] };
  }
  if (nextNodeIds.length || nextEdgeIds.length) {
    return { type: "multi", nodeIds: nextNodeIds, edgeIds: nextEdgeIds };
  }
  return null;
}

function normalizeWorldRect(start: WorldPoint, current: WorldPoint): LayoutRect {
  const x = Math.min(start.x, current.x);
  const y = Math.min(start.y, current.y);
  return {
    x,
    y,
    width: Math.abs(current.x - start.x),
    height: Math.abs(current.y - start.y),
  };
}

function selectCanvasElementsInRect(
  rect: LayoutRect,
  nodes: PropNode[],
  edges: PropEdge[],
  editorStateMap: Map<string, PropGraphNodeEditorState>,
): Selection {
  const nodeMap = new Map(nodes.map((node) => [node.id, node]));
  const nodeIds = nodes
    .filter((node) => {
      const editorState = editorStateMap.get(node.id);
      return editorState ? rectsIntersect(rect, createLayoutRect(node, editorState.position)) : false;
    })
    .map((node) => node.id);
  const edgeIds = edges
    .filter((edge) => {
      const from = nodeMap.get(edge.fromNodeId);
      const to = nodeMap.get(edge.toNodeId);
      const fromState = editorStateMap.get(edge.fromNodeId);
      const toState = editorStateMap.get(edge.toNodeId);
      if (!from || !to || !fromState || !toState) return false;
      const fromPoint = getPortAnchor(from, fromState, "output", edge.fromPortId);
      const toPoint = getPortAnchor(to, toState, "input", edge.toPortId);
      return segmentIntersectsRect(fromPoint, toPoint, rect);
    })
    .map((edge) => edge.id);
  return toSelection(nodeIds, edgeIds);
}

function createCanvasClipboard(
  selection: Selection,
  nodes: PropNode[],
  edges: PropEdge[],
  editorNodes: PropGraphNodeEditorState[],
  entryNodeIds: string[],
): CanvasClipboard | null {
  const selectedIds = getSelectionIds(selection);
  if (!selectedIds.nodeIds.size && !selectedIds.edgeIds.size) return null;

  const copiedNodeIds = new Set(selectedIds.nodeIds);
  edges.forEach((edge) => {
    if (!selectedIds.edgeIds.has(edge.id)) return;
    copiedNodeIds.add(edge.fromNodeId);
    copiedNodeIds.add(edge.toNodeId);
  });
  if (!copiedNodeIds.size) return null;

  const editorNodeMap = new Map(editorNodes.map((item) => [item.nodeId, item]));
  const copiedNodes = nodes
    .filter((node) => copiedNodeIds.has(node.id))
    .map((node) => clonePlain(node));
  const copiedEditorNodes = copiedNodes.map((node, index) =>
    clonePlain(editorNodeMap.get(node.id) ?? createDefaultNodeEditorState(node, index)),
  );
  const copiedEdges = edges
    .filter(
      (edge) =>
        copiedNodeIds.has(edge.fromNodeId) &&
        copiedNodeIds.has(edge.toNodeId) &&
        (selectedIds.edgeIds.has(edge.id) ||
          (selectedIds.nodeIds.has(edge.fromNodeId) && selectedIds.nodeIds.has(edge.toNodeId))),
    )
    .map((edge) => clonePlain(edge));

  return {
    nodes: copiedNodes,
    editorNodes: copiedEditorNodes,
    edges: copiedEdges,
    entryNodeIds: entryNodeIds.filter((id) => copiedNodeIds.has(id)),
  };
}

function pasteCanvasClipboardPayload(
  clipboard: CanvasClipboard,
  center: WorldPoint,
  pasteCount: number,
): CanvasClipboard {
  const bounds = getEditorNodeBounds(clipboard.editorNodes);
  const offset = 36 * pasteCount;
  const targetX = center.x - bounds.width / 2 + offset;
  const targetY = center.y - bounds.height / 2 + offset;
  const idMap = new Map(clipboard.nodes.map((node) => [node.id, createPropGraphId("node")]));
  const nodes = clipboard.nodes.map((node) => ({
    ...clonePlain(node),
    id: idMap.get(node.id) ?? createPropGraphId("node"),
  } as PropNode));
  const editorNodes = clipboard.editorNodes.map((item) => ({
    ...clonePlain(item),
    nodeId: idMap.get(item.nodeId) ?? createPropGraphId("node"),
    position: {
      x: Math.round(item.position.x - bounds.x + targetX),
      y: Math.round(item.position.y - bounds.y + targetY),
    },
  }));
  const edges = clipboard.edges
    .map((edge) => {
      const fromNodeId = idMap.get(edge.fromNodeId);
      const toNodeId = idMap.get(edge.toNodeId);
      if (!fromNodeId || !toNodeId) return null;
      return {
        ...clonePlain(edge),
        id: createPropGraphId("edge"),
        fromNodeId,
        toNodeId,
      };
    })
    .filter((edge): edge is PropEdge => Boolean(edge));
  return {
    nodes,
    editorNodes,
    edges,
    entryNodeIds: clipboard.entryNodeIds
      .map((id) => idMap.get(id))
      .filter((id): id is string => Boolean(id)),
  };
}

function getEditorNodeBounds(editorNodes: PropGraphNodeEditorState[]): LayoutRect {
  if (!editorNodes.length) return { x: 0, y: 0, width: NODE_WIDTH, height: NODE_HEIGHT };
  const minX = Math.min(...editorNodes.map((item) => item.position.x));
  const minY = Math.min(...editorNodes.map((item) => item.position.y));
  const maxX = Math.max(...editorNodes.map((item) => item.position.x + NODE_WIDTH));
  const maxY = Math.max(...editorNodes.map((item) => item.position.y + NODE_HEIGHT));
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

function rectsIntersect(a: LayoutRect, b: LayoutRect) {
  return (
    a.x <= b.x + b.width &&
    a.x + a.width >= b.x &&
    a.y <= b.y + b.height &&
    a.y + a.height >= b.y
  );
}

function pointInRect(point: WorldPoint, rect: LayoutRect) {
  return (
    point.x >= rect.x &&
    point.x <= rect.x + rect.width &&
    point.y >= rect.y &&
    point.y <= rect.y + rect.height
  );
}

function segmentIntersectsRect(a: WorldPoint, b: WorldPoint, rect: LayoutRect) {
  if (pointInRect(a, rect) || pointInRect(b, rect)) return true;
  const middle = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  if (pointInRect(middle, rect)) return true;
  const corners = [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.width, y: rect.y },
    { x: rect.x + rect.width, y: rect.y + rect.height },
    { x: rect.x, y: rect.y + rect.height },
  ];
  return corners.some((corner, index) =>
    lineSegmentsIntersect(a, b, corner, corners[(index + 1) % corners.length]),
  );
}

function lineSegmentsIntersect(a: WorldPoint, b: WorldPoint, c: WorldPoint, d: WorldPoint) {
  const ab = cross(a, b, c) * cross(a, b, d);
  const cd = cross(c, d, a) * cross(c, d, b);
  return ab <= 0 && cd <= 0;
}

function cross(a: WorldPoint, b: WorldPoint, c: WorldPoint) {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

function clonePlain<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

function getTabKey(id: string) {
  return `graph:${id}`;
}

function toNumberArray(value?: Array<number | string>) {
  return (value ?? [])
    .map((item) => Number(item))
    .filter((item) => Number.isFinite(item));
}

function getNodeColor(type: PropNodeType) {
  switch (type) {
    case "eventTurnStart":
    case "eventAcquire":
    case "eventUse":
    case "eventEliminated":
      return "#0f766e";
    case "targetSelf":
    case "targetBoard":
      return "#2563eb";
    case "prototypeLibrary":
    case "prototypeSelect":
      return "#4f46e5";
    case "targetFilter":
    case "filterRarity":
    case "filterTag":
    case "filterPrototype":
    case "filterRandom":
      return "#0d9488";
    case "intConstant":
    case "intAttribute":
    case "intQuantity":
    case "intKindCount":
    case "intRandom":
    case "intOperation":
    case "floatConstant":
    case "floatAttribute":
    case "floatRandom":
    case "floatOperation":
    case "roundNumber":
    case "rarityConstant":
    case "tagConstant":
      return "#0891b2";
    case "logicCompareInt":
    case "logicCompareFloat":
    case "logicContains":
    case "logicProbability":
      return "#7c3aed";
    case "actionGrow":
    case "actionExtraYield":
    case "actionPermanentBoost":
    case "actionPurify":
    case "actionProtect":
    case "actionAccelerate":
    case "actionTransform":
    case "actionSpawn":
    case "actionEliminate":
    case "actionCollect":
    case "actionHarvest":
    case "actionRarityWeight":
    case "actionExtendCommission":
    case "actionSwapPosition":
    case "actionTriggerVisual":
      return "#c2410c";
  }
}

function isNegativeEffectNode(node: PropNode) {
  return isActionNodeType(node.type) && (node.data as { negative?: boolean }).negative === true;
}

function getPortColor(kind: PropGraphPort["kind"]) {
  switch (kind) {
    case "flow":
      return "#334155";
    case "value":
      return "#0891b2";
    case "target":
      return "#c2410c";
    case "condition":
      return "#7c3aed";
    case "rarity":
      return "#a16207";
    case "tag":
      return "#db2777";
  }
}

function getPortConnectionStyle(
  state: PortConnectionState,
  baseColor: string,
): PortConnectionVisualStyle {
  switch (state) {
    case "source":
      return {
        borderColor: "#1677ff",
        background: "#e0f2fe",
        boxShadow: "0 0 0 5px rgba(22, 119, 255, 0.18)",
        labelColor: "#1677ff",
        opacity: 1,
        scale: 1.1,
      };
    case "connectable":
      return {
        borderColor: "#16a34a",
        background: "#dcfce7",
        boxShadow: "0 0 0 4px rgba(22, 163, 74, 0.16)",
        labelColor: "#166534",
        opacity: 1,
        scale: 1.08,
      };
    case "hoverConnectable":
      return {
        borderColor: "#15803d",
        background: "#bbf7d0",
        boxShadow: "0 0 0 7px rgba(22, 163, 74, 0.26)",
        labelColor: "#14532d",
        opacity: 1,
        scale: 1.22,
      };
    case "hoverInvalid":
      return {
        borderColor: "#ef4444",
        background: "#fee2e2",
        boxShadow: "0 0 0 7px rgba(239, 68, 68, 0.22)",
        labelColor: "#b91c1c",
        opacity: 1,
        scale: 1.18,
      };
    case "unavailable":
      return {
        borderColor: "#cbd5e1",
        background: "#f8fafc",
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.08)",
        opacity: 0.45,
      };
    case "idle":
    default:
      return {
        borderColor: baseColor,
        background: "#fff",
        boxShadow: "0 1px 3px rgba(15, 23, 42, 0.18)",
        opacity: 1,
      };
  }
}

function getNodeTypeLabel(type: PropNodeType) {
  return nodeTypeOptions.find((option) => option.value === type)?.label ?? type;
}
